import {Map} from 'immutable';
import humanSize from 'human-size';

import { UploadSize5mb } from '../constants/AppConstants';

import gatwick from './gatwick';
import gatwickBa from './gatwick-ba';
import gatwickRyanair from './gatwick-ryanair';
import southeastern from './southeastern';
import southern from './southern';
import thameslink from './thameslink';
import greatnorthern from './greatnorthern';
import onTrackRetail from './ontrackretail';
import ticketyboo from './ticketyboo';
import transpennine from './transpennine';

/**
 * Dictionary of strings for the app. Uses ICU message syntax
 * http://userguide.icu-project.org/formatparse/messages
 */

const defaultMessages = new Map({
    'global.none': 'None',
    'global.from': 'from',
    'global.start': 'Start',
    'global.end': 'End',
    'global.back': 'Back',
    'global.next': 'Next',
    'global.to': 'to',
    'global.go': 'Go',
    'global.leg': 'Leg',
    'global.select': 'Select',
    'global.selected': 'Selected',
    'global.and': 'and',
    'global.here': 'here',
    'global.moreInfo': 'More info',
    'global.lessInfo': 'Less info',
    'global.was': 'was',
    'global.loading': 'Loading...',
    'global.save': 'Save',
    'global.continue': 'Continue',
    'global.filters': 'Filters',
    'global.backToMainSite': 'Back to {brand} website',
    'global.banner.beta.logo.text': 'BETA',
    'global.banner.beta.text': 'We have updated the way you find and buy tickets.',
    'global.banner.beta.help': 'Need help?',
    'global.banner.existing': 'If you already have an account with us, please',
    'global.banner.dismiss': 'Dismiss',
    'global.banner.readmore': 'Read more',
    'global.banner.existing.reset': 'reset your password',
    'global.banner.existing.before': 'before {date}',
    'global.strike.message':
        "Tickets are not currently available online for these services due to industrial action - <a href='{moreInfo}' target='_blank'>more info</a>",
    'global.key': 'Key',
    'global.keyGo': 'keyGo',
    'global.theKey': 'The Key',
    'global.directions.outward': 'Outward',
    'global.directions.inward': 'Return',
    'global.smartcard': 'Smartcard',
    'global.smartcardUppercase': 'Smartcard',
    'global.smartcards': 'Smartcards',
    'global.smartcardTicket': 'Smartcard ticket',
    'global.smartcardTicketUppercase': 'Smartcard Ticket',
    'global.clickHere': 'click here',
    'global.clickFor': 'click for ',
    'global.clickToSelect': 'click to select',
    'global.clickForMoreInfo': 'click for more info',
    'global.clickToExpand': 'Click to expand',
    'global.clickToCollapse': 'Click to collapse',
    'global.searchFor': 'Search for',
    'global.passenger': 'Passenger',
    'global.adult': 'Adult',
    'global.child': 'Child',
    'global.externalLink': ' opens in a new window',
    'global.yes': 'Yes',
    'global.no': 'No',
    'global.showMore': 'Show more',
    'global.showLess': 'Show less',
    'global.gotIt': 'Got it',
    'global.timeDuration.ariaLabel': '{hours, plural, =0 {} =1 {# hour} other {# hours}} {minutes, plural, =0 {} =1 {# minute} other {# minutes}}',
    'global.timeDuration.fastest.ariaLabel': '{hours, plural, =0 {} =1 {# hour} other {# hours}} {minutes, plural, =0 {} =1 {# minute} other {# minutes}}. This is the fastest journey time displayed.',
    'global.step': 'Step',
    'global.stepCounter': '({currentStep}/{stepCount})',
    'global.stepCounter.aria': '<{global.step}> {currentStep} of {stepCount}.',

    'help.openReturn.title': 'What is an Open Return?',
    'help.openReturn.message':
        'Open Returns are flexible tickets as you do not need to specify the exact time and date you want to return on. \nThe tickets are valid for one calendar month from the outward date printed.',
    'help.seasonTickets.custom.title': 'What is a Custom season ticket?',
    'help.seasonTickets.custom.message':
        'You can select a specific end date or validity for your season ticket, for any period from one month to 10 months and 12 days. Tickets for longer than 10 months and 12 days are priced the same as the annual ticket.',
    'help.seasonTickets.flexi.title': 'What is a Flexi Season ticket?',
    'help.seasonTickets.flexi.message': 'Flexi Season tickets are valid for a 28-Day period and entitle you to 8 days of travelling between the stations specified, within that 28-Day period. \n If you make the same journey twice a week you will find that, on most routes, Flexi Season tickets offer better value for money compared to buying daily tickets. \n You can travel at peak and off peak times, and you don’t need to tell us in advance what days you plan to travel. \n You must activate a day’s worth of travel in your Flexi Season before you board a train service. \n Flexi Season tickets can be purchased online and at some ticket offices.',
    'help.seasonTickets.flexi.moreInfo': 'Learn more about {moreInfoLink}',
    'help.seasonTickets.flexi.moreInfo.link.text': 'Flexi Season tickets',
    'help.seasonTickets.flexi.moreInfo.link.aria': 'Learn more about Flexi Season tickets',
    'help.railcard1617.title': 'What is 16/17 Saver?',
    'help.railcardJobCentrePlus.title': 'What is a Jobcentre Plus railcard?',
    'help.railcard1617.message':
        '<p>If you are 16 to 17 years old you may be eligible for the 16-17 Saver railcard. If you have a valid 16-17 Saver railcard you can take advantage of 50% discounted travel.</p><p>Please ensure you carry your valid railcard with you to avoid being charged a penalty fare.</p><p>For more information, please <a href="{railcard1617WebsiteUrl}" target="_blank">click here</a>.</p>',
    'help.jobcentreplus.message':
        '<p>This is provided to those unemployed claiming Jobseekers Allowance or Universal Credit for 3-9 months (18-24 year olds) or 3-12 months (over 25s).</p><p>Other benefit recipients may receive a Jobcentre Plus Travel Discount Card from 3 months of their claim and if they are actively engaged with a Jobcentre Plus adviser. Cardholders are entitled to a 50% discount on selected rail tickets.</p>',
    'help.jobcentreplus.validityInfo':
        'Only valid for Flexi Season tickets',

    'help.tocSpecific.title': 'Search for the cheapest {toc} fares',
    'help.tocSpecific.message':
        "<p>You can search for the cheapest {toc} fares available for your selected journey by choosing '<{booking.operator.specifictoc}>'.</p><p>Alternatively, where multiple operators serve your selected journey, you can view all available fares by choosing '<{booking.operator.all}>'.</p>",

    'notifications.accountActions.text': 'Account Actions',
    'notifications.accountActions.title': 'You have multiple account actions available',
    'notifications.keyGoIncompleteJourney.text':
        'account.smartcard.keygo.incompleteJourneys.heading',
    'notifications.brandSplit.header': 'Select the network you travel on',
    'notifications.brandSplit.description': 'Select the network you travel on',
    'notifications.keyGoCardExpire.title': 'account.smartcard.keygo.cardExpire.heading',
    'notifications.keyGoCardExpire.description':
        'Update your payment details now to continue using <{global.keyGo}>',
    'notifications.railcard.applicationSubmitted':
        'Your railcard application has been submitted.\nWe aim to review this within 5 working days.',
    'notifications.railcard.deleted':
        'Please note – this railcard entitlement will now be removed from your account',
    'notifications.pa-request.canceled': 'Passenger Assist request canceled',
    'notifications.pa-request.updated': 'Passenger Assist request updated',
    'notifications.accessibility-profile.created': 'Accessibility profile created',
    'notifications.accessibility-profile.updated': 'Accessibility profile updated',
    'notifications.accessibility-profile.deleted': 'Accessibility profile deleted',

    'pageTitles.default': 'app.owner',
    'pageTitles.point-search': 'Find train tickets',
    'pageTitles.season-search': 'Find season tickets',
    'pageTitles.other-search': 'Find other tickets',
    'pageTitles.daysave-search': 'Find DaySave tickets',
    'pageTitles.roversrangers-search': 'Find Rover tickets',
    'pageTitles.season-results': 'season.results.heading',
    'pageTitles.season-details': 'season.photocard.header',
    'pageTitles.daysave-results': 'daySave.results.heading',
    'pageTitles.roversrangers-results': 'roversRangers.results.heading',
    'pageTitles.delivery': 'booking.delivery.header',
    'pageTitles.payment': 'booking.payment.header',
    'pageTitles.payment-confirmation': 'Payment successful',
    'pageTitles.account': 'nav.myaccount',
    'pageTitles.account-menu': 'pageTitles.account',
    'pageTitles.account-payment': 'account.nav.paymentdetails',
    'pageTitles.account-addresses': 'account.nav.addresses',
    'pageTitles.account-ordersrefunds': 'account.nav.orders-refunds',
    'pageTitles.account-orderdetails': 'Order details',
    'pageTitles.account-refunddetails': 'Refund options',
    'pageTitles.account-photocards-railcards': 'account.nav.photocards-railcards',
    'pageTitles.account-smart-carnet': 'account.nav.smart-carnet',
    'pageTitles.account-disruption-alerts': 'account.nav.disruptionAlerts',
    'pageTitles.account-reservation': 'account.nav.reservation',
    'pageTitles.smartcard-apply': 'account.nav.smartcard',
    'pageTitles.smartcard-tickets': '<{global.theKey}> <{global.smartcard}> - tickets',
    'pageTitles.smartcard-history': '<{global.theKey}> <{global.smartcard}> - journey history',
    'pageTitles.smartcard-keygo':
        '<{global.theKey}> <{global.smartcard}> - <{global.keyGo}> account',
    'pageTitles.smartcard-replacement': '<{global.theKey}> <{global.smartcard}> - replacement',
    'pageTitles.smartcard-transfer': 'account.heading.smartcard.transfer',
    'pageTitles.smartcard-transfer-results': 'account.heading.smartcard.transfer',
    'pageTitles.smartcard-transfer-confirmation': 'account.heading.smartcard.transfer',
    'pageTitles.evouchers': 'account.nav.evouchers',
    'pageTitles.disruption-alerts': 'account.nav.disruptionAlerts',
    'pageTitles.newsletter-unsubscribe': 'Unsubscribe',
    'pageTitles.auto-dr-claims': 'One Click Delay Repay',
    'pageTitles.se.main.page': 'Buy tickets with no fees!',

    'notifications.basket.cleared': 'Basket cleared successfully',
    'notifications.basket.itemAdded': 'Journey added to basket',
    'notifications.basket.itemDeleted': 'Journey removed from basket',

    'app.owner': 'Thameslink Railway',
    'app.owner.short': 'Thameslink',
    'app.owner.appName': 'Thameslink',

    // Used for <time></time> datetime attribute
    'time.duration': 'PT{hours}H {minutes}M',

    'time.minutes.short': '{minutes}m',
    'time.hours.short': '{hours}hr',

    'time.minutes.medium': '{minutes, plural, =1{min} other {#mins}}',
    'time.hours.medium': '{hours, plural, =1{hr} other {#hrs}}',

    'time.minutes.long': '{minutes, plural, =1 {1 minute} other {# minutes}}',
    'time.hours.long': '{hours, plural, =1 {1 hour} other {# hours}}',
    'time.days.long': '{days, plural, =1 {1 day} other {# days}}',
    'time.months': '{months} {months, plural, =1{month} other{months}}',

    'time.minutes.late': '{minutes} mins late',

    'booking.search.show': 'Show',
    'booking.search.point': 'Find trains',
    'booking.search.season': 'Show tickets',
    'booking.search.other': 'Show tickets',
    'booking.search.earlier': 'Show earlier',
    'booking.search.later': 'Show later',
    'booking.search.earlier.short': 'Earlier',
    'booking.search.later.short': 'Later',
    'booking.search.earlierLater.label': 'Show {period, select, earlier {earlier} other {later}} trains',
    'booking.search.hiddenServices': '{ticketName} is not available on {count} services.',
    'booking.search.showHiddenServices': 'Show',
    'booking.search.hideInvalidServices': 'Hide',
    'booking.search.departNowLabel': 'Depart now',
    'booking.search.journey-notification.modal.title': 'Service Information for your journey',

    'brand.split.header': 'Your network',
    'brand.split.text': 'Select the network you travel on below:',
    'brand.split.success': 'Selection successful',
    'brand.split.message': 'Your account has now been updated to be associated with',

    'skipToContent': 'Skip to content',

    'nav.heading': 'Book your tickets',
    'nav.pointTickets': 'Tickets',
    'nav.seasonTickets': 'Season tickets',
    'nav.otherTickets': 'Other tickets',
    'nav.welcome': 'Welcome back, {name}',
    'nav.myaccount': 'My account',

    'info.title': 'Information',

    'error.title': 'Error',
    'error.general.title': 'We are very sorry.',
    'error.general.message':
        'An unknown error occurred when trying to complete your request. Please try again and if you still see this message - please contact us.',

    'error.smartcard.heading': '<{global.smartcardUppercase}> Error',
    'error.smartcard.title': '<{global.smartcardUppercase}> not available',
    'error.smartcard.message':
        'An unknown error occurred when loading details for your <{global.key}> <{global.smartcard}>. Some <{global.smartcard}> functions will not be available. This may be a temporary problem. Please contact us if the problems persist.',

    'error.submit-railcard-application.message':
        'An error occurred when trying to submit your railcard application. Please try again and if you still see this message - please contact us.',
    'error.railcardNotFound.title': 'Railcard not found',
    'error.railcardAlreadyExist.heading': 'Railcard registration error',
    'error.railcardAlreadyExist.title': 'Railcard already exists',
    'error.railcardAlreadyExist.message':
        'The railcard you are trying to register already exists. Please check your details and try again.',

    'error.remove-railcard.message':
        'An error occurred when trying to remove your railcard. Please try again and if you still see this message - please contact us.',

    'booking.ticket': 'Ticket',
    'booking.tickets': 'Tickets',
    'booking.toggle.paperRadio': 'Paper ticket fares',
    'booking.toggle.keyRadio': '<{global.key}> <{global.smartcard}> fares',
    'booking.smartcard.info': 'Buying a ticket for the <{global.key}> <{global.smartcard}>? ',
    'booking.smartcard.info.header':
        'An easier way to use your <{global.key}> <{global.smartcard}>!',
    'booking.smartcard.info.body':
        'When buying Season tickets, you no longer need to select <{global.key}> <{global.smartcard}> fares in advance. Just enter your route as normal, select the ticket you need, and your <{global.key}> <{global.smartcard}> will be offered as a delivery option if available.',
    'booking.smartcard.passenger.adult': 'One adult',
    'booking.smartcard.passenger.child': 'One child',
    'booking.smartcard.railcard': 'Railcard',
    'booking.smartcard.info.awaitingProductMinOffset':
        'During preload period, you can only search for tickets for one adult and starting after {date}, to ensure that you receive them in time for your journey',
    'booking.smartcard.warning.cardFull':
        'You have reached the maximum {count, plural, =3 {of three} =5 {of five} other {number of}} tickets. ',
    'booking.smartcard.warning.cannotLoadProduct': `You're not able to purchase any further <{global.key}> <{global.smartcard}> tickets until your previous purchase has been loaded onto your card. `,
    'booking.smartcard.moreInformation': 'More information',
    'booking.smartcard.warning.downForMaintenance':
        'Due to essential maintenance, it is currently not possible to purchase a <{global.key}> <{global.smartcardTicket}>. Please try again later.',

    'booking.addons.travelcard': 'Travelcard',
    'booking.addons.plusbus': 'PlusBus',
    'booking.addons.plusbus.outwardJourney': 'Outbound journey',
    'booking.addons.plusbus.returnJourney': 'Return journey',
    'booking.addons.plusbus.info':
    "{plusBusLink} gives you unlimited bus and tram* travel on participating operators' services around the whole urban area of your selected stations. The prices shown cover PlusBus travel for ALL passengers.",
    'booking.addons.plusbus.select.sr': 'Press to add or remove Plusbus on your journey',
    'booking.addons.plusbus.footnotes':
        '* where tram services are available and operators participate in the PlusBus scheme',

    'booking.info.payg': 'Pay As You Go',
    'booking.info.payg.intro': 'Pay as you go is a convenient way to pay for your travel in and around London on most National Rail, bus, Tube, tram and DLR services. Pay as you go is available with an Oyster card or when using contactless.',
    'booking.info.payg.oyster-card-fare': '<p><b>The Oyster card fare for the journey currently selected would be £{price}</b></p>',
    'booking.info.payg.oyster.title': 'Oyster',
    'booking.info.payg.oyster.summary': 'You can use Oyster Pay As You Go on all suburban trains stopping in Zones 1 – 9',
    'booking.info.payg.oyster.bullets': `Daily capping of fares
                                            Set up Auto top-up so you never run out of credit
                                            Add Railcard or Annual Gold Card discounts
                                            Get Child or Student discounts with an Oyster photocard
                                            Add Season tickets and Travelcards for more than one day of travel`,
    'booking.info.payg.oyster.link': 'Contactless and Oyster - find out more',
    'booking.info.payg.contactless.title': 'Contactless',
    'booking.info.payg.contactless.summary':
        'Use contactless debit/credit cards or mobile payments for pay as you go travel',
    'booking.info.payg.contactless.bullets': `Same Adult fare as Oyster
                                                No need to top-up
                                                Daily capping of fares
                                                Monday to Sunday capping of fares
                                                Please note: No Child, Railcard, Annual Gold Card, or concessionary discounts apply`,
    'booking.info.payg.contactless.link': 'Contactless - find out more',

    'booking.loading.results': 'Loading trains and fares...',
    'booking.loading.callingPoints': 'Loading calling points...',
    'booking.loading.addons': 'Checking available extras...',

    'booking.noResults.header': 'Sorry, we couldn’t find a journey that matched:',
    'booking.noResults.searchDescriptionVia':
        '{departureStation} to {arrivalStation} via {viaStation}',
    'booking.noResults.searchDescriptionAvoid':
        '{departureStation} to {arrivalStation} avoiding {avoidStation}',
    'booking.noResults.searchDescription': '{departureStation} to {arrivalStation}',
    'booking.noResults.text': 'Please try another search, or contact us for more information.',
    'booking.noResults.tryAnother': 'Try another search',
    'booking.noResults.singles': 'singles',
    'booking.noResults.dayReturns': 'day returns',
    'booking.noResults.seeSinglesOrReturns':
        'There are no open returns available on this route / date. Would you like to see {singles} or {returns} instead?',
    'booking.noResults.isInFlexiLimbo': 'Searching for the new Flexi Season? Make sure you search from the 28th June onwards to get the full details and fares.',

    'booking.incompleteResults.noReturns':
        'Sorry, there are no return journeys available for the following search:',
    'booking.incompleteResults.pleaseTryDifferentDateTime':
        'Please try another search using a different return date / time, or contact us for more information.',

    'booking.select.outward.time': 'Select an outward time',
    'booking.select.inward.time': 'Select a return time',
    'booking.select.outward.fare': 'Select an outward fare',
    'booking.select.inward.fare': 'Select a return fare',

    'booking.ticketType.single': 'Single',
    'booking.ticketType.return': 'Return',
    'booking.ticketType.openReturn': 'Open return',
    'booking.ticketType.remove-return': 'No return',

    'booking.promotion.pinCode': 'Pin Code',
    'booking.promotion.invalidPin': 'Sorry that pin code was incorrect',
    'booking.promotion.helpText': 'Please enter your pin code in order to plan your journey',
    'booking.promoCode.applied': 'Code {promoCode} applied',
    'booking.promoCode.terms.header': 'Promotional discount',
    'booking.promoCode.delivery.notAvailable': 'Unfortunately, your discount code is not valid with this ticket collection method',
    'booking.promoCode.delivery.option.notAvailable': 'If you select this delivery method, your chosen discount code cannot be applied, and you will be charged the full price for your tickets.',
    'booking.promoCode.input.aria': 'No discount code applied, tap to input discount code',
    'booking.promoCode.theSame.discount.error': 'This promo code has already been used for one of the journeys in your current order',
    'booking.promoCode.one.discount.error': 'Only one discount can be used per order',
    'booking.promoCode.generic.error': 'There was an error applying your discount code, please try again later',
    'booking.promoCode.char.limit.error': 'Discount code should be a maximum of 20 characters',

    'promotion.tocFamily.subheading': 'Log in',
    'promotion.tocFamily.text': `Enter your work or assigned email address to receive a magic login link and access the site.`,
    'promotion.tocFamily.email.label': 'Email Address',
    'promotion.tocFamily.email.placeholder': 'Enter your email address',
    'promotion.tocFamily.button.sr': 'Request login link',
    'promotion.tocFamily.button.text': 'Submit',
    'promotion.tocFamily.button.progress': 'Sending...',
    'promotion.tocFamily.success.text': 'Please check your email for your login link.',
    'promotion.tocFamily.success.subtext': 'You will be able to request a new magic link again in 2 minutes.',
    'promotion.tocFamily.success.countdown': 'Time remaining: {countdown}',
    'promotion.tocFamily.error': 'Failed to send login link. Please try again',
    'promotion.tocFamily.error.invalidEmail': 'Invalid email, please contact {toc}',
    'promotion.tocFamily.verify.title': 'Closed User Group Terms & Conditions',
    'promotion.tocFamily.verify.availableTickets': 'You have {count} tickets available to purchase',
    'promotion.tocFamily.verify.terms': 'As a member of our Closed User Group, you have access to exclusive ticket offers. By accepting these terms, you acknowledge that these tickets are for personal use only and cannot be resold. Please review our terms and conditions carefully before proceeding.',
    'promotion.tocFamily.verify.termsAndConditions': '<a href={url} target="_blank">Terms & Conditions</a>',
    'promotion.tocFamily.verify.accept': 'Accept',
    'promotion.tocFamily.results.heading': 'Select <b>fare</b>',

    'promotion.tocFamily.verify.error.title': 'Error',
    'promotion.tocFamily.verify.error.heading': 'There was an error verifying your token',
    'promotion.tocFamily.verify.error.message': 'Please try again or contact {toc} for help.',

    'promotion.tocFamily.allowanceExceeded.title': "You've reached your limit of discounted tickets this year",
    'promotion.tocFamily.allowanceExceeded.message': "As part of this promotion, you're only entitled to purchase {count} tickets per year. Your quota of tickets will reset on the 1st of Jan {year}.",
    'promotion.tocFamily.allowanceExceeded.continue': 'Buy regular tickets',

    'promotion.tocFamily.rover.header': 'Buy Staff Rover',
    'promotion.tocFamily.rover.buttonText': 'Get Rover tickets',
    'promotion.tocFamily.rover.title': 'Rover tickets',
    'promotion.tocFamily.rover.description': 'Choose your start date and number of passengers to purchase Rover tickets',
    'promotion.tocFamily.rover.dateField': 'Start date',
    'promotion.tocFamily.rover.passengers': '{value, plural, =1{passenger} other{passengers}}',
    'promotion.tocFamily.rover.searchOtherJourneys': 'Search for other journeys',
    'promotion.tocFamily.rover.error': 'You only have {tickets} {tickets, plural, =1{available ticket} other{available tickets}} left for purchase',

    'season.passengers.header': 'Passengers',
    'season.passenger.adult': 'Adult',
    'season.passenger.child': 'Child (5-15)',
    'season.summary.text': 'Starting',
    'season.custom.summary.text': 'Until',
    'season.date.modal.title': 'Season ticket start date?',
    'season.custom.modal.title': 'Season ticket end date?',
    'season.select.startDate': 'Please choose a start date',
    'season.select.endDate': 'Please choose a end date',
    'season.custom.orfor': 'or for',
    'season.validUntil': 'Valid until',
    'season.ticketNumber': 'Ticket number',
    'season.duration.months': '{months} {months, plural, =1{month} other{months}}',
    'season.duration.labels.months': 'Months',
    'season.duration.days': '{days, number} {days, plural, =1{day} other{days}}',
    'season.duration.labels.days': 'Days',
    'season.options.duration': 'Duration',
    'season.options.class': 'Class',
    'season.options.travelcards': 'Travelcards',
    'season.options.routes': 'Routes',
    'season.options.labels.flexi': 'Flexi Season',
    'season.options.labels.flexi.supplementary': 'New',
    'season.options.labels.weekly': 'Weekly',
    'season.options.labels.fourWeekly': 'Four weekly',
    'season.options.labels.monthly': 'Monthly',
    'season.options.labels.quarterly': 'Quarterly',
    'season.options.labels.sixMonthly': 'Six Monthly',
    'season.options.labels.annual': 'Annual',
    'season.options.labels.custom': 'Custom',
    'season.options.labels.railcard1617': '16/17 Saver',
    'season.options.labels.railcardJobCentrePlus': 'Jobcentre Plus',
    'season.options.labels.travelcards': 'Show fares with Travelcards',
    'season.results.heading': 'Season tickets',
    'season.results.passengers': `{adults, plural,
            =0    {}
            other {Adults: #}
        }{children, plural,
            =0    {}
            other {Children: #}
        }
    `,
    'season.results.editJourney': 'Edit your journey',
    'season.filters.heading': 'Filters',
    'season.filters.showingText': 'Showing results with <strong>{ filterNum } filters</strong>',
    'season.filters.editText': 'Edit',
    'season.filters.hide': 'Hide',
    'season.photocard.notes':
        'We cannot sell a Season Ticket without a valid photocard. Please enter your photocard details.',
    'season.photocard.header': 'Photocard details',
    'season.photocard.number': 'Photocard number',
    'season.photocard.ticket': 'Photocard for:',
    'season.photocard.multiple': 'Use a single photocard for all tickets',
    'season.ticket.firstClass': '1st Class',
    'season.label.customDuration':
        '{months, plural, =0{} =1{1 month} other{# months}} {days, plural, =0{} =1{1 day} other{# days}}',
    'season.warning.requiresFutureStartDate':
        'Requires a start date of {startDate} or later for postal delivery',

    'otherTickets.daySave.title': 'DaySave',
    'otherTickets.roversRangers.title': 'Rover tickets',

    'daySave.search.title': 'otherTickets.daySave.title',
    'daySave.results.heading': 'DaySave tickets',
    'daySave.results.no-results.heading': 'No results',
    'daySave.results.no-results.text':
        'Sorry, there were no DaySave tickets found that match your search. Please try a different date or origin station.',
    'daySave.results.backToSearch': 'Try a different search',
    'daySave.passengers.mustHaveAdult': 'There must be at least 1 adult travelling',

    'booking.error.noSmartcard':
        'You need an active <{global.key}> <{global.smartcard}> with no pending tickets to buy <{global.smartcard}> tickets',

    'roversRangers.search.title': 'otherTickets.roversRangers.title',
    'roversRangers.search.dateInputPrefix': 'Start date',
    'roversRangers.search.dateModalTitle': 'Rover ticket start date',
    'roversRangers.search.familyRangerPassengerInfo':
        'This ticket is valid for a maximum of 2 Adults and 4 Children',
    'roversRangers.search.dayRangerInfo':
        'Are you travelling with up to 2 adults and 4 children, the WM Family Day Ranger may work out cheaper. You can select it from the dropdown list on the search page',

    'roversRangers.results.heading': 'Rover tickets',
    'roversRangers.results.no-results.heading': 'daySave.results.no-results.heading',
    'roversRangers.results.no-results.text':
        'Sorry, there were no Rover tickets found that match your search. Please try a different date or ticket type.',
    'roversRangers.results.backToSearch': 'daySave.results.backToSearch',

    'roversRangers.passengers.header': 'Passengers',
    'roversRangers.passengers.leadPassenger': 'Lead passenger name',
    'roversRangers.passengers.placeholder':
        'Name of {adults, plural, =0{} one{adult}}{children, plural, =0{} one{child}}',
    'roversRangers.passengers.withRailcard': 'with {railcard}',
    'roversRangers.passengers.withoutRailcard': 'without Railcard',

    'roversRangers.ticketInfo.all':
        'Other Rovers are available - <a target="_blank" aria-label="click here for details about Other Rovers" href="https://www.nationalrail.co.uk/tickets-railcards-and-offers/ticket-types/ranger-tickets-and-rover-tickets/">click here for details</a>',
    'roversRangers.ticketInfo.14R':
        'First class All-Line Rovers are also available - <a target="_blank" aria-label="click here for details about First class All-Line Rovers" href="https://www.nationalrail.co.uk/times_fares/pr569241c35ce74100fb0de1b9798124.aspx">click here for details</a>',
    'roversRangers.ticketInfo.R07':
        'First class All-Line Rovers are also available - <a target="_blank" aria-label="click here for details about First class All-Line Rovers" href="https://www.nationalrail.co.uk/times_fares/pr20780b0a0400020167ed620a7e504e.aspx">click here for details</a>',

    'roversRangers.ticketTypeName.14R': 'All Line Rover (14 Days)',
    'roversRangers.ticketTypeName.R07': 'All Line Rover (7 Days)',
    'roversRangers.ticketTypeName.MTR': 'Kent Rover',
    'roversRangers.ticketTypeName.H37': 'Heart of England 3 in 7 Days',
    'roversRangers.ticketTypeName.HE7': 'Heart of England 7 Day Rover',
    'roversRangers.ticketTypeName.WM1': 'West Midlands Day Ranger',
    'roversRangers.ticketTypeName.WM3': 'West Midlands Family Day Ranger',
    'booking.journey.origin': 'Leaving from...',
    'booking.journey.origin.label': 'Leaving from (origin)',
    'booking.journey.destination': 'Going to...',
    'booking.journey.destination.label': 'Going to (destination)',
    'booking.journey.mandatoryField': 'This field is required',
    'booking.journey.switch': 'Switch origin and destination',
    'booking.journey.clear': 'Clear',
    'booking.journey.viaAvoid': 'Travel via or avoid',
    'booking.journey.via': 'Travel via',
    'booking.journey.avoid': 'Avoid',
    'booking.journey.viaStation': 'Travelling via {station}',
    'booking.journey.avoidStation': 'Avoid {station}',
    'booking.journey.viaLabel': 'Select a station to travel via.',
    'booking.journey.avoidLabel': 'Select a station to avoid.',
    'booking.journey.viaPlaceholder': 'Travelling via...',
    'booking.journey.avoidPlaceholder': 'Avoiding...',
    'booking.journey.chooseAJourney': 'Choose a journey',
    'booking.journey.chooseTicket': 'Choose ticket type',
    'booking.journey.stationName': '{name}({crs})',
    'booking.journey.description': '{origin} to {destination}',
    'booking.journey.delay': '{delay} late',
    'booking.journey.srDateDescription':
        '{scheduledDepartureDate}, {scheduledDeparture} to {scheduledArrival}',
    'booking.journey.srShortDescription':
        '{scheduledDeparture} from {origin} arriving at {destination} at {scheduledArrival}',
    'booking.journey.srLongDescription':
        '<{booking.journey.srShortDescription}>. This service takes {duration}, {changes} and is currently {delay}.',
    'booking.journey.srAdjustedTimes':
        'It will now depart at {adjustedDeparture} and arrive at {adjustedArrival}. ',
    'booking.journey.srChanges':
        '{changes, plural, =0 {is Direct} =1 {has 1 change} other {has # changes}}. ',
    'booking.journey.additional-options': 'Show all routes',

    'booking.journey.errors.sameStation': 'Origin and destination stations must be different',
    'booking.journey.errors.missingOrigin': 'Please enter the station you are leaving from',
    'booking.journey.errors.missingDestination': 'Please enter the station you are going to',
    'booking.journey.errors.viaSameStation': 'You cannot travel via your origin or destination',
    'booking.journey.errors.avoidSameStation':
        'You cannot avoid travelling via your origin or destination',

    'booking.dates.errors.departAfterArrive':
        'Your return journey is earlier than your outward journey',
    'booking.dates.errors.invalidDate': 'A date entered is invalid',
    'booking.dates.errors.pastDate': 'A date entered is in the past',
    'booking.dates.errors.smartcardPeriodReturn':
        'Only day returns are currently available on <{global.theKey}> <{global.smartcard}>. Please choose a return time on the same date as your outbound journey',
    'booking.dates.errors.datesAfterMax':
        'You can only book travel up to {maxOutboundDays} days into the future',
    'booking.dates.errors.datesAfterMaxPeriod':
        'You can only book travel up to {maxPeriod} into the future',
    'booking.dates.errors.datesBeforeSmartcardAwaitingPeriod':
        'You can only book travel that starts {days, plural, =1{# working day} other{# working days}} since today',
    'booking.dates.errors.railcard1617Expiry':
        'Your 16-17 Saver railcard allowance expires on {date}',
    'booking.railcard1617.enterDetails': 'Please enter your 16/17 Saver railcard details',
    'booking.railcard1617.form.firstName': 'First name',
    'booking.railcard1617.form.surname': 'Surname',
    'booking.railcard1617.form.railcardNumber': 'Railcard number',
    'booking.railcard1617.keyGoLinkingLabel': `Click here to save this railcard to keyGo account <b>"{email}"</b>.`,
    'booking.railcard1617.keyGoLinkingWarning': `<div class="railcard1617-modal__keygo-warning"><i class="otrl otrl-warning b-alert__icon b-alert__icon--multiline railcard1617__keygo-warning__icon"></i>You can remove this railcard from your keyGo account by visiting your Photocards/Railcards menu.  If you then wish to add this railcard to another account after removing it, you will need to contact Customer Services.</div>`,
    'booking.railcard1617.railcardDetails': 'Railcard details',
    'booking.railcard1617.errors.alreadyExists':
        'The railcard you are trying to register is already registered. Please check your details and try again.',
    'booking.railcard1617.errors.invalid':
        'The entered 16-17 Saver railcard number does not match the entered name',
    'booking.railcard1617.errors.unknown':
        'There was an error while trying to validate your railcard. Please try again later.',
    'booking.railcard1617.validationSuccess':
        'Your 16-17 Saver railcard has been applied to the search',

    'booking.season.errors.durationMissing': 'Please select your season ticket duration',

    'booking.journey.leg.callingPoints': 'Calling points',
    'booking.journey.leg.platform': 'Platform {platform}',
    'booking.journey.leg.replacementBus': 'Rail replacement bus',
    'booking.journey.leg.journeyTime': 'Journey time: {journeyTime}',
    'booking.journey.leg.facilities': 'Train facilities',
    'booking.journey.leg.facilities.firstClass': 'First Class',
    'booking.journey.leg.facilities.firstClassMeal': 'First Class meal',
    'booking.journey.leg.facilities.firstClassRestaurant': 'First Class restaurant',
    'booking.journey.leg.facilities.hotFood': 'Hot food',
    'booking.journey.leg.facilities.buffet': 'Buffet',
    'booking.journey.leg.facilities.restaurant': 'Restaurant',
    'booking.journey.leg.facilities.trolley': 'Trolley',
    'booking.journey.leg.facilities.wheelchair': 'Wheelchair access',
    'booking.journey.leg.details.operator': 'Service operator',
    'booking.journey.leg.details.service': 'Service',
    'booking.journey.leg.details.journeyTime': 'Journey time',
    'booking.journey.leg.toggle': '{show, select, true {Hide} other {Show}} calling points',

    'booking.dates.addReturn': 'Add return',
    'booking.dates.return': 'Return',
    'booking.dates.outbound': 'Outbound',
    'booking.dates.departing': 'Leaving at',
    'booking.dates.arriving': 'Arriving',
    'booking.dates.departing.short': 'Dep ',
    'booking.dates.arriving.short': 'Arr',
    'booking.dates.now': 'Now',
    'booking.dates.todayAt': 'Today',
    'booking.dates.tomorrowAt': 'Tomorrow',
    'booking.dates.sameDay': 'Same day',
    'booking.dates.nextDay': 'Next day',
    'booking.dates.openReturn-title': 'Open return',
    'booking.dates.openReturn':
        'You have chosen an Open Return ticket, these tickets are valid for one calendar month from the outward date',
    'booking.dates.openReturn-title-v2': 'Return',
    'booking.dates.openReturn-v2': 'Within a month',
    'booking.dates.modal.title.outward.Depart': 'When would you like to leave?',
    'booking.dates.modal.title.outward.Arrive': 'When would you like to arrive?',
    'booking.dates.modal.title.inward.Depart': 'When would you like to come back?',
    'booking.dates.modal.title.inward.Arrive': 'When would you like to arrive back?',
    'booking.dates.addOpenReturn': 'Get an <span class="like-link"><strong>open return ticket</strong></span> and return within a month for an extra',
    'booking.dates.addOpenReturn.equal': 'Get an <span class="like-link"><strong>open return ticket</strong></span> and return within a month <strong>at not additional cost</strong>',
    'booking.dates.addOpenReturn.cheaper': 'Get an <span class="like-link"><strong>open return ticket</strong></span> and return within a month saving',

    'booking.time.labels.time': 'Time',
    'booking.time.labels.hours': 'Hours',
    'booking.time.labels.minutes': 'Minutes',

    'booking.operator.all': 'All operators',
    'booking.operator.specifictoc': 'Only {toc} services',

    'booking.passengers.adults': `{adults, plural,
            =0    {}
            one   {# x Adult}
            other {# x Adults}
        }
    `,
    'booking.passengers.children': `{children, plural,
            =0    {}
            one   {# x Child}
            other {# x Children}
        }
    `,
    'booking.passengers.errors.atLeastOne': 'You must select at least 1 passenger',
    'booking.passengers.errors.upToNine': 'You can select up to a maximum of 9 passengers',
    'booking.passengers.summary.text': `{adults, plural,
            =0    {}
            one   {1 Adult {children, plural, =0{} other{ & }}}
            other {# Adults {children, plural, =0{} other{ & }}}
        }{children, plural,
            =0    {}
            one   {1 Child}
            other {# Children}
        }
    `,
    'booking.passengers.names.text': `{adults, plural,
            =0    {}
            one   {adult}
        }{children, plural,
            =0    {}
            one   {child}
        }
    `,
    'booking.passengers.modal.title.withRailcards': 'Passengers and Railcards',
    'booking.passengers.modal.title': 'Passengers',
    'booking.passenger.child-fare.info':
        'Children under 5 travel free. Under 16s are eligible for child fares. Passengers aged 16 or over must buy an adult ticket.',

    'booking.railcards': 'Railcards',
    'booking.railcardsCount': '{ quantity } x { name }',
    'booking.railcards.season.railcard1617.heading': 'Validate your 16-17 Saver railcard',
    'booking.railcards.season.railcard1617.warning':
        'If you have a 16-17 Saver Railcard click above to validate it and receive discounts on Season tickets',
    'booking.railcards.select': 'Select a Railcard',
    'booking.railcards.add': 'Add Railcard',
    'booking.railcards.remove': 'Remove Railcard',
    'booking.railcards.errors.moreThanPassengers': 'alertmessage.form.moreRailcardsThanPassengers',
    'booking.railcards.summary': `{railcards, plural,
        =0    {No Railcards}
        one   {1 Railcard}
        other {# Railcards}
    }`,
    'booking.railcards.summary-v2': `{railcards, plural,
        =0    {No railcards}
        one   {1 Railcard}
        other {# Railcards}
    }`,
    'booking.general.discount': 'Discount applied',
    'booking.railcards.discount': '{railcard} discount applied',
    'booking.railcards.discount.warning': 'Minimum fare and time restrictions may apply',
    'booking.railcard.2TR': 'Two Together Railcard',
    'booking.railcard.DIC': 'Disabled Child Railcard',
    'booking.railcard.DIS': 'Disabled Persons Railcard',
    'booking.railcard.FAM': 'Family & Friends Railcard',
    'booking.railcard.HMF': 'HM Forces Railcard',
    'booking.railcard.JCP': 'Jobcentre Plus Travel Discount Card',
    'booking.railcard.NEW': 'Network Railcard',
    'booking.railcard.NGC': 'Annual Gold Card',
    'booking.railcard.SRN': 'Senior Railcard',
    'booking.railcard.TST': '26-30 Railcard',
    'booking.railcard.TSU': '16-17 Saver Railcard',
    'booking.railcard.VET': 'Veterans Railcard',
    'booking.railcard.YNG': '16-25 Railcard',

    'booking.railcards.season.warning':
        'Please be advised that Young Scot Railcard discounts on season tickets are not available on this site',

    'booking.summary.title':
        '{ticketType, select, single{Single} other{Return}} journey from {origin} to {destination}',
    'booking.summary.modalTitle': 'Your basket',
    'booking.summary.confirmClear': 'Please confirm that you wish to clear your basket',
    'booking.summary.emptyBasket': 'Clear basket',
    'booking.summary.pickOutbound': 'Choose your outbound ticket...',
    'booking.summary.pickReturn': 'Choose your return ticket...',
    'booking.summary.action.editJourney': 'Edit journey',
    'booking.summary.action.continue': 'Continue',
    'booking.summary.action.goBack': 'Go back',
    'booking.summary.totalLabel': 'Sub-total',
    'booking.summary.thisJourneyLabel': 'Current journey:',
    'booking.summary.returnHeading': 'Return',
    'booking.summary.outboundHeading': 'Outbound',
    'booking.summary.addAnotherJourney': 'Add another journey',

    'booking.railcards.stepper.adult': '{adults, plural, =1{Adult} other{Adults}}',
    'booking.railcards.stepper.child': '{children, plural, =1{Child} other{Children}}',

    'boooking.railcards.checkExpiry':
        'Please check the expiry date of your Railcard, to make sure it is still valid on your date of travel',

    'booking.preferences': 'Preferences',

    'booking.preferences.directOnly': 'Show direct services only',
    'booking.preferences.preferThames': 'Prefer Thameslink trains',
    'booking.preferences.smartcardOnly': 'Show <{global.key}> <{global.smartcard}> fares only',
    'booking.preferences.sleepersOnly': 'Show sleeper services only',
    'booking.preferences.additionalTime': 'Additional change time',

    'booking.fares.modal.pickOutbound': 'Select your outbound ticket',
    'booking.fares.modal.pickInbound': 'Select your inbound ticket',

    'booking.fares.name.withSubtypes': '{ticketName} with {subTypes}',
    'booking.fares.standard': 'Standard',
    'booking.fares.firstClass': 'First Class',
    'booking.fares.carnet': 'Carnet',
    'booking.fares.railcards': 'Railcards',
    'booking.fares.railcards.discounted': 'Railcard {discountPercent, select, false{discounted} other {+ {discountPercent}% discount}}',
    'booking.fares.discount': 'Discount',
    'booking.fares.discounted': 'Discounted {discountPercent}%',
    'booking.fares.discounted.fixed': 'Discounted {discountFixed}',
    'booking.fares.originalPrice': 'Original price: {price}',
    'booking.fares.save': 'save',
    'booking.fares.tickets.singles': 'Single tickets',
    'booking.fares.tickets.more-singles': 'Other single tickets',
    'booking.fares.tickets.no-singles': 'There are no single tickets available',
    'booking.fares.tickets.no-more-singles': 'There are no more single tickets available',
    'booking.fares.tickets.returns': 'Return tickets',
    'booking.fares.tickets.more-returns': 'Other return tickets',
    'booking.fares.tickets.no-returns': 'There are no return tickets available',
    'booking.fares.tickets.no-more-returns': 'There are no more return tickets available',
    'booking.fares.details.short': 'Ticket details',
    'booking.fares.details': 'Show ticket details',
    'booking.fares.details.hide': 'Hide ticket details',
    'booking.fares.groupsave': 'GroupSave',
    'booking.fares.groupsave.discounted': 'GroupSave {discountPercent, select, false{discounted} other {+ {discountPercent}% discount}}',
    'booking.fares.details.route': 'Valid routes',
    'booking.fares.details.dates': 'Valid dates',
    'booking.fares.details.dates.outward': 'Outward',
    'booking.fares.details.dates.return': 'Return',
    'booking.fares.details.dates.period': `{from} {showRange, plural, =1 {- {until}} other {}}`,
    'booking.fares.detail.alternate.origin': 'Valid from',
    'booking.fares.detail.alternate.destination': 'Valid to',
    'booking.fares.detail.alternate.info':
        'Your ticket is only valid for travel {direction} the following London Terminal stations:',
    'booking.fares.details.ticket': `{adults, plural,
            =0    {}
            one   {1 Adult}
            other {# Adults at {individualPrice}}
        }{children, plural,
            =0    {}
            one   {1 Child}
            other {# Children at {individualPrice}}
        } {ticketTypeOverride, plural,
            =0    {}
            other {{ticketTypeOverride} }}`,
    'booking.fares.details.carnetTicket': `{adults, plural,
            =0    {}
            one   {1 Adult ticket}
            other {# Adult tickets at {individualPrice}}
        }{children, plural,
            =0    {}
            one   {1 Child}
            other {# Child tickets at {individualPrice}}
        } {ticketTypeOverride, plural,
            =0    {}
            other {{ticketTypeOverride} }
        }`,
    'booking.fares.details.smartCarnet': `{passes, plural,
        =0    {}
        one   {1 Adult x 1 pass }
        other {1 Adult x # passes }
    }`,
    'booking.fares.details.carnet': 'Carnet information',
    'booking.fares.details.discount': 'Discount info',
    'booking.fares.details.discount.summary':
        'This ticket has been discounted by {discountPercent}%',
    'booking.fares.details.restriction': 'Show ticket restrictions',
    'booking.fares.details.groupSave.children':
        'To ensure you get the best value, some children have been treated as adults to qualify for the GroupSave discount.',
    'booking.fares.details.railcards.exception': 'Railcards were not applied to this search',
    'booking.fares.details.railcards.reminder':
        'You must bring your Railcard(s) with you on your journey',
    'booking.fares.details.bestfare.part1':
        'Select another single ticket in the next step from just ',
    'booking.fares.details.bestfare.part2': ' to get the best value of ',
    'booking.fares.details.bestfare.part3': ' for two single tickets',
    'booking.fares.details.seefares': 'See return',
    'booking.fares.details.breakOfJourney': 'Break of journey',
    'booking.fares.details.breakOfJourney.outward': 'Outward',
    'booking.fares.details.breakOfJourney.return': 'Return',
    'booking.fares.details.breakOfJourney.allowed': 'You can break and resume your journey',
    'booking.fares.details.breakOfJourney.notAllowed': 'You cannot break and resume your journey',
    'booking.fares.details.breakOfJourney.smartCarnet': 'You can break and resume your journey. You must start at the origin station and end your journey at the destination of keyGo Carnet ticket. Any break of journey must be made along the line of this route.',
    'booking.fares.details.daysave.childFareRestriction':
        'Child fares are only available to accompanied children under the age of 16. No more than four children may accompany each adult or group at the child fare',
    'booking.fares.details.daysave.offPeakRestriction.1':
        'Anytime on Saturdays, Sundays and Bank Holidays',
    'booking.fares.details.daysave.offPeakRestriction.2':
        'Not before 10:00 Monday to Friday (except Bank Holidays)',
    'booking.fares.details.daysave.offPeakRestriction.3':
        'Not between 16:15 and 19:15 Monday to Friday when boarding trains from East Croydon, London stations (Victoria, London Bridge and Clapham Junction)',
    'booking.fares.details.daysave.moreInfo': 'More information',
    'booking.fares.details.comparison-percentage': '{percentage}% cheaper',
    'booking.fares.details.comparison-caveat.P7D': 'vs {ticketNum} x Anytime Day Return Ticket',
    'booking.fares.details.comparison-caveat.P1M':
        'vs {ticketNum} x Weekly equivalent season ticket',
    'booking.fares.details.comparison-caveat.P1Y':
        'vs {ticketNum} x Monthly equivalent season ticket',
    'booking.fares.details.comparison-caveat.P3M':
        'vs {ticketNum} x Weekly equivalent season ticket',
    'booking.fares.details.comparison-caveat.P6M':
        'vs {ticketNum} x Weekly equivalent season ticket',
    'booking.fares.details.comparison-caveat.FL1':
        'vs {ticketNum} x Anytime Day Return ticket',
    'booking.fares.details.comparison-caveat.CFO': 'vs {ticketNum} x Anytime Day Ticket',
    'booking.fares.details.comparison-caveat.CFP': 'vs {ticketNum} x Anytime Day Ticket',
    'booking.fares.details.comparison-caveat.0BK': 'vs {ticketNum} x Off Peak Day Ticket',
    'booking.fares.details.comparison-caveat.0BI': 'vs {ticketNum} x Anytime Day Ticket',
    'booking.fares.details.comparison-sr-only-caveat.P7D':
        'this ticket is £{cheaperAmount} ({cheaperPercentage}%) cheaper compared to {ticketNum} Anytime Day Return Tickets,',
    'booking.fares.details.comparison-sr-only-caveat.P1M':
        'this ticket is £{cheaperAmount} ({cheaperPercentage}%) cheaper compared to {ticketNum} Weekly equivalent season tickets,',
    'booking.fares.details.comparison-sr-only-caveat.P1Y':
        'this ticket is £{cheaperAmount} ({cheaperPercentage}%) cheaper compared to {ticketNum} Monthly equivalent season tickets,',
    'booking.fares.details.comparison-sr-only-caveat.P3M':
        'this ticket is £{cheaperAmount} ({cheaperPercentage}%) cheaper compared to {ticketNum} Weekly equivalent season tickets,',
    'booking.fares.details.comparison-sr-only-caveat.P6M':
        'this ticket is £{cheaperAmount} ({cheaperPercentage}%) cheaper compared to {ticketNum} Weekly equivalent season tickets,',
    'booking.fares.details.no-comparison': 'No comparison available',
    'booking.fares.details.accessibility.warning':
        "<p class=\"b-alert__message\">First class areas on <{app.owner.short}> don't have wheelchair accommodation, or wheelchair access to accessible toilets. If you are travelling with another operator, please check with them</p>",
    'booking.fare.select-ticket':
        '£{price} {class}, {ticketType} ticket valid on the {description}, {action}',
    'booking.fare.select-cheapest-ticket':
        'Cheapest ticket £{price} {class} class {ticketType} ticket valid on the {description}, {action}',
    'booking.fare.select-season-ticket':
        '£{price}, {ticketClass}, {duration} {type} from {origin} to {destination}, {comparison} {description}, {action}',
    'booking.fare.selected-season-ticket':
        'You have selected a £{price}, {ticketClass}, {duration} {type} from {origin} to {destination}, it is valid from {validityStart} to {validityEnd}, click for more info.',
    'booking.fare.select-rovers-ticket':
        '£{price}, {ticketClass}, {ticketName} ticket, it is valid from {validityStart}, {action}',
    'booking.fare.selected-rovers-ticket':
        'You have selected a £{price}, {ticketClass}, {ticketName} ticket, it is valid from {validityStart}, click for more info.',
    'booking.fare.select-daysave-ticket': '<{booking.fare.select-rovers-ticket}>',
    'booking.fare.selected-daysave-ticket': '<{booking.fare.selected-rovers-ticket}>',
    'booking.fare.cheapest': 'CHEAPEST',
    'booking.fare.cheapest.label': `
        This is the cheapest service available.
        {count, plural,
            =0 {}
            =1 {There is 1 other service in this list with the same low price.}
            other {There are # other services in this list with the same low price.}
        }`,
    'booking.fare.cheapest-list': 'Cheapest Ticket',
    'booking.fare.remaining-availability': '{availability} left',
    'booking.fare.remaining-availability-prompt': '<b>Hurry!</b> {availability} Left',
    'booking.fare.limited-availability': 'Limited Availability',
    'booking.fare.unavailable': 'Fare unavailable',
    'booking.fare.info.validServices': 'Services valid with this ticket',
    'booking.fare.eligibleServicesModal.title': 'Services valid with this ticket',
    'booking.fare.eligibleServicesModal.outwardJourney': 'Outward journey',
    'booking.fare.eligibleServicesModal.returnJourney': 'Return journey',
    'booking.fare.eligibleServicesModal.serviceSelected': 'Service selected:',
    'booking.fare.eligibleServicesModal.ticketSelected': 'Ticket selected:',
    'booking.fare.eligibleServicesModal.rtn': 'Rtn: ',
    'booking.fare.eligibleServicesModal.validUntil': 'Valid until:',
    'booking.fare.eligibleServicesModal.one-month': '(1 month)',
    'booking.fare.eligibleServicesModal.description': '<p>Minimum fare and time restrictions may apply when using a railcard. Check <a target="_blank" href="https://www.nationalrail.co.uk/railcards/">Railcards | National Rail</a> before you travel.</p>',

    'booking.fare.eligibleServicesModal.table.earlier': 'Earlier',
    'booking.fare.eligibleServicesModal.table.earlier.ariaLabel': 'Show earlier trains',
    'booking.fare.eligibleServicesModal.table.later.ariaLabel': 'Show later trains',
    'booking.fare.eligibleServicesModal.table.show': 'Show',
    'booking.fare.eligibleServicesModal.table.later': 'Later',
    'booking.fare.eligibleServicesModal.table.departing': 'Departing',
    'booking.fare.eligibleServicesModal.table.from': 'From',
    'booking.fare.eligibleServicesModal.table.to': 'To',
    'booking.fare.eligibleServicesModal.table.duration': 'Duration',
    'booking.fare.eligibleServicesModal.table.changes': 'Changes',
    'booking.fare.eligibleServicesModal.table.status': 'Status',
    'booking.fare.eligibleServicesModal.table.serviceDetails': 'Service details',
    'booking.fare.eligibleServicesModal.table.error': 'Unable to load eligible services',
    'booking.fare.eligibleServicesModal.table.noServices': 'No services found',
    'booking.fare.eligibleServicesModal.table.ariaDescription': `
        Showing {
            count,
            plural,
            =0 {0 services}
            =1 {1 service}
            other {# services departing between {earliest} and {latest}}
        }
    `,

    'booking.fares.details.class.1st': '1st',
    'booking.fares.details.class.standard': 'Std',
    'booking.fares.details.class.1st.full': 'First Class',
    'booking.fares.details.class.standard.full': 'Standard',
    'booking.fares.no-results': 'There are no results available for this search.',
    'booking.fares.no-results.link': 'Try changing your search parameters.',
    'booking.fares.nre-link.text': 'Details',

    'journey.tocDetails.service': 'A {toc} service',
    'journey.tocDetails.single': '{toc}',
    'journey.tocDetails.multiple': '{toc} plus other operators',

    'journey.duration.direct': 'Direct',
    'journey.duration': 'Duration',
    'journey.duration.changes': '{changes, plural, =0 {Direct} =1 {Changes 1 } other {Changes #}}',
    'journey.duration.changesInverted':
        '{changes, plural, =0 {Direct} =1 {1 change } other {# changes }}',
    'journey.duration.on-time': 'On time',
    'journey.duration.late': 'late',
    'journey.duration.train-cancelled': 'Train cancelled',

    'journey.warnings.replacementBus':
        'This journey includes {busCount} replacement {busCount, plural, =1{bus} other{buses}}.',
    'journey.warnings.delayed-departure': 'Departure has been delayed by {departureDelay}.',
    'journey.warnings.delayed-arrival': 'Arrival has been delayed by {arrivalDelay}.',
    'journey.warnings.delayed': 'This journey has been delayed.',
    'journey.warnings.notAvailableWithTicket': `{ticketName} not available.`,
    'journey.warnings.cancelled': 'This train has been cancelled.',
    'journey.warnings.cancelled-with-reason': '{journeyCancellationReason}.',
    'journey.warnings.serviceInThePast': 'This train has already left the station.',
    'journey.warnings.overtaken': 'This train will be overtaken by a later service.',
    'journey.warnings.notCollectable':
        'This train departs in less than {minTodOffset} and ticket collection is not possible.',
    'journey.warnings.noneOutwardCollectable':
        'All outward trains depart in less than {minTodOffset} and ticket collection is not possible.',
    'journey.warnings.notCollectableSmartcard':
        'This train departs in less than {minSmartcardOffset} and ticket collection is not possible.',
    'journey.warnings.arrivalAfterReturnDepart':
        'This train departs before your selected outbound train arrives.',
    'journey.warnings.hasCountedPlace': 'Please note this service has mandatory reservations.',
    'journey.warnings.hasOtherAsServiceOperator': 'This journey requires a connection involving an alternative method of transport, that will not be covered by your rail ticket. Please plan ahead and check before you travel.',

    'booking.seating': 'Reserve a seat',
    'booking.seating.preferences': 'Reserve Seating',
    'booking.seating.noPreference': 'No preference',
    'booking.seating.direction': 'Direction',
    'booking.seating.direction.forwards': 'Forwards',
    'booking.seating.direction.backwards': 'Backwards',
    'booking.seating.position': 'Position',
    'booking.seating.position.aisle': 'Aisle seat',
    'booking.seating.position.window': 'Window seat',
    'booking.seating.position.individual': 'Single seat (First Class only)',
    'booking.seating.seatType': 'Seat type',
    'booking.seating.seatType.airline': 'Row seat',
    'booking.seating.seatType.table': 'Table seat',
    'booking.seating.features': 'Features',
    'booking.seating.features.quietCoach': 'Quiet Coach',
    'booking.seating.features.nearToilets': 'Near toilets',
    'booking.seating.features.powerSocket': 'Power socket',
    'booking.seating.features.nearLuggageRack': 'Near luggage rack',

    'booking.seating.noPromises': 'Seat preferences cannot be guaranteed.',
    'booking.seating.outbound': 'Outbound',
    'booking.seating.inbound': 'Return',
    'booking.seating.seat': 'Seat',
    'booking.seating.coachSeat': 'Coach {coach}, Seat {seat}',
    'booking.seating.youAskedUsFor': 'You asked us for',
    'booking.seating.expires': 'Reservation expires',
    'booking.seating.loading': 'Reserving you some seats...',
    'booking.seating.none': 'Seat reservations are not available on this service',

    'booking.seating.describe.direction': '{direction, select, FACE{Forward facing} BACK{Backwards facing} other{}}',
    'booking.seating.describe.position': '{position, select, AISL{Aisle seat} WIND{Window seat} INDL{Single seat (First class only)} other{}}',
    'booking.seating.describe.seatType': '{seatType, select, AIRL{Row seat} TABL{Table seat} other{}}',

    'booking.seating.usualSeatingAvailability.modalTitle': 'SeatFinder',
    'booking.seating.usualSeatingAvailability.update': 'Update service selection',
    'booking.seating.usualSeatingAvailability.bodyCopy1': 'Check how busy your train is before you travel.',
    'booking.seating.usualSeatingAvailability.changes.direct': 'Direct',
    'booking.seating.usualSeatingAvailability.changes.changes': '{changes, plural, =1 {# change} other {# changes}}',
    'booking.seating.usualSeatingAvailability.seating.many': 'Usually many seats available',
    'booking.seating.usualSeatingAvailability.seating.plenty': 'Usually plenty of seats available',
    'booking.seating.usualSeatingAvailability.seating.some': 'Usually some seats available',
    'booking.seating.usualSeatingAvailability.seating.few': 'Usually only a few seats available',
    'booking.seating.usualSeatingAvailability.seating.standing': 'Usually standing room only',
    'booking.seating.usualSeatingAvailability.seating.noData': 'Seating data unavailable',
    'booking.seating.usualSeatingAvailability.seating.noDataForLeg': 'No data available for this journey leg',
    'booking.seating.usualSeatingAvailability.serviceFareMismatch': 'This service is not valid with your currently selected ticket type.',
    'booking.seating.usualSeatingAvailability.serviceHeading': 'Service',
    'booking.seating.usualSeatingAvailability.serviceSelectionLegend.outward': 'Select outward service',
    'booking.seating.usualSeatingAvailability.serviceSelectionLegend.inward': 'Select inward service',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.select': 'Select this service',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.punctuality': 'Service punctuality',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.departureTime': 'Service departure time',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.arrivalTime': 'Service arrival time',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.duration': 'Service duration',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.changeCount': 'Number of changes for this service',
    'booking.seating.usualSeatingAvailability.serviceTableHeading.seatingAvailability': 'Service seating availability',
    'booking.seating.usualSeatingAvailability.serviceTable.selectService': 'Select this service',
    'booking.seating.usualSeatingAvailability.loadEarlierServices': 'Earlier',
    'booking.seating.usualSeatingAvailability.loadEarlierServicesAriaLabel.outward': 'Load earlier outward services',
    'booking.seating.usualSeatingAvailability.loadEarlierServicesAriaLabel.inward': 'Load earlier inward services',
    'booking.seating.usualSeatingAvailability.loadLaterServices': 'Later',
    'booking.seating.usualSeatingAvailability.placeholder': 'No Return Selected',
    'booking.seating.usualSeatingAvailability.loadLaterServicesAriaLabel.outward': 'Load later outward services',
    'booking.seating.usualSeatingAvailability.loadLaterServicesAriaLabel.inward': 'Load later outward services',
    'booking.seating.usualSeatingAvailability.serviceTable.key.many': 'Many seats available',
    'booking.seating.usualSeatingAvailability.serviceTable.key.plenty': 'Plenty of seats available',
    'booking.seating.usualSeatingAvailability.serviceTable.key.some': 'Some seats available',
    'booking.seating.usualSeatingAvailability.serviceTable.key.few': 'Only a few seats available',
    'booking.seating.usualSeatingAvailability.serviceTable.key.standing': 'Standing room only',
    'booking.seating.usualSeatingAvailability.serviceTable.key.noData': 'Data unavailable ',
    'booking.seating.usualSeatingAvailability.serviceTable.title': 'Usual seating availability',

    'booking.stepFreeAccess.full': 'Step-free access',
    'booking.stepFreeAccess.partially': 'Some step-free access',
    'booking.stepFreeAccess.not': 'No step-free access',

    'booking.stepFreeAccess.button.info': 'Press to find out more about step-free access at {station}',

    'booking.journeySupport.title': 'Journey support',
    'booking.journeySupport.toggle.stepFree': 'Press to display step-free information for each journey',
    'booking.journeySupport.toggle.seatAvailability': 'Press to display seat availability information for each journey',
    'booking.journeySupport.toggle.stepFree.hide': 'Press to hide step-free information for each journey',
    'booking.journeySupport.toggle.seatAvailability.hide': 'Press to hide seat availability information for each journey',
    'booking.journeySupport.toggle.title.stepFree': 'Step-free access',
    'booking.journeySupport.toggle.title.seatAvailability': 'SeatFinder',
    'booking.journeySupport.toggle.title.stepFree.show': 'Show Step-free access information for your journey',
    'booking.journeySupport.toggle.title.seatAvailability.show': 'Show train seating capacity for journey',
    'booking.journeySupport.toggle.title.stepFree.hide': 'Hide Step-free access information for your journey',
    'booking.journeySupport.toggle.title.seatAvailability.hide': 'Hide train seating capacity for journey',

    'booking.journeySupport.modal.title': 'Journey Support',
    'booking.journeySupport.modal.key': 'Journey support key',
    'booking.journeySupport.modal.seatFinder.title': 'SeatFinder',
    'booking.journeySupport.modal.seatFinder.info': '<p>SeatFinder data tells you how busy our trains usually are, to help you find a quieter time to travel. SeatFinder data is only available for Southeastern-operated services.</p>',
    'booking.journeySupport.modal.stepFree.title': 'Step-free access',
    'booking.journeySupport.modal.stepFree.info': `
        <p>Step-free access stations have lifts or ramps, or a combination of both, so you don’t have to use escalators or stairs to access the platforms.</p>
        <p>If there are any short-term changes to a station’s step-free status, an alert will be shown in our journey planner results.</p>
        <p>Step-free access - This is a Category A station, it has step-free access to all platforms.</p>
        <p>Some step-free access - This is a Category B station, it has a degree of step-free access to the platforms, which may be in both directions or in one direction only - please check details.</p>
        <p>No step-free access - This is a Category C station, it has no step-free access to any platform.</p>`,
    'booking.journeySupport.modal.label.show': 'Press to display journey support key',
    'booking.journeySupport.modal.label.hide': 'Press to hide journey support key',

    'seating.error': `Sorry, we couldn't reserve you any seats. This could be because your ticket selection is too close to the time of departure.`,
    'seating.error.failedNotRequired': 'Seat reservations not required.',

    'header.favourites': 'Favourites',
    'header.welcome': 'Welcome back,',
    'header.basket.journeyText': '{journeys, plural, =1 {# journey} other {# journeys}}',

    'favourites.modal.title': 'Favourite tickets',
    'favourites.modal.error.NoMatchingFareError':
        'Sorry, We could not find a ticket that matches your previous order.',
    'favourites.modal.step1Title': 'Choose a ticket',
    'favourites.modal.step1Text': 'These are your saved tickets, select one to re-order it.',
    'favourites.modal.step2Title': 'Choose a date',
    'favourites.modal.step3Title': 'Add PlusBus',
    'favourites.modal.reorder': 'Re-order',
    'favourites.modal.noPlusBus':
        'PlusBus is not available on this route. You can continue to reorder your selected {count, plural, =1 {ticket} other {tickets}} without PlusBus.',
    'favourites.error.limit.title': 'We could not save your favourite ticket',
    'favourites.error.limit.message':
        'You have saved the maximum amount of favourites. Please remove one or more favourites and try again.',

    'button.text.close': 'Close',
    'button.text.cancel': 'Cancel',
    'button.text.select': 'Select',
    'button.text.done': 'Done',
    'button.text.apply': 'Apply',
    'button.text.search': 'Search',
    'button.text.searchExistingUser': 'Search your email',
    'button.text.save': 'Save',
    'button.text.delete': 'Delete',
    'button.text.continue': 'Continue',
    'button.text.next': 'Next',
    'button.text.check': 'Check',
    'button.text.back': 'Back',
    'button.text.confirm': 'Confirm',
    'button.text.submit': 'Submit',
    'button.text.edit': 'Edit',
    'button.text.removeJourney': 'Remove journey',
    'button.text.addjourney': 'Add another journey',
    'button.text.paymentDetails': 'Continue',
    'button.text.paymentDetailsOthers': 'Other payment options',
    'button.text.checkout': 'Checkout',
    'button.text.cancelaccount': 'Cancel my account',
    'button.text.showFilters': 'Show filters',
    'button.text.confirmSmartcardReceived': 'Confirm <{global.smartcard}> received',
    'button.text.prev-tip': 'Previous tip',
    'button.text.next-tip': 'Next tip',
    'button.text.amend': 'Amend',
    'button.text.updatePaymentDetails': 'Update payment details',
    'button.text.refresh': 'Refresh',
    'button.text.cancelReservation': 'Cancel reservation',
    'button.text.upcomingJourneys': 'Your upcoming journeys',
    'button.text.previousJourneys': 'Your previous journeys',

    'icon.disruption.cancelled.aria': 'Cancelled',
    'icon.disruption.disabled.aria': 'Disabled for trip',
    'icon.disruption.delayed.aria': 'Delayed',
    'icon.disruption.disruption.aria': 'Disruption',
    'icon.disruption.replacementBus.aria': 'Bus replacement',

    'booking.delivery.header': 'Select a delivery option',
    'booking.delivery.stationSelect': 'Select a station',

    'booking.delivery.ticketondeparture.available':
        'In { todCollectionMinimumTime }, you can collect your ticket(s) from a self-service machine at {origin} or any other enabled station.',
    'booking.delivery.ticketondeparture.notavailable':
        'In { todCollectionMinimumTime }, you can collect your ticket(s) from a self-service machine at any enabled station.',
    'booking.delivery.ticketondeparture.stationNotEnabledWarning':
        'Unfortunately, {origin} does not have a self-service machine. Please choose another station you would prefer to collect your tickets from.',
    'booking.delivery.ticketondeparture.differingCollectionStationWarning':
        'Your preferred collection station is different to your first departure station. Please keep this in mind when collecting your tickets.',

    'booking.delivery.leadPassenger.useForAllTrips': 'Use this name for all journeys',
    'booking.delivery.loadingdate.secondaryInfoLinking': 'Your ticket will also be ready for collection from the gates at <b>{station}</b> in 2 hours.',
    'booking.delivery.loadingdate.chooseAnotherStation': 'To choose another station, ',


    'sticket.photo.upload.title': 'sTicket Photo',
    'sticket.photo.upload.confirm.message': "As you have an active sTicket, we have to approve your change of photo. While your photo is awaiting approval, you can continue using your active sTicket.\n\nPlease note, if a member of rail staff can't confirm you are the ticket holder, you may be charged the full ticket price or a penalty fare.",
    'sticket.photo.upload.confirm.title': 'Changing your photo',
    'sticket.photo.upload.delete.title': 'Delete photo',
    'sticket.photo.upload.delete.confirm.text': 'Are you sure you want to delete your sTicket photo?',
    'sticket.photo.upload.delete.confirm.button': 'Delete',
    'sticket.photo.change.notification': 'Photo change request has been successfully sent',
    'sticket.photo.waiting': '<b>Your new photo is waiting to be approved</b>. While your new photo is awaiting approval, you can continue using any active sTickets with your current photo.',
    'delivery.sticket.photo.unavailable.title': 'sTicket unavailable',
    'delivery.sticket.photo.unavailable.text': 'Unfortunately, STickets are currently unavailable for this line or route. Please choose another delivery method',
    'delivery.sticket.photo.title': 'Please make sure your image:',
    'delivery.sticket.photo.bullet1': 'Is instantly recognisable as you',
    'delivery.sticket.photo.bullet2': 'Is a Passport style photograph',
    'delivery.sticket.photo.bullet3': 'There is nothing covering your face',
    'delivery.sticket.photo.bullet4': 'Do not use an image filter',
    'delivery.sticket.photo.bullet5': "Please note, if a member of rail staff can't confirm you are the ticket holder, you may be charged the full ticket price or liable to a penalty fare",
    'delivery.sticket.errors.SticketDeliveryOptionIsNotAvailableOnRoute': 'sTickets are only available for journeys on the Southeastern network and on selected routes enabled by other Train Operating Companies. Please note:<ul><li>sTickets are not available for journeys which allow travel on London Underground services (such as Travelcards)</li><li>Early Bird discounted Season tickets are not available as sTickets</li></ul>',
    'delivery.sticket.errors.TooManyTicketsInBasketError': 'You can only purchase one sTicket at a time.',
    'delivery.sticket.details': `
        <p>sTicket is a digital version of a Season ticket provided as a QR code. It is instantly available to download and accessed through the {appName} app. No need to wait for an email or queue at the station.</p>
        <p>Scan the QR code at the station ticket gate (where barcode scanners are available) or show to staff when asked.</p>
        <p>If you’re buying on behalf of another person, please do so from their account or select a different collection method.</p>
        <p>Please note: sTickets are not accepted at London Underground stations or on London Underground services.</p>`,
    'delivery.sticket.recommended': 'Recommended',
    'delivery.moreInfo': 'more info',
    'delivery.moreInfoTitle': 'More info',
    'delivery.moreInfo.aria': 'Click here for more information about {optionName} option.',
    'delivery.collect-from-station': 'Collect ticket from station',
    'delivery.e-ticket': 'Download eTicket',
    'delivery.e-ticket.gPass': 'Google Pass',
    'delivery.e-ticket.aPass': 'Apple Pass',
    'delivery.e-ticket.detail': `
        <p>An eTicket is a digital ticket with a QR code. Once your booking is complete, you will be sent a separate email with your download options:</p>
        <ul class="list-bullets-modal">
            <li>Add the eTicket to your Apple or Google Wallet (recommended)</li>
            <li>Print the PDF document with your eTicket</li>
            <li>Forward the PDF attachment to the intended user so that they can print or add the eTicket to their mobile.</li>
        </ul>
        <p>Scan the QR code at the ticket gate at the station (where barcode scanners are available) or show to staff when asked.</p>
        <p>Please note: eTickets are not accepted on London Underground services.</p>
        {eTicketsFaqLink, select, null {} other {<p>See {eTicketsFaqLink} for more information.</p>}}`,
    'delivery.e-ticket.faqsLinkText': 'eTicket FAQs page',
    'delivery.e-ticket.recommended': 'Recommended',
    'delivery.e-ticket.firstBenefit': 'Instantly available to download on your phone, no need to queue at the station.',
    'delivery.e-ticket.warning.message': 'eTickets are not available for Travelcards, Boundary Zone tickets and journeys involving London Underground.',
    'delivery.e-ticket.postal.message': 'You will receive a printed eTicket.',
    'delivery.sTicket.firstBenefit': 'Instantly available to download in the Southeastern app. No need to wait for an email or queue at the station',
    'delivery.print.daySave.message': '<p>Our new tickets have been designed to be more sustainable and easier to recycle. Just scan the barcode on the gate at the station and show to staff when asked.</p>',
    'delivery.print-at-home': 'Print@Home Ticket',
    'delivery.print-at-home.detail':
        '<p>Print at Home tickets are only valid when presented with a valid form of identification (ID) issued in the name of the person holding the ticket. This can be in the form of a Credit/Debit card, Passport or Driving License. You may be asked to show your ID along with your Print at Home ticket if requested by a member of staff.</p><p>Your ticket is only valid on Gatwick Express trains and will not be accepted on trains run by any other operator.</p><p>Once your booking has been completed your ticket will be sent to you. This must be printed by you on plain white A4 paper prior to making your journey.</p>',
    'delivery.print-at-home.instructions':
        'Lead passenger <strong>MUST</strong> be travelling and will need to show ID when asked.</br><strong>ALL</strong> passengers must travel together.',
    'delivery.1st-class-mail': '1st Class Mail',
    'delivery.1st-class-mail.detail':
        '<p>Tickets purchased by 16:00 will be processed and dispatched within 24 hours of booking using Royal Mail First Class post. Please allow five working days for delivery.</p><p>Please note we are unable to guarantee delivery of any unsigned for first class post, and would suggest using a ticket machine at the station as it’s quicker, easier and free of charge.</p><p>Applicable to UK addresses only.</p><p>Don’t forget you can get your tickets for free if you purchase them on the <{global.key}> <{global.smartcard}> or collect from a ticket vending machine at a station.</p>',
    'delivery.royal-mail-signed-for-1st-class.detail': '<p>We now offer this service free for bookings over £100.</p><p>For bookings less than £100 you can select this delivery option for a cost of £2.00, it offers:</p><ul><li>Proof of postage.</li><li>Signature on delivery.</li><li>Online confirmation of delivery.</li></ul><p>Please note, we are unable to guarantee next day delivery, if this is a requirement then we recommend you use the Next Day Special Delivery option.</p>',
    'delivery.royal-mail-special-delivery-guaranteed': 'Royal Mail Special Delivery Guaranteed{trademark}',
    'delivery.royal-mail-special-delivery-guaranteed.detail': '<p>Order your tickets by 16:00 and we will guarantee delivery the next working day*.</p><p>{price} (inclusive of VAT) guarantees delivery before 13:00 and provides insurance up to £500.00.</p><p>*Remote areas and Islands of Scotland will take longer to reach. Orders placed on Saturday or Sunday are dispatched on Monday and delivered on Tuesday. Orders placed on a Bank Holiday are dispatched on Tuesday and delivered on Wednesday.</p>',
    'delivery.royal-mail-special-delivery-insured.detail': '<p>Order your tickets by 16:00 and we will guarantee delivery the next working day*.</p><p>{price} (inclusive of VAT) guarantees delivery before 13:00 and provides insurance up to £2500.00.</p><p>*Remote areas and Islands of Scotland will take longer to reach. Orders placed on Saturday or Sunday are dispatched on Monday and delivered on Tuesday. Orders placed on a Bank Holiday are dispatched on Tuesday and delivered on Wednesday.</p>',
    'delivery.add-to-my-key-smartcard.AddAnotherJourneyInfo': 'You can\'t add an another journey because only one ticket can be purchased at a time with the <{global.smartcard}>',
    'delivery.add-to-my-key-smartcard.TooManyTicketsOnSmartcardError': 'Unavailable because you have reached the maximum number of tickets on your <{global.smartcard}>.',
    'delivery.add-to-my-key-smartcard.TooManyTicketsInBasketError': 'Unavailable because only one smart ticket can be purchased at a time.',
    'delivery.add-to-my-key-smartcard.TooManyAdultTicketsInBasketError': '<p>To use your <{global.key}> <{global.smartcard}> you need to remove a ticket(s) from the basket to match the smartcards on your account.</p><p>Find out more about linking accounts click <a href={url}>here</a></p>',
    'delivery.add-to-my-key-smartcard.TooManyChildTicketsInBasketError': '<p>To use your <{global.key}> <{global.smartcard}> you need to remove a ticket(s) from the basket to match the smartcards on your account.</p><p>Find out more about linking accounts click <a href={url}>here</a></p>',
    'delivery.add-to-my-key-smartcard.NotEnoughActiveSmartcardsError': '<p>Unavailable because you have <strong>{adultTicketCount} adult ticket{adultTicketCount, plural, =1{} other{s}}</strong> and <strong>{childTicketCount} child ticket{childTicketCount, plural, =1{} other{s}}</strong> in your basket but only <strong>{adultSmartcardCount} adult <{global.key}> <{global.smartcard}>{adultSmartcardCount, plural, =1{} other{s}}</strong> and <strong>{childSmartcardCount} child <{global.key}> <{global.smartcard}>{childSmartcardCount, plural, =1{} other{s}}</strong> linked to your account.</p><p>To use <{global.key}> <{global.smartcard}> you need to <strong>remove a ticket(s)</strong> from the basket to match the <{global.smartcards}> on your account.</p>',
    'delivery.add-to-my-key-smartcard.SmartcardCantBeDeliveredInTimeError': '<strong>Unavailable as there is not enough time to guarantee delivery before start date. Please allow five working days for delivery.</strong>',
    'delivery.add-to-my-key-smartcard.PendingSmartcardError': 'Unavailable because your new <{global.smartcard}> request is being processed.',
    'delivery.add-to-my-key-smartcard.PendingActionlistError': 'Unavailable because you have a previous smart ticket still to be collected onto your <{global.smartcard}>.',
    'delivery.add-to-my-key-smartcard.ExpiredSmartcardError': 'Unavailable because your <{global.smartcard}> has expired.',
    'delivery.add-to-my-key-smartcard.InvalidSmartcardStatusError': 'Unavailable because your <{global.smartcard}> is not valid.',
    'delivery.add-to-my-key-smartcard.InvalidKeygoError': 'To continue with your <{global.keyGo}> Carnet purchase, you need to register for <{global.keyGo}>.',
    'delivery.add-to-my-key-smartcard.InvalidKeygoError.button': 'Register for <{global.keyGo}>',
    'delivery.add-to-my-key-smartcard.NotEnoughTimeToActionlistTicketError': 'There is not enough time to action list the ticket for customers with a <{global.smartcard}>.',
    'delivery.add-to-my-key-smartcard.NotCorrectAgeForFareError': 'This fare is not valid for your <{global.smartcard}> due to age restrictions.',
    'delivery.add-to-my-key-smartcard.DisabledPreEncodeError': 'Unfortunately, you cannot currently load this ticket onto your <{global.smartcard}>.',
    'delivery.add-to-my-key-smartcard.SmartCarnetExceededNumberOfPassesError': '<p><{global.keyGo}> carnet in your basket have exceeded the maximum limit of 30.</p><p><strong>Please reduce the number of <{global.keyGo}> carnet from your basket to proceed.</strong></p>',
    'delivery.add-to-my-key-smartcard.InvalidCarnetTicketTypeError': '<p>Your basket contains <{global.keyGo}> carnets and other types of tickets.</p><p><strong>Please remove non <{global.keyGo}> carnet tickets from your basket to proceed.</strong></p',
    'delivery.add-to-my-key-smartcard.AssignToNewSmartcardError': 'Select a linked <{global.key}> <{global.smartcard}> below. If this ticket is intended for you, please choose a start date of <b>{deliveryDate}</b> or later to allow delivery of your new <{global.key}> <{global.smartcard}>.',
    'delivery.add-to-my-key-smartcard.NoSmartcardError': 'You do not have a <{global.key}> <{global.smartcard}> with which to collect your ticket.',
    'delivery.smartcard.firstBenefit': '<{global.key}> <{global.smartcard}> won’t wear out like paper tickets',
    'delivery.smartcard.secondBenefit': 'If you lose your <{global.key}> <{global.smartcard}>, we’ll send you a replacement free of charge',
    'delivery.smartcard.thirdBenefit': 'One Click Delay Repay notifications – Sign up and we’ll prepare a claim for you if you are delayed 15 mins or more.',
    'delivery.fulfilment-options-info': 'Details on all <strong>fulfilment options</strong>',

    'delivery.smartcard-passengers.assign-instruction': 'Assign ticket(s) to your <{global.key}> <{global.smartcard}> or linked <{global.key}> <{global.smartcard}> accounts',
    'delivery.smartcard-passengers.assign-select-placeholder': 'Assign to a <{global.key}> <{global.smartcard}>',
    'delivery.smartcard-passengers.error': 'You must assign the ticket to either your Key Smartcard or a linked Smartcard account from the list above.',
    'delivery.smartcard-passengers.your-smartcard': 'Your <{global.key}> <{global.smartcard}>',
    'delivery.smartcard-passengers.new-smartcard': 'New <{global.key}> <{global.smartcard}>',
    'delivery.smartcard-passengers.card-ending': 'Card number ending',
    'delivery.smartcard-passengers.instruction': 'Tickets must be loaded onto each individual Smartcard before travel',
    'delivery.royal-mail-recorded-delivery.detail':
        'delivery.royal-mail-signed-for-1st-class.detail',
    'delivery.royal-mail-special-delivery.detail':
        'delivery.royal-mail-special-delivery-guaranteed.detail',
    'delivery.the-key-smartcard': 'Load to the <{global.key}> <{global.smartcard}>(s)',
    'delivery.more-info.postal.paper-tickets-info': '(Paper Tickets)',

    'delivery.smartcard.flexiWarning.heading': 'Important information',
    'delivery.smartcard.flexiWarning.bodyCopy1': 'Do you have <{global.keyGo}> registered on your Key Smartcard?',
    'delivery.smartcard.flexiWarning.bodyCopy2': 'If so, you\'ll need to register for a second Key Smartcard before you buy your Flexi Season ticket, more details can be found below.',
    'delivery.smartcard.flexiWarning.moreInfo': 'More information about keyGo and Flexi Season',
    'delivery.smartcard.flexiWarning.acknowledge': 'Yes, I understand',
    'delivery.smartcard.flexiWarning.continue': 'Continue to payment',

    'delivery.smartcard.button.another-station': 'Press to choose another station to collect your ticket',

    'delivery.name': 'Full name',

    'delivery.sticket.benefits.card.first.title': 'Immediate Access',
    'delivery.sticket.benefits.card.first.info': 'Available immediately in the Southeastern app',
    'delivery.sticket.benefits.card.second.title': 'Easy access',
    'delivery.sticket.benefits.card.second.info': 'Stored securely in the Southeastern app ready to use',
    'delivery.sticket.benefits.card.third.title': 'Scan through',
    'delivery.sticket.benefits.card.third.info': 'No need to hang around the station, just scan and go',

    'delivery.eticket.benefits.card.first.title': 'Quick delivery',
    'delivery.eticket.benefits.card.first.info': 'Barcode ticket available immediately by email',
    'delivery.eticket.benefits.card.second.title': 'Easy access',
    'delivery.eticket.benefits.card.second.info': 'Add your eTicket to your phone wallet or print at home',
    'delivery.eticket.benefits.card.third.title': 'Scan through',
    'delivery.eticket.benefits.card.third.info': 'No need to hang around the station, just scan and go',

    'delivery.smartcard.benefits.card.first.title': 'Quick delivery',
    'delivery.smartcard.benefits.card.first.info': 'Load your ticket at home with the Southeastern app within 5 mins',
    'delivery.smartcard.benefits.card.second.title': 'Tap through',
    'delivery.smartcard.benefits.card.second.info': 'No need to hang around the station, just tap and go',
    'delivery.smartcard.benefits.card.third.title': 'Travelcard ready',
    'delivery.smartcard.benefits.card.third.info': 'Can include TfL travel in London when you buy a Travelcard ticket',

    'delivery.option.type.eTicket': 'eticket',
    'delivery.option.type.sTicket': 'sticket',
    'delivery.option.type.smartcard': 'smartcard',

    'booking.delivery.ToD': 'Collect from station',
    'booking.delivery.notAvailable':
        'Sorry. There are no collection or delivery options available for your order.',
    'booking.delivery.notAvailable.paper':
        'Sorry. There are no collection or delivery options available for your order. This may be because you are attempting to buy a paper ticket within { todCollectionMinimumTime } of your time of departure.',
    'booking.delivery.smartcardCollectionNotPossible':
        '<{global.smartcardUppercase}> ticket collection not possible',
    'booking.delivery.smartcardWithinTwoHours':
        'You cannot buy a <{global.smartcard}> ticket where your journey starts less than 2 hours after your purchase time, as it may take up to 2 hours before your ticket is ready to collect.',
    'booking.delivery.smartcardFull':
        'Your <{global.smartcard}> is currently full, and has no capacity to load another product.',
    'booking.delivery.smartcardBusy': 'There is another ticket awaiting collection on your card.',
    'booking.delivery.smartcardLoginOrRegister':
        'Add to a new, or existing, <{global.key}> <{global.smartcard}> ',
    'booking.delivery.smartcardApply': 'Add to a new <{global.key}> <{global.smartcard}> ',
    'booking.delivery.smartcardUnavailable': '<{global.key}> <{global.smartcard}> unavailable',
    'booking.delivery.smartcard.modal.title': 'Download your tickets at home, instantly',
    'booking.delivery.smartcard.modal.body': 'Using the {brand} On Track mobile app, load your ticket straight onto your <{global.key}> <{global.smartcard}> by simply tapping the <{global.smartcard}> onto the back of your phone after purchase.',
    'booking.delivery.smartcard.modal.cta': 'Download our app from the App Store or Google Play',
    'booking.delivery.loadSmartcardIllustration': 'Load <{global.smartcard}> illustration',
    'booking.delivery.sticket.modal.title': 'Download your sTickets at home, instantly',
    'booking.delivery.tryAnotherSearch': 'Try another search',
    'booking.delivery.free': 'Free',
    'booking.delivery.unavailable': 'Unavailable',
    'booking.delivery.unavailable.body': "We're sorry, but you won't be able to pick up this Season ticket from the station or receive it via post. Please choose another way to collect it instead",
    'booking.delivery.unavailable.smartcard': "Sorry, we couldn't find any ways to collect this ticket right now because of a technical problem. Please come back later and try again.",
    'booking.delivery.smartcard.differingCollectionStationWarning': 'booking.delivery.ticketondeparture.differingCollectionStationWarning',

    'booking.delivery.header.smartcard': '<{global.key}> <{global.smartcard}> ticket collection',
    'booking.delivery.loadingstation': 'Select the station that you would like to load your tickets from',
    'booking.delivery.loadingdate': 'Choose a loading date',

    'booking.delivery.loading.info.gtr': 'Activate your Flexi Pass just before you travel. Once activated, Flexi Passes cannot be cancelled and are non-refundable',

    'booking.delivery.loading.important.gtr1': 'Please note',
    'booking.delivery.loading.important.gtr2': ' – when collecting from station gate or validator, your Flexi day pass will also be activated at the same time.',

    'booking.delivery.loadingdate.info': 'Please allow a minimum of two hours after purchase before loading the ticket(s) to the <{global.key}> <{global.smartcard}>(s) or use the {brand} On Track mobile app to load the ticket(s) now.',
    'booking.delivery.loading.info.interop': 'You can also load your Flexi Season ticket at one of our ticket offices near you, please allow a minimum of two hours after purchasing for loading via ticket offices.',
    'booking.delivery.loadingdate.info.short': 'You can now load your tickets onto your <{global.key}> <{global.smartcard}> instantly using our app',

    'booking.delivery.loadingdate.downloadGTR': 'You can load and activate your Flexi Season tickets within 15 minutes via our {brand} On Track mobile app. ',
    'booking.delivery.loadingdate.downloadGTRPaymentConfirmation': 'Please use the {brand} On Track mobile app to load your ticket to your Key Smartcard now.',
    'booking.delivery.loading.collectViaAppNow': 'Please use the {brand} On Track mobile app to load the ticket to your Key Smartcard.',
    'booking.delivery.loading.collectViaAppNowLinking': 'Please use the {brand} On Track mobile app to load the ticket(s) to your Key Smartcard(s).',
    'booking.delivery.loadingdate.secondaryInfoGTRPartOne': 'You can load and activate your Flexi Season by placing your smartcard at the station gates and validators.',
    'booking.delivery.loadingdate.secondaryInfoGTRPartTwo': 'Please select the station that you would like to load and activate Flexi Season from. Please allow a minimum of two hours after purchasing to load via station gates and validators.',

    'booking.delivery.loadingdate.orderInfoGTRPartOne': 'You can load and activate your Flexi Season by placing your smartcard at {location} station gates and validators. ',
    'booking.delivery.loadingdate.orderInfoGTRPartTwo': 'Please allow a minimum of two hours after purchasing to load via station gates and validators.',

    'booking.delivery.smartcard.number': '<{global.key}> <{global.smartcard}> number',
    'booking.delivery.smartcard.noselected':
        "Are you sure you don't want to proceed with a <{global.key}> <{global.smartcard}>?",
    'booking.delivery.smartcard.noselected.back':
        'Continue with <{global.key}> <{global.smartcard}>',
    'booking.delivery.smartcard.noselected.continue': "Yes, I'm sure",
    'booking.delivery.button.back': 'Go back',
    'booking.delivery.goBack.modal.title': 'Go back options',

    'booking.additional-fares-collapsible.trigger': 'View all available fares',

    'booking.additional-fares-collapsible.trigger-v2-show': 'See <b>all</b> available fares',
    'booking.additional-fares-collapsible.trigger-v2-hide': 'Hide <b>all</b> available fares',

    'booking.fare-category.select-one': 'Select an outward fare',

    'booking.fare-category.go': 'Go',
    'booking.fare-category.cta': 'View <span>{type}</span> tickets',

    'booking.fare-category.cheapest': 'Cheapest',
    'booking.fare-category.return': '(Rtn)',
    'booking.fare-category.title.Advance': 'Advance',
    'booking.fare-category.title.Advance.return': 'Singles',
    'booking.fare-category.title.OffPeak': 'Off Peak',
    'booking.fare-category.title.OffPeak.return': 'Off Peak',
    'booking.fare-category.title.Anytime': 'Anytime',
    'booking.fare-category.title.Anytime.return': 'Anytime',

    'booking.fare-category.sub-title.Advance': 'Non-Flexible',
    'booking.fare-category.sub-title.Advance.return': 'For most ticket types, including Advance',
    'booking.fare-category.sub-title.OffPeak':
        'Semi-flexible tickets, often with time and/or date restrictions',
    'booking.fare-category.sub-title.OffPeak.return':
        'Semi-flexible tickets, often with time and/or date restrictions (Return)',
    'booking.fare-category.sub-title.Anytime': 'Fully flexible, for travel at any time of day',
    'booking.fare-category.sub-title.Anytime.return':
        'Fully flexible, for travel at any time of day (Return)',

    'booking.fare-category.description.Advance':
        '<p><span class="main-title">Advance</span> - book in advance for travel on a specific service.</p><p>Advance tickets are single fares offering great value for money on many journeys. In most cases you must book in advance up to the day before travel and these tickets are subject to availability.</p> <p>Generally, the earlier you book the better the deal. Tickets are normally available up to 12 weeks before travel – earlier on some routes. You will need to choose a specific train at the time of booking and must travel on that service. No break of journey is allowed except to change between trains.</p> <p>Advance fares are valid only on the date and train shown on the ticket. Advance tickets are non-refundable but may be amended prior to the date and time of travel.</p>',
    'booking.fare-category.description.OffPeak':
        '<p><span class="main-title">Off-Peak Single tickets</span> - semi-flexible tickets with time and/or date restrictions.</p><p>Off-Peak tickets are for use on trains that are less busy and are cheaper than Anytime tickets. You will have to travel at certain times of the day and/or days of the week, and sometimes on specific routes or operators. Some train companies sell even cheaper Super Off-Peak tickets which are valid on the least busy services.</p> <p>In most cases you may break your journey at any point, or more than once if you wish. Where this is not the case it will be stated in the restrictions shown for that ticket, available at the time of purchase.</p> <ul class="list-fancy"><li>Off-Peak Day Single tickets are valid for one single journey on the date shown on the ticket</li> <li><p>Off-Peak Single tickets are valid for one single journey on the date shown on the ticket. If you cannot complete your journey on the same day you will be allowed to finish it the following day.</p> <p>If you board a train at a time when your Off-Peak ticket is not valid, you will be charged the difference between the fare you’ve paid and the cheapest valid fare for the service concerned.</p></li></ul>',
    'booking.fare-category.description.Anytime':
        '<p><span class="main-title">Anytime Single tickets</span> - fully flexible, for travel at any time of day.</p> <ul class="list-fancy"><li>Anytime Day Single tickets are valid for one single journey on the date shown on the ticket.</li> <li>Anytime Single tickets are valid for one single journey, and are valid for two days beginning from the date shown on the ticket. You may need to travel by a specific route or train company but the ticket will state this. You are allowed to break your journey at any point, or more than once if you wish. This means that you do not have to make the whole of your journey at the same time or, where allowed, on the same day.</li></ul>',
    'booking.fare-category.description.Advance.return':
        '<{booking.fare-category.description.Advance}> <{booking.fare-category.description.OffPeak}> <{booking.fare-category.description.Anytime}> ',
    'booking.fare-category.description.OffPeak.return':
        '<p><span class="main-title">Off-Peak Return tickets</span> - semi-flexible tickets with time and/or date restrictions.</p><p>Off-Peak tickets are for use on trains that are less busy and are cheaper than Anytime tickets. You will have to travel at certain times of the day and/or days of the week, and sometimes on specific routes or operators. Some train companies sell even cheaper Super Off-Peak tickets which are valid on the least busy services.</p> <p>In most cases you may break your journey at any point, or more than once if you wish. Where this is not the case it will be stated in the restrictions shown for that ticket, available at the time of purchase.</p><ul class="list-fancy"><li>Off-Peak Day Return tickets are valid for one return journey on the date shown on the ticket</li><li>Off-Peak Return tickets are valid for one return journey. The outward part is valid for travel on the date shown on the ticket, if the return part has not been used. If you cannot complete your journey on the same day, you will be allowed to finish it the following day. The return part is valid for one calendar month from the start date shown on the ticket.</li></ul> <p>If you board a train at a time when your Off-Peak ticket is not valid, you will be charged the difference between the fare you’ve paid and the cheapest valid fare for the service concerned.</p>',
    'booking.fare-category.description.Anytime.return':
        '<p><span class="main-title">Anytime Return tickets</span> - fully flexible, for travel at any time of day.</p><ul class="list-fancy"><li>Anytime Day Return tickets are valid for one return journey on the date shown on the ticket.</li><li>Anytime Return tickets are valid for one return journey. The outward part is valid for five days unless otherwise shown and the return part is valid for one calendar month from the start date shown on the ticket.</li></ul><p>You may need to travel by a specific route or train company but the ticket will state this. You are allowed to break your journey at any point, or more than once if you wish. This means that you do not have to make the whole of your journey at the same time or, where allowed, on the same day.</p>',

    'booking.fare-category.restriction.Advance':
        'Advance fares are valid only on the date and train shown on the ticket. Advance tickets are non-refundable but may be amended prior to the date and time of travel.',

    'booking.fare-category.standard': 'Standard',
    'booking.fare-category.first-class': 'First Class',

    'booking.journey-modal.title': 'Update your search',
    'booking.basket-modal.title': 'Selected tickets',

    'booking.fare-row.more-info': 'More info',

    'booking.toc-specific-search.button': 'Show cheapest {toc} fares',
    'booking.usp.noBookingFees.banner': 'No booking fees!',
    'booking.usp.noBookingFees.inline': 'No booking fee',

    'booking.search-type-caveat.cheapest-start':
        'Above tickets are for services on the <b>cheapest available trains.</b> Click',
    'booking.search-type-caveat.cheapest-end': 'to check if faster services are available.',
    'booking.search-type-caveat.fastest-start':
        'The fares above are based on the <b>fastest available trains.</b> Click',
    'booking.search-type-caveat.fastest-end':
        'to check if slower routes with cheaper fares are available.',

    'booking.service-carousel.no-results': 'No results for this search, ',
    'booking.service-carousel.no-results-link': 'try changing your search parameters',

    'booking.service-carousel.no-results-v2': 'No results found',
    'booking.service-carousel.no-results-link-v2':
        'No results for this search, try changing your search parameters',

    'booking.service-carousel.title.outward': 'Outward',
    'booking.service-carousel.title.inward': 'Return',
    'booking.service-carousel.item.cancelled': 'Cancelled',
    'booking.service-carousel.item.delayed': 'Delayed',
    'booking.service-carousel.item.on-time': '<{journey.duration.on-time}>',
    'booking.service-carousel.item.late': '<{journey.duration.late}>',
    'booking.service-carousel.item.tocDetails.single': '{toc} Service',
    'booking.service-carousel.item.tocDetails.multiple': 'Part {toc}',
    'booking.service-carousel.overtaken': 'Overtaken',

    'booking.service.departure-label': 'Leaves at:',
    'booking.service.arrival-label': 'Arrives at:',
    'booking.service.duration-label': 'Duration:',
    'booking.service.changes-label': 'Changes:',
    'booking.service.select-service': 'Select service, ',
    'booking.service.selected-service': 'Selected service, ',

    'booking.basket.addReturn': 'Add return from {price}',
    'booking.basket.collapse': 'Hide selected',
    'booking.basket.expand': 'Show selected',
    'booking.basket.sub-total': 'Total:',
    'booking.basket.basket-total': 'Basket total:',
    'booking.basket.continue': 'Continue',
    'booking.basket.add-to-basket': 'Add to basket',
    'booking.basket.or': 'or',
    'booking.basket.get-refund': 'Proceed to refund',
    'booking.basket.back': 'Back',
    'booking.basket.cheapest-selected': 'We have selected the cheapest shown tickets for you',
    'booking.basket.item.more-info': 'More info',
    'booking.basket.item.title-to': 'to',
    'booking.basket.item.single-outward': 'Out',
    'booking.basket.item.single-inward': 'Rtn',
    'booking.basket.item.return': 'Out',
    'booking.basket.item.open-return': '<b>Open Return:</b>within 1 month',
    'booking.basket.travelcard-fares': 'Travelcard fare',
    'booking.basket.travelcard.cta': 'Click to see available fares',
    'booking.basket.price-label': 'Total',
    'booking.basket.continue-button': 'Continue',
    'booking.basket.help.select-day-save-fare': 'To continue, you need to select a DaySave ticket',
    'booking.basket.help.select-rovers-fare':
        'To continue, you need to select a Rover ticket',
    'booking.basket.help.passenger-details':
        'To continue, you need to fill in all required passenger details',
    'booking.basket.help.select-season-fare': 'To continue, you need to select a season ticket',
    'booking.basket.help.select-outward-fare': 'To continue, you need to select an outward ticket',
    'booking.basket.help.select-both-fares':
        'To continue, you need to select an outward and a return ticket',
    'booking.basket.help.select-inward-fare': 'To continue, you need to select a return ticket',
    'booking.basket.help.open-golf':
        'To continue, you need to add at least one ticket to the basket',
    'booking.basket.help.select-non-overlapping-services':
        'The return train you have chosen will already have departed when your outward train arrives at its destination.',
    'booking.basket.seating-reservation.trigger': 'Reserve seats',
    'booking.basket.seating-reservation.trigger.edit': 'Change seats',
    'booking.basket.seating-reservation-v2.trigger': 'Reserve my seats',
    'booking.basket.seating-reservation-v2.trigger.edit': 'Change selected seats',
    'booking.basket.plusbus.trigger': 'Buy PlusBus',
    'booking.basket.plusbus.trigger.edit': 'Change PlusBus',
    'booking.basket.plusbus-v2.trigger': 'Add PlusBus from',
    'booking.basket.plusbus-v2.trigger.edit': 'Modify PlusBus',
    'booking.basket.photo-card-trigger': 'Photocard details',
    'booking.basket.passengers-trigger': 'Passenger details',
    'booking.basket.oyster-trigger': 'With Contactless',
    'booking.basket.oyster-weekly-cap.trigger': 'Contactless Weekly Capping',
    'booking.basket.travelcard-trigger': 'Get a Travelcard from ',

    'booking.header.edit-journey': 'Change journey details',
    'booking.header.edit-journey-v2': 'Amend search',
    'booking.header.tabs.sr-optimised': '. This view is optimised for screen readers',
    'booking.header.tabs.list': 'List',
    'booking.header.tabs.grid': 'Grid',
    'booking.tutorial.railcard':
        '<strong>Great!</strong> 1/3 off prices have been applied to your tickets below',

    'booking.filters.go-cheaper': 'Go Cheaper',
    'booking.filters.go-faster': 'Go Faster',
    'booking.filters.go-1st-class': 'Go 1st Class',

    'booking.filters.go-cheaper.sr-only': 'select the cheapest fare',
    'booking.filters.go-faster.sr-only': 'select the fastest fare',
    'booking.filters.go-1st-class.sr-only': 'select the cheapest first class fare',

    'booking.layout.grid': 'Use grid view',
    'booking.layout.list': 'Use list view',

    'booking.payment.button.back': 'Go back',
    'booking.payment.button.addjourney': 'Add another journey',
    'booking.payment.header': 'Payment details',
    'booking.payment.choosePaymentMethod': 'Choose your payment method',
    'booking.payment.paymentOption.google-pay': 'Google Pay',
    'booking.payment.paymentOption.apple-pay': 'Apple Pay',
    'booking.payment.choosePaymentMethodDeliveryAddress': 'Choose your payment method and delivery address',
    'booking.payment.help.select-payment-method': "To continue, you need to select a payment method",
    'booking.payment.reward.apply.aria': 'Apply reward code',
    'booking.payment.reward.remove.aria': 'Remove reward code',
    'booking.payment.reward.title': 'Reward code',
    'booking.payment.reward.remove': 'Remove code',
    'booking.payment.reward.login': 'If you have a Reward code, please {loginButton} to apply it to your order',
    'booking.payment.reward.loggedIn': 'If you have a Reward code, please input it here. The reward will be available in your booking confirmation email',
    'booking.payment.reward.confirmation.title': 'You’ve earned a reward for this order',
    'booking.payment.reward.confirmation.description': 'Details of how to claim your reward can be found in your booking confirmation email',
    'booking.service-list.outward.returnPrice': 'Return {isSingle, select, true {from } other {}}',
    'booking.service-list.inward.returnPrice': 'Total ',
    'booking.service-list.services.title': 'Choose <b>{direction, select, outward {Outbound} inward {Return} other {}}</b>',
    'booking.service-list.service.moreFares': 'Other fares available',
    'booking.service-list.service.moreFares.sr': 'press to find other fares for this journey',
    'booking.service-list.service.moreInfo': 'Journey info',
    'booking.service-list.service.reservations': 'Journey info & reserve seating',
    'booking.service-list.service.reservations.sr': 'press to find out more information about this journey',
    'booking.service-list.service.cancelled': 'Train cancelled',
    'booking.service-list.service.warningsMore': '{count} more',
    'booking.service-list.service.warningsMore.label': '{count} more service-related message{count, plural, =1{} other{s}}',
    'booking.service-list.service.fare.label': 'Featured fare',
    'booking.service-list.service.selectFare.label': '{price, select, false{} other{{price}.}} Press to select the cheapest fare for this service',
    'booking.service-list.service.selectFixedFare.label': 'Press to select this return service',
    'booking.service-list.service.fastestJourney.label': 'This is the fastest journey time displayed.',
    'booking.service-list.service-modal.button.viewServices': 'View other services',
    'booking.service-list.service-modal.button.selectReturn': 'Select this return service',
    'booking.service-list.service-modal.button.viewFares': 'Other fares for this service',
    'booking.service-list.service-modal.button.selectFare': 'Add to basket',
    'booking.service-list.fares.title': 'Choose your ticket',
    'booking.service-list.fares.changeService': 'Change service',
    'booking.service-list.fares.toggleFirstClass': '<b>1st Class</b> (from £{cheapestPrice})',
    'booking.service-list.fare.title': '{name} ({code})',
    'booking.service-list.fare.moreInfo': 'More info',
    'booking.service-list.fare.moreInfo.label': 'More info for this fare',
    'booking.service-list.fare.selectFare.label': '{price, select, false{} other{{price}.}} Press to select {name}',
    'booking.service-list.fare.class': '{class, select, standard {Standard} other {First Class}}',
    'booking.service-list.fare.class.label': '{class, select, standard {Standard class} other {First Class}}',
    'booking.service-list.fares.classCheapest': '(from £{cheapestPrice})',
    'booking.service-list.views.label': 'Choose journey planner view',
    'booking.service-list.views.title': 'View',
    'booking.service-list.views.list': 'List',
    'booking.service-list.views.list.label': 'List view',
    'booking.service-list.views.grid': 'Grid',
    'booking.service-list.views.grid.label': 'Grid view',
    'booking.service-list.loading.label': 'Loading trains',
    'booking.service-list.results.label': '{count, plural, =1{1 journey} other{# journeys}} found',
    'booking.service-list.noResults.label': 'No journeys found',
    'service-info.modal.title': 'Journey info',
    'service-info.modal.heading': '{direction, select, outward {Outbound} other {Return}} journey',
    'booking.smartCarnet.offPeak': 'Off Peak',
    'booking.smartCarnet.peak': 'Peak',

    'booking.fareDetails.refunds.advanceTicket': "Advance fares are valid only on the date and time shown on the ticket. Advance tickets are non-refundable but may be amended prior to the date and time of travel, subject to a £10 administration fee.<br><br>If your journey is cancelled or disrupted, arrangements will be made to accommodate you on another train. If the train you purchased a ticket for is cancelled or delayed, and as a result you decide not to travel, a full refund will be available, and you will not be charged an administration fee.",
    'booking.fareDetails.refunds.dayTicket': "This ticket is refundable. If you decide not to use your ticket to make all or part of your intended journey, you can get a refund within 28 days of the journey date.<br><br>You may be required to pay an administration fee, up to a maximum of £5 per order. The refund amount will normally consider any use you have made of the ticket and in some circumstances no refund will be paid.",
    'booking.fareDetails.refunds.daySaveOffPeak': 'This ticket cannot be changed or refunded.',
    'booking.fareDetails.refunds.seasonTicket': "This ticket is refundable. If you decide not to use your ticket to make all or part of your intended journeys, you can get a refund within 28 days of the ticket expiry date.<br><br>You will be required to pay an administration fee, up to a maximum of £5 per order. Any refund is calculated from the date the Season ticket is returned and will be the difference between the price you paid and the cost of a ticket or tickets for the period for which you have actually used the ticket. In some circumstances no refund will be due.<br><br>Please make sure you bring your Railcard(s) with you.",
    'booking.fareDetails.refunds.flexiTicket': "This ticket is refundable. Most Flexi Season tickets will need at least two passes remaining for a refund to be possible. But there are some routes that may need more or fewer passes remaining.<br><br>You must apply for a refund before the end of the Flexi Season ticket's 28-day validity period.<br><br> You will be required to pay an administration fee, up to a maximum of £5 per order. Any refund is calculated from the date the Season ticket is returned and will be the difference between the price you paid and the cost of a ticket or tickets for the period for which you have actually used the ticket. In some circumstances no refund will be due.<br><br>Please make sure you bring your Railcard(s) with you.",

    'customerdetails.title': 'Title',
    'customerdetails.initials': 'Initials',
    'customerdetails.initial': 'Initial',
    'customerdetails.name': 'First Name',
    'customerdetails.surname': 'Surname',
    'customerdetails.contactname': 'Contact Name',
    'customerdetails.addressline1': 'Address line 1',
    'customerdetails.addressline2': 'Address line 2',
    'customerdetails.addressline3': 'Address line 3',
    'customerdetails.towncity': 'Town/City',
    'customerdetails.postcode': 'Postcode',
    'customerdetails.country': 'Country',
    'customerdetails.country.select': 'Choose a country...',
    'customerdetails.email': 'Email Address',
    'customerdetails.confirmemail': 'Confirm email address',
    'customerdetails.telephone': 'Contact phone number',
    'customerdetails.countrycode': 'Country Code',
    'customerdetails.mobilephone': 'Mobile phone number',
    'customerdetails.password': 'Password',
    'customerdetails.password.show': 'Show password',
    'customerdetails.password.hide': 'Hide password',
    'customerdetails.password.shown': 'Password visible',
    'customerdetails.password.hidden': 'Password hidden',
    'customerdetails.password.restrictions': 'Must contain min. 8 characters, 1 lowercase, 1 uppercase and 1 digit',
    'customerdetails.photocardFirstname': 'First Name on photocard',
    'customerdetails.photocardLastname': 'Last Name on photocard',
    'customerdetails.photocardNumber': 'Photocard number',
    'customerdetails.photocardAddress': 'Photocard Address:',
    'customerdetails.addressName': 'Address name',

    'customerdetails.dob.day': 'Day',
    'customerdetails.dob.dd': 'DD',
    'customerdetails.dob.mm': 'MM',
    'customerdetails.dob.month': 'Month',
    'customerdetails.dob.year': 'Year',
    'customerdetails.dob.yyyy': 'YYYY',

    'customerdetails.payment.existingcard': 'Use existing card',
    'customerdetails.payment.savecard': 'Save card to speed up future purchases',
    'customerdetails.payment.cardSummary': '{nickname} (**{lastDigits})',
    'customerdetails.payment.cardName': 'Card name',
    'customerdetails.payment.cardholder': 'Cardholder name',
    'customerdetails.payment.cardnumber': 'Card number',
    'customerdetails.payment.cardnumber.placeholder': '0000 0000 0000 0000',
    'customerdetails.payment.expirymonth': 'Expiry month',
    'customerdetails.payment.expirymonth.defaultOption': 'MM',
    'customerdetails.payment.expiryyear': 'Expiry year',
    'customerdetails.payment.expiryyear.defaultOption': 'YYYY',
    'customerdetails.payment.cvv': 'CVV',
    'customerdetails.paymentRegistration.a11y': 'Optional, add a password to create an account',
    'customerdetails.paymentRegistration.title': 'Add a password to create an account',
    'customerdetails.paymentRegistration.subtitle': 'Reasons to create an account:',
    'customerdetails.paymentRegistration.benefits.1': 'Faster checkouts',
    'customerdetails.paymentRegistration.benefits.2': 'Refunds',
    'customerdetails.paymentRegistration.benefits.3': 'Purchase history',

    'passwordHint.length': '{minLength} characters',
    'passwordHint.lowercase': '1 lowercase',
    'passwordHint.uppercase': '1 uppercase',
    'passwordHint.digit': '1 digit',
    'passwordHint.itemOk': 'ok',
    'passwordHint.itemNotOk': 'not ok',
    'paymentCard.none': 'You don’t currently have any card details saved. The next time you buy a ticket you can save your card details by ticking the ‘Save card’ box on the payment screen.',
    'paymentCard.delete': 'Delete card',
    'paymentCard.confirmDelete': 'Please confirm that you wish delete the following saved card:',
    'paymentCard.deleteKeyGoCard':
        'This card is associated with a {keygobrand} product on your <{global.key}> <{global.smartcard}>. If you change your method of payment for {keygobrand}, then you will be able to remove this card.',
    'paymentCard.expires': 'Card expiry date',
    'paymentCard.expiryDate': 'Expires: {expiryDate}',

    'orBar.or': 'or',

    'account.login': 'Log in',
    'account.logout': 'Log out',
    'account.login.registerLabel': 'Don\'t have an account?',

    'login.username': 'Email Address',
    'login.password': 'Password',
    'login.link.password': '{accountType} password',
    'login.subtitle': 'Log in to your account',
    'login.modal.body.ariaLabel': 'Login Modal, please enter your email address and password.',

    'login.register.or': 'or',

    'login.forgotPassword.link': 'Forgotten your password?',
    'login.forgotPassword': 'Forgotten password',
    'login.buyTickets': 'Buy tickets',
    'login.resetPassword.help': 'Please choose a new password',
    'login.forgotPassword.help': `Please enter your email address and we'll send you instructions for resetting your password.`,
    'login.forgotPassword.submitted':
        'You have been sent instructions on how to reset your password.',
    'login.resetPassword.header': 'Reset password',
    'login.resetPassword.success': 'Your password reset was successful and you are now logged in.',
    'login.resetPassword.error':
        'Your password reset request may have expired so please try again.',
    'login.register.link': 'Not registered?',
    'login.setPasswordAndPreferences.heading': 'Set Password and Preferences',
    'login.setPasswordAndPreferences.form.action': 'Save',
    'login.setPasswordAndPreferences.modal.title': 'Your password and marketing preferences',
    'login.setPasswordAndPreferences.modal.success.message':
        'Your password and preferences have been saved successfully. You are now logged in.',
    'login.setPasswordAndPreferences.modal.error.message':
        "There's been a problem saving your password and marketing preferences. Please try again and if you still see this message - please contact us.",

    'login.link.amazon.message':
        'We have found an existing {accountType} account<br/><b>{email}</b><br/>that matches your Amazon email.',
    'login.link.amazon.loginAccount':
        'Please login below to link your {accountType} account with your Amazon account.',
    'login.link.amazon.close': 'Cancel',
    'login.link.amazon.confirm': 'OK',
    'login.link.amazon.title': 'Link your account with Amazon',
    'login.link.amazon.continueText': 'Link accounts',
    'login.link.amazon.closeText': 'Cancel',
    'login.link.amazon.error': 'Wrong {accountType} password. Please try again.',
    'login.link.amazon.success.heading': 'Linking of accounts successful',
    'login.link.amazon.success.message':
        'From now on you can also login using just the Amazon Login button.',
    'login.link.amazon.success.title':
        'You have successfully linked your Amazon and {accountType} accounts',
    'login.amazon.error': 'Login with Amazon failed. Please try again later.',

    'login.registerFirst.message': `You don't have a <b>{accountType}</b> account yet.<br><br> Please register now in order to use Amazon Login to <span class="text-nowrap">sign you in</span>.`,
    'login.registerFirst.title': 'Register',
    'login.registerFirst.closeText': 'Cancel',
    'login.registerFirst.continueText': 'Register',

    'login.syncEmail.amazon.message': `Your Amazon email <b>{amazonEmail}</b> is different than your {accountType}'s account email <b>{accountEmail}</b>.<br/><br/>Would you like to update your account email to match the Amazon one?`,
    'login.syncEmail.amazon.title': 'Update email',
    'login.syncEmail.amazon.closeText': 'Maybe later',
    'login.syncEmail.amazon.continueText': 'Update email',

    'amazonLogin.name': 'Amazon Login',

    'login.promptPasswordChange.title': 'Welcome back to <{app.owner}>, {firstNames}',
    'login.promptPasswordChange.information': `We're updating our password requirements to help enhance the security of our website and your data.`,
    'login.promptPasswordChange.supplementary':
        'To help us do this we would like you to choose a brand new password, it should have a minimum of 8 characters, including 1 uppercase letter and 1 number.',
    'login.promptPasswordChange.defer': 'You may choose to do this another time if you prefer.',
    'login.promptPasswordChange.currentPassword': 'Current password',
    'login.promptPasswordChange.newPassword': 'New password',
    'login.promptPasswordChange.error':
        'There was a problem changing your password. Please check you have entered your current password correctly, and that your new password fulfils all the criteria.',
    'login.promptPasswordChange.validationError':
        'Please check you have entered your current password correctly, and that your new password fulfils all the criteria.',
    'login.promptPasswordChange.success': 'Password successfully updated',
    'login.promptPasswordChange.button.save': 'Save changes',
    'login.promptPasswordChange.button.back': 'Change my password',
    'login.promptPasswordChange.cancel.title': 'Review complete',
    'login.promptPasswordChange.cancel.confirm': `Thank you for reviewing your security and deciding that you’re happy with your current password.
                                                You can change your password anytime by going to your Account Details.`,

    'login.accountLocked.requestedReset': 'Password reset requested',
    'login.accountLocked.button.reactivate': 'Reactivate',
    'login.accountLocked.titleDefault': 'Action required',
    'login.accountLocked.messageDefault':
        'Your account has been locked as you have entered an incorrect password a number of times in succession. In order to regain access to your account, please press the reactivate button below and follow the instructions emailed to you.',
    'login.accountLocked.titleDormant': `Welcome back`,
    'login.accountLocked.messageDormant': `To help keep your details safe we have temporarily locked your account. Just click Reactivate below and we'll send you an email to unlock your account.`,
    'login.accountLocked.messageLocked': 'Your account has been locked. Please get in touch with customer services to unlock your account.',
    'login.accountLocked.error': 'There was a problem reactivating your account at this time',

    'photoupload.format-requirements-label': 'Must be in one of the following formats: ',
    'photoupload.buttontext': 'Upload photo',
    'photoupload.error.imageOrientation':
        'Image has to be a portrait. Its height must be considerable greater than its width.',
    'photoupload.error.imageOrientationWithDifference': `Image has to be a portrait. It's {difference} pixel(s) too wide.`,
    'photoupload.error.fileSize': 'The selected file is too large, please choose another',
    'photoupload.error.unacceptedFileFormat': 'Cannot upload the file. The format is not supported',
    'photoupload.error.required': 'Please upload a valid passport style photo',

    'account.register': 'Register',
    'account.register.smartcard':
        'Log in or Register to use your <{global.key}> <{global.smartcard}>',
    'account.register.success': 'Registration successful',
    'account.register.smartcardOnlySuccess': '<{global.smartcardUppercase}> application received',
    'account.register.successMessage': '<dt>Hello, {name}!</dt><dd>Welcome to {brand}.</dd>',
    'account.register.success.button': 'Continue',
    'account.register.partialSuccess':
        'There was a problem applying for a <{global.key}> <{global.smartcard}>',
    'account.register.partialSuccessMessage':
        'Your account has been registered, and you are now logged in. Please try to apply for a <{global.smartcard}> in the Account section, or contact customer services if the problem persists.',
    'account.register.partialSuccess.button': 'Go',
    'account.register.address': 'Address (Please use your home address)',
    'account.register.opt.description':
        "We would like to keep you updated about changes to your services, ticket expiries and renewals, and special offers. Please tick the boxes below to let us know which communications you'd like to receive.",
    'account.register.confirm.terms': 'I have read and accept the {terms}',
    'account.register.confirm.privacypolicy': 'I have read and accept the {privacypolicy}',
    'account.register.smartcardActivation':
        'You can purchase a smart Season Ticket within the next {preloadTimeLimit}. It will come pre-loaded onto your <{global.key}> card when it is delivered to your home address.',
    'account.register.smartcardPreload':
        'You can purchase a smart Season Ticket within the next {preloadTimeLimit}. It will come pre-loaded onto your <{global.key}> card when it is delivered to your home address. Your ticket must start after {awaitSmartcardProductDays} to ensure that you receive it on time.',
    'account.register.smartcardPreload.new': 'new',
    'account.register.smartcardPreload.existing': 'existing',
    'account.register.selfSmartcardError':
        'There was a problem applying for a <{global.key}> <{global.smartcard}>',
    'account.register.selfSmartcardErrorMessagePartial':
        'Your account has been registered but the <{global.smartcard}> number you have entered is incorrect or is already in use. Please try again with a different <{global.smartcard}> number or contact customer support for further assistance.',
    'account.register.selfSmartcardErrorMessage':
        'The <{global.smartcard}> number you have entered is incorrect or is already assigned to another account. Please try again with a different <{global.smartcard}> number or contact customer support for further assistance.',
    'account.register.keyGoPreload':
        'You can purchase a smart Season Ticket or add <{global.keyGo}> within the next {preloadTimeLimit}. It will come pre-loaded onto your <{global.key}> <{global.smartcard}> and ready to use when it is delivered to your home address. Your ticket must start after {awaitSmartcardProductDays} to ensure that you receive it on time.',
    'account.register.findDayTicketButtonText': 'Search for Day Tickets',
    'account.register.findSeasonTicketButtonText': 'Search for Season Tickets',
    'account.register.ocdrButtonText': 'Sign up to One Click Delay Repay',
    'auto-dr.account.signup': 'Sign up',
    'account.register.transferTicketButtonText': 'Transfer a ticket',
    'account.register.keygoButtonText': 'Register for <{global.keyGo}>',
    'account.register.loadingStation.fieldLabel': 'Loading station',
    'account.register.loadingStation.fieldPlaceholder': 'Select loading station',
    'account.register.loadingStation.fieldDescription':
        'Please allow a minimum of two hours after purchase before loading your ticket onto your <{global.key}> <{global.smartcard}>.',
    'account.register.whatsNext': "What's next?",
    'account.register.smartcardApplicationReceived':
        'You can view the progress of your application in your ',
    'account.register.swapSeasonTicket':
        'Swap your current paper season ticket for a Key <{global.smartcard}> Season',
    'account.register.viewAccount': 'account',
    'account.register.autoDelayRepay': 'Auto Delay Repay',
    'account.register.oneClickDelayRepay': 'One Click Delay Repay',
    'account.register.autoDelayRepay.description': `When you travel with us using your new <{global.key}> <{global.smartcard}>, in many cases we can determine when you may have been delayed and create a Delay Repay claim for you. All you have to do is log in and verify the delay and choose how you would like to be reimbursed.
        Whenever we determine you have been delayed, we will also send you an email alert.`,
    'account.register.autoDelayRepay.label':
        'I wish to receive One Click Delay Repay email notifications',
    'account.register.withoutSmartcard':
        'Continue without registering for a <{global.theKey}> <{global.smartcard}>',
    'account.register.withoutSmartcardAndKeygo':
        'Continue without registering for a <{global.theKey}> <{global.smartcard}> and <{global.keyGo}>',
    'account.register.withPaperTransfers.details':
        'Transfer a current paper season to a <{global.theKey}> <{global.smartcard}>',
    'account.register.loginLabel': 'Already have an account?',
    'account.register.loginButton': 'Log in',
    'account.register.emailInUse': 'Email address already in use',

    'account.register.alreadyAmazon.heading': 'Register',
    'account.register.alreadyAmazon.title': 'You already have registered with that Amazon account',
    'account.register.alreadyAmazon.message': 'We have logged you in automatically.',
    'account.register.steps.count': 'Step {stepNumber} of {totalSteps}',
    'account.register.steps.details': 'Your details',
    'account.register.steps.smartcard': 'Key smartcard',
    'account.register.steps.preferences': 'Preferences',
    'account.register.steps.back.details': 'Back to your details',
    'account.register.steps.back.smartcard': 'Back to the <{global.key}> <{global.smartcard}>',
    'account.register.password': 'Password',
    'account.register.next': 'Continue',
    'account.register.submit': 'Submit',
    'account.register.subtitle': 'Create an account',
    'account.register.cta.side': 'No {brand} account needed',
    'account.register.cta.main': 'Log in with Amazon',
    'account.register.closeConfirm':
        'Are you sure you want to close the form? You will lose all data entered',
    'account.register.footer.close': 'No, go back',
    'account.register.footer.continue': 'Yes, close the form',
    'account.register.continueWithout': 'Continue without',
    'account.register.create': 'Create an account',
    'account.register.continue': 'Continue',
    'account.register.smartcard.labelLong': 'Get the <{global.key}> <{global.smartcard}>',
    'account.register.smartcard.labelShort': 'Continue',
    'account.register.smartcard.forbidden-child-smartcard-registering': 'Children under 16 are not eligible to register for a <{global.smartcard}>.',

    'account.register.details.title': 'Create an account',
    'account.register.details.body': 'Create an account and benefit from:',
    'account.register.details.alt': 'User creating a {brand} account on a laptop',
    'account.register.create.benefits.checkout': 'Faster checkout',
    'account.register.create.benefits.refunds': 'Easy refunds and ticket changes',
    'account.register.create.benefits.history': 'View your purchase history',
    'account.register.create.benefits.updates': 'Service updates and promotions',

    'account.register.smartcard.title': 'Get the<br /> <{global.key}> <{global.smartcard}><br />with your account',
    'account.register.smartcard.title-mobile': 'Get the <{global.key}> <{global.smartcard}><br />with your account',
    'account.register.smartcard.logo.alt': '<{global.smartcard}> logo',
    'account.register.smartcard.body': 'Our <{global.key}> <{global.smartcard}> is a smarter alternative to paper tickets, making it quicker and easier to buy a ticket.',
    'account.register.smartcard.alt': 'User tapping <{global.key}> <{global.smartcard}> on a card reader',
    'account.register.smartcard.benefits.tickets': 'Load season tickets',
    'account.register.smartcard.benefits.app': 'Buy and download your tickets at home with our app',
    'account.register.smartcard.benefits.queues': 'Fast-track through the station and avoid the queues',
    'account.register.smartcard.benefits.contactless': 'Contactless travel, just tap and go',
    'account.register.smartcard.benefits.refunds': 'Quicker refunds',

    'account.register.preferences.title': 'One last thing...',
    'account.register.preferences.body': 'We would like to keep you updated about changes to your services and special offers.',
    'account.register.preferences.alt': 'User receiving notification from {brand} on a mobile device',

    'account.register.amazon.logout.info': 'Your Amazon account is <em>{ email }</em>.',
    'account.register.amazon.logout.action': 'Log out',

    'account.nav.collapse.btn.label': 'Expand the Account Menu Section {title}',
    'account.nav.accountdetails': 'Account details',
    'account.nav.paymentdetails': 'Payment details',
    'account.nav.addresses': 'Addresses',
    'account.nav.preferences': 'My preferences',
    'account.nav.photocards': 'Photocards',
    'account.nav.railcards': 'Railcards',
    'account.nav.photocards-railcards': 'Photocards / Railcards',
    'account.nav.stickets': 'sTickets',
    'account.nav.purchases': 'Purchases',
    'account.nav.upcomingjourneys': 'Upcoming journeys',
    'account.nav.seasontickets': 'Season tickets',
    'account.nav.orders-refunds': 'Manage my tickets / refunds',
    'account.nav.smart-carnet': '<{global.keyGo}> Carnet',
    'account.nav.linkorder': 'Link Order',
    'account.nav.smartcard': '<{global.theKey}> <{global.smartcard}>',
    'account.nav.smartcard.replacement': '<{global.theKey}> replacement',
    'account.nav.smartcard.keygoaccount': '<{global.keyGo}> account',
    'account.nav.smartcard.keygohistory': '<{global.key}> <{global.smartcard}> history',
    'account.nav.smartcard.keygosignup': 'Sign up for <{global.keyGo}>',
    'account.nav.smartcard.keygo': '<{global.keyGo}>',
    'account.nav.smartcard.keygo.payment': '<{global.keyGo}> payment',
    'account.nav.smartcard.link': 'The <{global.key}> - Linked Accounts',
    'account.nav.smartcard.tickets': '<{global.theKey}> tickets',
    'account.nav.smartcard.key-tickets': 'Key Tickets',
    'account.nav.smartcard.journeyhistory': '<{global.theKey}> journey history',
    'account.nav.evouchers': 'eVouchers',
    'account.nav.delayrepay': 'Delay Repay',
    'account.nav.auto-delay-repay': 'Auto Delay Repay',
    'account.menu.showNavigation': 'Show navigation',
    'account.nav.etickets': 'eTickets',
    'account.nav.disruptionAlerts': 'Disruption alerts',
    'account.nav.myTrees': 'My Trees',
    'account.nav.flexi': 'Flexi Season Tickets',
    'account.nav.passengerAssist': 'Passenger Assist',
    'account.nav.bookPassengerAssist': 'Book Passenger Assist',
    'account.nav.bookedAssistance': 'View Booked Assistance',
    'account.nav.accessibilityProfile': 'View My Accessibility Profile',
    'account.nav.reservation': 'Make a seat / bike reservation',

    'account.delete.heading': 'Delete this item',
    'account.delete.text': 'Please confirm that you wish delete the following item',

    'account.edit.heading': 'Edit this item',

    'account.drc.title': 'Digital Railcards',
    'account.drc.description': 'Tap the heart icon to choose your favourite Digital Railcard and we will automatically apply the railcard to all your ticket searches.',
    'account.drc.setFavourite': 'Save as favourite',
    'account.drc.removeFavourite': 'Your favourite railcard',
    'account.drc.blockDevice': 'Remove from device',
    'account.drc.firstDevice': 'Device ID 1',
    'account.drc.secondDevice': 'Device ID 2',
    'account.drc.primaryHolder': 'Primary holder',
    'account.drc.secondaryHolder': 'Secondary holder',
    'account.drc.empty': 'You don’t currently have any digital railcard',
    'account.drc.renew': 'Renew railcard',

    'account.details.name': 'Name',
    'account.details.email': 'Email',
    'account.details.confirm.email': 'Confirm Email',
    'account.details.confirm.changes': 'Confirm changes',
    'account.details.password': 'Password',
    'account.details.password.new': 'New password',
    'account.details.telephoneNumber': 'Contact phone number',
    'account.details.dateOfBirth': 'Date of Birth',
    'account.details.userSince': 'User since',
    'account.details.deleteUser': 'Request to delete my account',
    'account.details.deleteUser.sr': 'Click to request your account deletion',
    'account.details.emailMarketing': 'Email preferences:',
    'account.details.smsMarketing': 'SMS preferences:',
    'account.details.smsMarketing.phoneRequired':
        'Please set a contact telephone number to fully activate SMS notifications',
    'account.details.updated': 'User preferences updated successfully',
    'account.details.password.current': 'Current password',
    'account.details.password.change': 'Password change confirmation',
    'account.details.password.previous.confirm': 'Confirm previous password',
    'account.details.password.previous': 'Previous password',
    'account.details.updateErrorEmail':
        'We could not update your account at this time. Please note that we cannot change your email address if we already have an account using that email address.',
    'account.details.updateErrorGeneral':
        'We could not update your account at this time. Please try again later.',
    'account.details.updateErrorNewPhoto':
        'We could not upload a new photo at this time. Please try again later.',
    'account.details.photoWrongDimensions':
        'Error, photo is not in portrait orientation.',

    'account.details.updatePhoto':
        'This is your current Smartcard photo. Please update it if you wish to use a new photo with your replacement Smartcard.',
    'account.details.updatePhotoNoExistingPhoto':
        'Please upload a new photo for your replacement Smartcard.',

    'account.details.loginProviders':
        'You can log in to your account using the following identity providers:',
    'account.details.amazon.unlink': 'Unlink Amazon account',
    'account.details.amazon.unlink.confirm.message':
        'Are you sure you want to unlink your account from Amazon?<br><br>You will be able to link your account with Amazon again if needed.',
    'account.details.amazon.unlink.confirm.closeText': 'Cancel',
    'account.details.amazon.unlink.confirm.continueText': 'Unlink',
    'account.details.amazon.unlink.confirm.title': 'Confirm unlinking',
    'account.details.amazon.unlink.success.heading': 'Unlinking of accounts successful',
    'account.details.amazon.unlink.success.message':
        'You can link your Amazon account with your {accountType} account again the next time you use the Amazon Login button.<br><br>If you have never set your {accountType} password click the <i>"Forgotten your password?"</i> link the next time you are logging in.',
    'account.details.amazon.unlink.success.title':
        'You have successfully unlinked your Amazon and {accountType} accounts',
    'account.details.pendingDeletion.timeframe': 'You have asked for your account to be deleted which has been passed on to our team to action, we will send confirmation of when this has been completed within 28 days.',
    'account.details.pendingDeletion.ico': 'If you wish to know more about the right to be forgotten and exemptions that may apply, please visit ',
    'account.details.pendingDeletion.privacy': 'You can find our ',
    'account.details.pendingDeletion.privacy.link': 'Privacy Policy here',
    'account.details.pendingDeletion': 'To keep your account open you can ',
    'account.details.pendingDeletion.button': 'cancel this request',
    'account.details.pendingDeletion.button.sr': 'Click here to cancel the account deletion request',
    'account.details.pendingDeletion.modal.text': 'Are you sure you would like to cancel the account deletion process?',
    'account.deleteUserModal.title': 'Delete account',
    'account.deleteUserModal.continueButton': 'Delete my account',
    'account.deleteUserModal.smartcard': 'Not having an account with us means you will no longer be able to use your Key smartcard, you will have to transfer any valid Key smartcard tickets to paper tickets.',
    'account.deleteUserModal.information': 'It also means it will take longer to complete some actions on our app and website, such as buying a ticket, claiming compensation and getting a refund, and you will no longer receive offers or important updates.',
    'account.deleteUserModal.ico': 'If you wish to know more about the right to be forgotten and exemptions that may apply, please visit ',
    'account.deleteUserModal.privacy': 'You can find our ',
    'account.deleteUserModal.privacy.link': 'Privacy Policy here',
    'account.deleteUserModal.password': 'To confirm you still want to delete your account, please verify your password.',
    'account.deleteUserModal.timeframe': 'We will respond to your request within 28 days.',
    'account.deleteUserModal.success': 'Your account is pending deletion, please check your emails for confirmation.',
    'account.deleteUserModal.error.password': 'The password you provided is incorrect',
    'account.deleteUserModal.error.unknown': 'There was an unknown error, please try again later',

    'account.confirm.smartcardsignup': 'Get <{global.theKey}> <{global.smartcard}> with my account',
    'account.smartcardsignup.readmore':
        'Find out more about <{global.theKey}> <{global.smartcard}>',
    'account.smartcardsignup.details':
        '<{global.theKey}> <{global.smartcard}> is an alternative to paper tickets that makes it quicker and easier to buy and use tickets. {readmore}',
    'account.smartcardsignup.flexi': 'A Flexi Ticket can only be used with a Key Smartcard. Apply for your Key Smartcard below.',
    'account.smartcardsignup.address': 'Delivery Address',
    'account.smartcardsignup.dob': 'Date of birth',
    'account.smartcardsignup.isrn.placeholder': '(e.g. 633597-0000-0000-0000)',
    'account.smartcardsignup.children':
        'For children under 16, a passport style photo is required.',
    'account.smartcardreplacement.children':
        'You can upload a new photo for your replacement smartcard here.',
    'account.smartcardsignup.children.requirements':
        'Once the photograph has been approved, you will receive email confirmation. To complete the registration process you will also be requested to submit an age verification document such as copy of the child’s passport or birth certificate which should be submitted at any {brand} Key valid station. Once this is done a new Key <{global.smartcard}> will be issued to the address registered within <{global.theKey}> account.',
    'account.smartcardsignup.children.condition1': 'Must be portrait facing forwards',
    'account.smartcardsignup.uploadphoto': 'Upload photo',
    'account.smartcardsignup.keygoSignUp':
        'Get <{global.theKey}> <{global.smartcard}> and <{global.keyGo}> with my account',
    'account.smartcardsignup.autoDelayRepay':
        'If we see from your tap ins and out that you’ve been delayed 15 mins or more on our services, we will prepare a Delay Repay claim for you and email you to confirm. {moreInformation}',
    'account.smartcardsignup.moreInformation': 'For more information, please click here.',
    'account.smartcardsignup.benefit1': 'Beat the queues',
    'account.smartcardsignup.benefit2': 'Load season tickets',
    'account.smartcardsignup.benefit3': 'Auto delay repay',
    'account.smartcardsignup.benefit4': 'Tap & Go',
    'account.smartcardsignup.benefit5': '',

    'account.smartcardstatus.loading': 'Loading <{global.smartcard}> status',
    'account.smartcardstatus.replacementSmartcard':
        'Your request for a replacement <{global.smartcard}> is being processed.',
    'account.smartcardstatus.pendingSmartcard':
        'Your application for a <{global.smartcard}> is being processed.',
    'account.smartcardstatus.hotlisted': 'Your card is hotlisted please apply for a new one.',
    'account.smartcardstatus.applicationSubmitted': '1. Application submitted',
    'account.smartcardstatus.applicationProcessed': '2. Application processed',
    'account.smartcardstatus.smartcardPrinted': '3. <{global.key}> <{global.smartcard}> printed',
    'account.smartcardstatus.smartcardSent': '4. <{global.key}> <{global.smartcard}> in post',
    'account.smartcardstatus.notStarted': 'Pending',
    'account.smartcardstatus.inProgress': 'In progress',
    'account.smartcardstatus.completed': 'Completed',
    'account.smartcardstatus.applicationStatus': 'Application status',
    'account.smartcardstatus.replacementStatus': 'Replacement status',
    'account.smartcardstatus.replacementServiceUnavailable':
        'Replacement <{global.smartcard}> requests are unavailable at this time, please try again later.',

    'account.smartcard.product.status.pending': 'Pending',
    'account.smartcard.product.status.sendingToStation': 'Sending to station',
    'account.smartcard.product.status.sendingTopupToStation': 'Sending topup to station',
    'account.smartcard.product.status.readyToCollect': 'Ready to collect',
    'account.smartcard.product.status.topupReadyToCollect': 'Top up ready to collect',
    'account.smartcard.product.status.loadedOnCard': 'Loaded on card',
    'account.smartcard.product.status.productBlocked': 'Product blocked',
    'account.smartcard.product.status.productRemovedFromCard': 'Product removed from card',
    'account.smartcard.product.status.errorPleaseContact': 'Error - please contact us',
    'account.smartcard.product.status.cancelled': 'Cancelled',

    'account.smartcard.text.dateapplied': 'Date applied',
    'account.smartcard.text.label': '<{global.smartcard}> number',
    'account.smartcard.text.number': '<{global.key}> <{global.smartcard}> number',
    'account.smartcard.text.description': '({cardDescription})',
    'account.smartcard.text.capacity': '<{global.key}> <{global.smartcard}> capacity',
    'account.smartcard.text.cardExpiry': '<{global.key}> <{global.smartcard}> expiry',
    'account.smartcard.text.cardStatus': '<{global.key}> <{global.smartcard}> status',
    'account.smartcard.text.available': '{available} tickets available',
    'account.smartcard.text.cardFull': `Ticket limit reached. You're not able to purchase any more tickets on your <{global.key}> <{global.smartcard}> until one of your existing tickets has expired`,
    'account.smartcard.text.collectionWarning': `You're not able to purchase any further <{global.key}> <{global.smartcard}> tickets online until you have loaded these tickets onto your <{global.key}> <{global.smartcard}>`,
    'account.smartcard.text.cardtype': 'Card type',
    'account.smartcard.text.paymentcard': 'Card number',
    'account.smartcard.text.name': 'Cardholder name',
    'account.smartcard.text.customerName': 'Customer name',
    'account.smartcard.text.keygo':
        '<span class="text-keygo">key</span><i class="text-keygo--light">Go</i>',
    'account.smartcard.text.keygo.cancel':
        'Are you sure you want to close your keyGo account?  Please note:',
    'account.smartcard.text.keygo.cancel.warning.general': "Railcard details will be removed from your online account will need to be re-added if you sign up to keyGo again",
    'account.smartcard.text.keygo.cancel.warning.carnet': "Any keyGo Carnets tickets will no longer be available to use and cannot be refunded without an active keyGo account. Refund any complete bundles now before cancelling your keyGo account.",
    'account.smartcard.text.keygo.notReady': 'Account already has keyGo, or <{global.smartcard}> not ready for keyGo.',
    'account.smartcard.text.keygo.hosted.error.header': "The keyGo activation wasn't successful.",
    'account.smartcard.text.keygo.hosted.error.message': 'Please try again later or contact customer services.',
    'account.smartcard.text.balance': 'balance',
    'account.smartcard.text.lasttopup': 'Most recent top-up',
    'account.smartcard.text.validfromdate': 'Valid from',
    'account.smartcard.text.validuntildate': 'Valid until',
    'account.smartcard.text.validfromdateFlexi': 'Passes valid from',
    'account.smartcard.text.validuntildateFlexi': 'Passes expire on',
    'account.smartcard.text.status': 'Status',
    'account.smartcard.text.class': 'Class',
    'account.smartcard.text.class.first': 'First Class',
    'account.smartcard.text.class.standard': 'Standard',
    'account.smartcard.text.route': 'Route',
    'account.smartcard.text.autotopup': 'Auto top-up amount',
    'account.smartcard.text.noactivetickets':
        'No <{global.key}> <{global.smartcard}> tickets are currently active',
    'account.smartcard.text.incompletejourney':
        'You have incomplete journeys that should be completed immediately.',
    'account.smartcard.text.replacementinfo':
        'You can apply for up to two replacement cards free of charge; further requests may incur a fee and you will need to call our <{global.smartcard}> team on {phoneNumber} to continue your application.',
    'account.smartcard.text.replacementinfo.tickets.pre-replacement': 'Whilst waiting for your new card to arrive you will need to buy paper tickets to travel. Please keep these tickets as you will be able to claim a refund for them.',
    'account.smartcard.text.replacementinfo.tickets.post-replacement': 'Whilst waiting for your new card to arrive you will need to buy paper tickets to travel. Please keep these tickets as you will be able to claim a refund for them.',
    'account.smartcard.text.replacementinfo.flexi':
        'If you hold a flexi season ticket and aren\'t able to use all your passes due to waiting for the delivery of your replacement, you must apply for a refund for any remaining passes through your online account before the expiry of your flexi season ticket.',
    'account.smartcard.text.replacementinfo.season':
        'A refund will be given to annual season ticket holders providing the season ticket is active at the time of loss. A £5 admin fee will be applied to all tickets as per our refund policy',
    'account.smartcard.text.replacementinfo.refundpolicy': 'Refund policy',
    'account.smartcard.text.journeysFor': 'Showing journeys for {month}',
    'account.smartcard.text.journeyhistory':
        'Your journey history details are available for a maximum of 8 weeks.',
    'account.smartcard.text.journeyhistory.noresults': 'There are no journeys to display.',
    'account.smartcard.text.journeysbetween': 'Showing journeys between {startDate} and {endDate}',
    'account.smartcard.text.calltocomplete':
        'Please contact customer services on {phoneNumber} to inform them of the correct journey details so they can update your account',
    'account.smartcard.text.replacementReason': 'Select a replacement reason',
    'account.smartcard.text.cardNo': 'Card number',
    'account.smartcard.text.blockedDate': 'Blocked date:',
    'account.smartcard.text.replacementReason.title': 'Replacement reason:',
    'account.smartcard.text.replacementReason.NOT_WORKING': 'Card not working',
    'account.smartcard.text.replacementReason.LOST': 'Card lost',
    'account.smartcard.text.replacementReason.STOLEN': 'Card stolen',
    'account.smartcard.text.replacementReason.NOT_RECEIVED': 'Card not received',
    'account.smartcard.text.replacementReason.NAME_CHANGE': 'Change of name',
    'account.smartcard.text.additionalDetails': 'Additional details',
    'account.smartcard.text.crimeNumber': 'Crime number',
    'account.smartcard.text.conditionsLink': 'National Rail Conditions of Travel',
    'account.smartcard.text.address': 'your new Smartcard will be posted to the following address. If the address is not correct, please update your address before requesting a new card.',
    'account.smartcard.text.declaration':
        'I declare that the information I have given is to the best of my knowledge a true statement of the facts. I realise that I may be prosecuted if I knowingly make a false statement. I hereby give authority for the making of such enquiries as are reasonably necessary to verify the information that I have given in respect of this application is correct. I agree to accept the {link}, in particular Condition 34.',
    'account.smartcard.text.replacement.cancellationWarning':
        '<p><strong>Please note:</strong> your existing Key smartcard will be cancelled as soon as your replacement request is submitted.</p>',
    'account.smartcard.text.replacement.success':
        'We are processing your request to replace your <{global.key}> <{global.smartcard}>.',
    'account.smartcard.text.replacement.warning':
        'We are processing your request to replace your <{global.key}> <{global.smartcard}>. Information shown here reflects your most recent <{global.key}> <{global.smartcard}>. Some information may not be available at this time',
    'account.smartcard.text.replacement.linkText':
        'View the progress of your <{global.key}> <{global.smartcard}> replacement',
    'account.smartcard.button.completeyourjourney':
        'Complete your <{global.key}> <{global.smartcard}> journey',
    'account.smartcard.button.replacemycard': 'Request a new <{global.key}> <{global.smartcard}>',
    'account.smartcard.button.buynewticket': 'Buy a new <{global.key}> <{global.smartcard}> ticket',
    'account.smartcard.status.pending': 'Pending',
    'account.smartcard.status.active': 'Active',
    'account.smartcard.status.expired': 'Expired',
    'account.smartcard.status.full': 'Full',
    'account.smartcard.text.cancel': 'Are you sure you wish to cancel?',
    'account.smartcard.text.touchin': 'Touch in',
    'account.smartcard.text.touchout': 'Touch out',
    'account.smartcard.text.purchaseHistory.link':
        'Click here to manage your tickets including refunds',
    'account.smartcard.text.refund.link':
        'Click here to apply for a refund for <{global.smartcardTicketUppercase}>s',
    'account.smartcard.activation.status.activating': 'ACTIVATING',
    'account.smartcard.activation.status.ready': 'READY TO BE ACTIVATED',
    'account.smartcard.activation.text.heading':
        '<{global.key}> <{global.smartcard}> needs to be activated',
    'account.smartcard.activation.text.warning':
        'Please activate your <{global.smartcard}> when you have received it',
    'account.smartcard.activation.text.message':
        'You need to activate your <{global.smartcard}> in order to buy tickets.',
    'account.heading.smartcard.transfer': 'Transfer a paper season ticket',
    'account.heading.smartcard.replacement': '<{global.key}> <{global.smartcard}> - replacement',
    'account.heading.smartcard.replacement.warning':
        '<{global.key}> <{global.smartcard}> - replacement in progress',
    'account.heading.smartcard.replacement.request':
        'Request a new <{global.key}> <{global.smartcard}>',
    'account.heading.smartcard.previous':
        'Previous <{global.key}> {count, plural, =1{<{global.smartcard}>} other{<{global.smartcards}>}}',
    'account.heading.smartcard.apply': 'Apply for <{global.theKey}> <{global.smartcard}>',
    'account.heading.smartcard.register': 'Register <{global.theKey}> <{global.smartcard}>',
    'account.heading.smartcard.status': '<{global.key}> <{global.smartcard}> status',
    'account.heading.smartcard.tickets': '<{global.key}> <{global.smartcard}> - tickets',
    'account.heading.smartcard.journeyhistory':
        '<{global.key}> <{global.smartcard}> - journey history',
    'account.heading.smartcard.incompletejourneys':
        '<{global.key}> <{global.smartcard}> incomplete journeys',
    'account.heading.smartcard.activetickets':
        '<{global.key}> <{global.smartcard}> tickets available to use',
    'account.heading.smartcard.pendingtickets':
        'Tickets yet to be loaded to your <{global.key}> <{global.smartcard}>',
    'account.heading.smartcard.keygo': '{keygobrand} sign up',
    'account.heading.smartcard.keygoaccount': '{keygobrand} account',
    'account.heading.smartcard.disabled': '<{global.key}> <{global.smartcard}> - currently offline',
    'account.heading.smartcard.activity': '<{global.key}> <{global.smartcard}> - activity',
    'account.heading.smartcard.link': 'The <{global.key}> - Linked Accounts',
    'account.heading.keygocancel.cancelModal': 'Cancel my <{global.keyGo}> account',
    'account.heading.keygoupdate.updateModal': 'Update my <{global.keyGo}> account',
    'account.heading.flexi': 'Flexi Season tickets',
    'account.heading.flexi-disabled': 'Flexi - currently offline',
    'account.smartcard.keygo.reimbursement.description': 'Manual Refund',
    'account.smartcard.keygo.manualRefundCode.MANUAL_REFUND': 'Via Card',
    'account.smartcard.keygo.manualRefundCode.USER_DESIRE': 'Via Cheque',
    'account.smartcard.keygo.reimbursement.card.failureMessage': `Unfortunately we were unable to refund your keyGo balance to your payment card.
                                                                  Do you want to request a cheque for {balance} instead?
                                                                  This will be sent to your saved <{global.smartcard}> address and can take up to 10 days to process.`,
    'account.smartcard.keygo.reimbursement.card.successMessage':
        'You have successfully refunded your <{global.keyGo}> balance to your payment card.',
    'account.smartcard.keygo.reimbursement.cheque.failureMessage':
        'Unfortunately we were unable to process your request at this time. Please try again later.',
    'account.smartcard.keygo.reimbursement.cheque.successMessage':
        'Your request for a cheque has been submitted. Please allow up to 10 days for this to be processed.',
    'account.smartcard.keygo.reimbursement.modal.title': 'Withdraw <{global.keyGo}> balance',
    'account.smartcard.keygo.reimbursement.modal.cheque.confirm': 'Yes, please send a cheque',
    'account.smartcard.keygo.reimbursement.modal.cheque.deny': "No, I don't",
    'account.smartcard.keygo.reimbursement.modal.withdrawText': `Please confirm that you wish to withdraw {balance} from your keyGo account?
                                                                 This will be refunded back to your payment card ending {cardNumber}.`,
    'account.heading.smartcard.keygo.incompletejourneys':
        'You have incomplete journeys on your <{global.keyGo}> account',
    'account.smartcard.keygo.add': 'Add <{global.keyGo}> to your <{global.smartcard}>',
    'account.smartcard.keygo.byContinuingYouAccept': 'By clicking "Sign up to keyGo" you confirm that you have read, and that you accept, {termsAndConditions}.',
    'account.smartcard.keygo.cancel': 'Cancelling your {keygobrand} account',
    'account.smartcard.keygo.cancelation.pending':
        'Your {keygobrand} account is now blocked. Please contact customer services if you have any queries.',
    'account.smartcard.keygo.cancelled.successMessage':
        'Your {keygobrand} account has been cancelled. Any credit will be returned within four days.',
    'account.smartcard.keygo.cancelation.balance.warning':
        'Please update your payment details to clear your balance. Once cleared, you will be able to sign up for <{global.keyGo}> again. This process may take up to five days',
    'account.smartcard.keygo.cardBusy': 'There is a ticket awaiting collection on your card',
    'account.smartcard.keygo.cardFull': 'Your card is full',
    'account.smartcard.keygo.cardTypes':
        'For recurring {keygobrand} payments we only accept Visa, Visa Debit, MasterCard, MasterCard Debit and American Express.',
    'account.smartcard.keygo.closeaccount': 'Click below to close your account',
    'account.smartcard.keygo.collect.info':
        'Your new <{global.key}> <{global.smartcard}> will arrive with {keygobrand} pre-loaded and ready to use at any station on the {keygobrand} map.',
    'account.smartcard.keygo.collect.map': '{keygobrand} map link.',
    'account.smartcard.keygo.completeJourney.error.locationName':
        'Please enter the station of your missing tap',
    'account.smartcard.keygo.completeJourney.error.timestamp':
        'Please enter the date and the time of your missing tap',
    'account.smartcard.keygo.completeJourney.error.tapOutBeforeTapIn':
        'Tap out should be after tap in time',
    'account.smartcard.keygo.completeJourney.error.tapInAfterTapOut':
        'Tap in should be before tap out time',
    'account.smartcard.keygo.completeJourney.error.reason': 'Please choose one reason',
    'account.smartcard.keygo.completeJourney.form.footer':
        'Once you press confirm, you will no longer be able to make edits to this journey online.',
    'account.smartcard.keygo.completeJourney.form.heading.missingTapOut':
        'Update your journey from {tapInLocation}',
    'account.smartcard.keygo.completeJourney.form.heading.missingTapIn':
        'Update your journey to {tapOutLocation}',
    'account.smartcard.keygo.completeJourney.form.reason': "Please tell us why you didn't tap",
    'account.smartcard.keygo.completeJourney.form.reason.defaultOption': 'Please select a reason',
    'account.smartcard.keygo.completeJourney.form.success':
        'You have successfully completed your journey. The changes will be reflected in your account in 24 hours.',
    'account.smartcard.keygo.completeJourney.form.location.placeHolder': '{tapType} at...',
    'account.smartcard.keygo.completeJourney.form.location.knownTap': '{tapType} at {location}',
    'account.smartcard.keygo.completeJourney.form.location.label': '{tapType} location',
    'account.smartcard.keygo.completeJourney.form.timestamp.label': '{tapType} time',
    'account.smartcard.keygo.completeJourney.infoModal.heading.failure':
        'Unable to complete the journey',
    'account.smartcard.keygo.completeJourney.infoModal.heading.success':
        'Journey completed successfully',
    'account.smartcard.keygo.completeJourney.reason.FORGOT_TO_TAP': 'Forgot to tap',
    'account.smartcard.keygo.completeJourney.reason.VALIDATOR_OUT_OF_ORDER':
        'Card reader not working',
    'account.smartcard.keygo.completeJourney.reason.TRAVEL_DISRUPTION': 'Travel Disruption',
    'account.smartcard.keygo.completeJourney.tapIn': 'Tap in',
    'account.smartcard.keygo.completeJourney.tapOut': 'Tap out',
    'account.smartcard.keygo.confirmterms': 'I have read and accept the Terms and Conditions',
    'account.smartcard.keygo.delayrepay':
        'If you would like to claim Delay Repay for a journey covered by {keygobrand} please visit our {delayRepayGuide}',
    'account.smartcard.keygo.railcard':
        'Already have a railcard? Apply it to your smartcard {railcard} to enjoy discounted KeyGo travel',
    'account.smartcard.keygo.delayrepayguide': 'Delay Repay guide',
    'account.smartcard.keygo.details': `Add <{global.keyGo}> to your Key Smartcard to travel smarter and easier when You Pay As You Go.`,
    'account.smartcard.keygo.moreDetails': "<p>Once you have KeyGo on your Key Smartcard, all your entry and exit taps across the network will be analysed to calculate the best day fare for your journeys based upon the tickets you could have purchased that day. KeyGo will then charge the most appropriate ticket(s) for the journeys you made that day against your nominated payment card.</p><p>If you hold a Season ticket on your Key Smartcard, KeyGo will only charge you for the journeys you make beyond Season ticket's validity. {learnMoreLink}</p>",
    'account.smartcard.keygo.moreDetails.link': 'Learn more about <{global.keyGo}> and see the full route',
    'account.smartcard.keygo.details.reminder': `Reminder:`,
    'account.smartcard.keygo.details.1': `By signing up to <{global.keyGo}>:`,
    'account.smartcard.keygo.details.2': `You are agreeing to a Continuous Payment Authority and providing express consent for us to deduct automatic payment via your debit or credit card.
                                          You agree to keep your payment card and contact details up to date.
                                          You agree to tap in and out on all your journeys as per the <{global.keyGo}> Conditions of Use.
                                          You understand where <{global.keyGo}> is valid for travel as described on the <{global.keyGo}> map.
                                          You have read and understood the <{global.keyGo}> Conditions of Use.`,
    'account.smartcard.keygo.loadingStation': 'Please allow a minimum of two hours after purchase before loading your ticket onto your <{global.keyGo}> before loading it onto your <{global.key}> <{global.smartcard}>. Or you can now load <{global.keyGo}> onto your <{global.key}> <{global.smartcard}> using our app. {findMoreLink}',
    'account.smartcard.keygo.loadingStation.findMore': 'Find out more',
    'account.smartcard.keygo.signup': 'You can also add <{global.keyGo}> to your <{global.key}> <{global.smartcard}> to travel smarter and easier when you Pay As You Go.',
    'account.smartcard.keygo.signupButton': 'Sign up to KeyGo',

    'account.smartcard.keygo.flexiWarning.heading': 'Important information',
    'account.smartcard.keygo.flexiWarning.bodyCopy1': 'Do you have Flexi Season loaded on your Key Smartcard?',
    'account.smartcard.keygo.flexiWarning.bodyCopy2': 'If so, you\'ll need to register for a second Key Smartcard before you sign up for <{global.keyGo}>, more details can be found below.',
    'account.smartcard.keygo.flexiWarning.moreInfo': 'More information about keyGo and Flexi Season',
    'account.smartcard.keygo.flexiWarning.acknowledge': 'Yes, I understand',
    'account.smartcard.keygo.flexiWarning.continue': 'Continue to KeyGo registration',

    'account.smartcard.keygo.cardDetails': 'Card details',
    'account.smartcard.keygo.registrationWarning.title': 'Important information',
    'account.smartcard.keygo.registrationWarning.alert': `Before you complete the signup process for {keygobrand} - please be aware that:`,
    'account.smartcard.keygo.registrationWarning.list': `You must touch in and out on all your journeys so that we know where you have travelled from and to
            This applies even if you hold a season ticket
            If you do not touch in and out you will be charged a £50 incomplete journey charge every time you fail to touch
            Continued misuse may lead to us closing your account
            You can only use {keygobrand} on Gatwick Express, Great Northern, Southern and Thameslink service (there are some exceptions to this, details can be found {moreDetails}&nbsp;)`,
    'account.smartcard.keygo.registrationWarning.confirm': 'Accept and proceed',
    'account.smartcard.keygo.iblocks.error.ACCOUNT_CLOSED':
        'Unable to complete your journey, Amendment is only available for an account with active keyGo.',
    'account.smartcard.keygo.iblocks.error.CONFLICT': 'Journey is already complete.',
    'account.smartcard.keygo.iblocks.error.REACHED_TAP_LIMIT':
        'You have reached the monthly limit of incomplete taps that you can amend.',
    'account.smartcard.keygo.iblocks.error.INVALID_TAP_TIME':
        'Cannot complete the journey as tap time provided will exceed journey time allowed.',
    'account.smartcard.keygo.iblocks.error.UNKNOWN_ERROR':
        'An error occurred while handling your request. Please try again.',
    'account.smartcard.keygo.cardExpire.heading': '<{global.keyGo}> Payment Card',
    'account.smartcard.keygo.cardExpire.pending':
        'The payment card linked to your <{global.keyGo}> account <strong>is expiring soon</strong>. Update your payment details now to continue using <{global.keyGo}>.',
    'account.smartcard.keygo.cardExpire.expired':
        'The payment card linked to your <{global.keyGo}> account <strong>has expired</strong>. Update your payment details now to continue using <{global.keyGo}>.',
    'account.smartcard.keygo.incomplete': 'Incomplete',
    'account.smartcard.keygo.incompleteJourneys.body':
        'You have incomplete <{account.smartcard.text.keygo}> journeys on your account.  Complete them now to pay the correct fare and avoid an Incomplete Journey Charge.',
    'account.smartcard.keygo.incompleteJourneys.heading': 'Incomplete <{global.keyGo}> Journeys',
    'account.smartcard.keygo.journeyhistory.time': 'Time: ',
    'account.smartcard.keygo.journeyhistory.operatorName': 'Operator Name: ',
    'account.smartcard.keygo.journeyhistory.routeName': 'Route: ',
    'account.smartcard.keygo.journeyhistory.location': 'Location: ',
    'account.smartcard.keygo.journeyhistory.type': 'Type: ',
    'account.smartcard.keygo.journeyhistory.amendBefore': 'Amend before ',
    'account.smartcard.keygo.journeyhistory.autoSameStationExit': 'Same Station Exit',
    'account.smartcard.keygo.journeyhistory.autofill': 'Autofill',
    'account.smartcard.keygo.journeyhistory.charged': 'Charged',
    'account.smartcard.keygo.journeyhistory.correction': 'Correction',
    'account.smartcard.keygo.journeyhistory.edit': 'Edit',
    'account.smartcard.keygo.journeyhistory.gatelineAutocomplete': 'Gateline Autocomplete',
    'account.smartcard.keygo.journeyhistory.hideJourney': 'Hide journey',
    'account.smartcard.keygo.journeyhistory.manualfill': 'Journey Amended',
    'account.smartcard.keygo.journeyhistory.multiUseBoundary': 'Season Boundary',
    'account.smartcard.keygo.journeyhistory.pendingCharge': 'Pending charge',
    'account.smartcard.keygo.journeyhistory.route': '{origin} to {destination}',
    'account.smartcard.keygo.journeyhistory.showJourney': 'Show journey',
    'account.smartcard.keygo.journeyhistory.singleUseBoundary': 'Prepaid Boundary',
    'account.smartcard.keygo.journeyhistory.smartCarnet.peak': 'keyGo Carnet Peak',
    'account.smartcard.keygo.journeyhistory.smartCarnet.offPeak': 'keyGo Carnet Off-Peak',
    'account.smartcard.keygo.journeyhistory.touch': 'Touch',
    'account.smartcard.keygo.journeyhistory.unknown': 'Unknown',
    'account.smartcard.keygo.journeyhistory.updateNow': 'Update now',
    'account.smartcard.keygo.balanceAdjustmentCode.INCORRECT_FARE': 'Incorrect fare calculation',
    'account.smartcard.keygo.balanceAdjustmentCode.GOODWILL_ADJUSTMENT': 'Goodwill adjustment',
    'account.smartcard.keygo.balanceAdjustmentCode.OTHER_REASON': 'Other',
    'account.smartcard.keygo.reasonsGiven': 'Reasons given:',
    'account.smartcard.keygo.keygoBalance': '<{global.keyGo}> balance',
    'account.smartcard.keygo.lastUpdated': '{amount} on {date}',
    'account.smartcard.keygo.minimumTopup': 'A minimum automatic top-up of £25 applies',
    'account.smartcard.keygo.noTopup':
        'A £0.01 pre-authorisation will be charged to your payment card to set up your account. This will automatically be refunded in 3-5 working days.',
    'account.smartcard.keygo.updatePayment':
        'A £0.01 pre-authorisation will be charged to your payment card to update your details. This will automatically be refunded in 3-5 working days.',
    'account.smartcard.keygo.notAvailable': 'Registration not currently available:',
    'account.smartcard.keygo.pay.edit': 'Edit details',
    'account.smartcard.keygo.ready':
        'You have chosen {location} to start your first journey. {keygobrand} will be ready for use two hours from registration. Place your <{global.key}> <{global.smartcard}> on the card reader at the station within the next 30 days to enable {keygobrand}.',
    'account.smartcard.keygo.text.journeyhistory':
        'Your journey history details are available for a maximum of 8 weeks.',
    'account.smartcard.keygo.text.journeyhistory.weeklyCapApplied.1': 'Weekly cap applied saving ',
    'account.smartcard.keygo.text.journeyhistory.weeklyCapApplied.2': ' today',
    'account.smartcard.keygo.warn.incompletejourney':
        'To avoid additional charges due to missing information, please review your recent activity.',
    'account.smartcard.keygo.text.incompletejourney':
        'You can amend up to 3 journeys online within 28 days. Please update your journey with the correct information before the amendment deadline specified for each journey.</br>If you’ve tapped in and out at the same station without making a journey – please contact Customer Services to avoid a penalty charge.',
    'account.smartcard.keygo.updated.successMessage':
        'Your {keygobrand} payment details have been updated.',
    'account.smartcard.pay': 'Confirm payment',
    'account.smartcard.paymentdetails': 'Payment details',
    'account.smartcard.noPaymentCard':
        'To view your <{global.keyGo}> account details, please log into the website you originally used to sign up to <{global.keyGo}>.',
    'account.smartcard.status.balance': 'Your balance may take up to four days to refresh',
    'account.smartcard.keygo.routes':
        'Automatically pay the best value fare on rail and bus services.',
    'account.smartcard.keygo.registration.disabled':
        'We are currently making exciting changes to <{global.keyGo}>. New registrations have been disabled until {date} while we put these improvements in place',
    'account.smartcard.keygo.withdraw': 'Withdraw',

    'account.smartcard.transfer.searchButton': 'Find paper season tickets',
    'account.smartcard.transfer.searchHelp':
        'Please enter your photocard number and your postcode to help us find your current paper season ticket(s).',
    'account.smartcard.transfer.searchIntro':
        'If you have an existing paper season ticket we can add it to your new <{global.smartcard}> providing that your ticket:',
    'account.smartcard.transfer.searchRules': `Is on a line of route where the {brand} <{global.key}> <{global.smartcard}> is enabled
                                                Has a minimum of one month's validity before it expires
                                                Was purchased from the {brand} website or from a {brand} ticket office
                                                Is a full-priced Adult Season Ticket (any discounted Season Ticket will not be eligible for transfer)
                                                Is not a Dual-availability Season Ticket`,
    'account.smartcard.transfer.searchSupport':
        'For any <{global.key}> card queries please contact Customer Services on {smartcardPhoneNumber} (calls charged at local rate and may be recorded)',
    'account.smartcard.transfer.searchNoResults':
        'We could not find any valid tickets to swap over with the details that you supplied.',
    'account.smartcard.transfer.searchNoResults.retry': 'Please check that:',
    'account.smartcard.transfer.searchNoResults.reasons': `The photocard and postcode you entered are correct.
                                                            The ticket has more than one month's validity remaining.
                                                            It is an adult ticket.
                                                            The ticket is valid entirely on {brand}.
                                                            You did not purchase the ticket at a discounted price.`,
    'account.smartcard.transfer.searchResultsHeading':
        'We found the following paper season {count, plural, =1{ticket} other{tickets}} which are eligible for transfer onto your new <{global.key}> card, please select {count, plural, =1{} other {the one you would like to transfer, and}} a collection station:',
    'account.smartcard.transfer.searchResultsInvalidHeading':
        'We found {count} paper season {count, plural, =1{ticket which is} other{tickets which are}} not eligible for transfer.',
    'account.smartcard.transfer.showInvalid': 'Show details',
    'account.smartcard.transfer.ticketOffice.openingHours':
        'Opening hours for the ticket office at this station: ',
    'account.smartcard.transfer.ticketOffice.closed': 'Closed',
    'account.smartcard.transfer.ticketOffice.opens': 'Opens: {time}',
    'account.smartcard.transfer.ticketOffice.closes': 'Closes: {time}',
    'account.smartcard.transfer.hideInvalid': 'Hide details',
    'account.smartcard.transfer.collectionStationLong': 'Please select a collection station',
    'account.smartcard.transfer.collectionStation': 'Collection station',
    'account.smartcard.transfer.smartcardDetails':
        ' Please enter the following details to apply for a <{global.key}> <{global.smartcard}>:',
    'account.smartcard.transfer.submit': 'Submit',
    'account.smartcard.transfer.searchAgain': 'Back to search',
    'account.smartcard.transfer.ticketRequired': 'Please select a ticket',
    'account.smartcard.transfer.cardFull':
        'You have reached the maximum number of tickets, so cannot transfer any more tickets to your <{global.key}> <{global.smartcard}>.',
    'account.smartcard.transfer.cannotTransfer': `You are not able to transfer tickets onto an existing <{global.key}> <{global.smartcard}>. `,
    'account.smartcard.transfer.childError':
        'Please note, we are unable to perform ticket transfers on Child tickets.',
    'account.smartcard.transfer.success':
        'Your application to transfer your season ticket has been successfully submitted.',
    'account.smartcard.transfer.smartcardSuccess':
        'You can collect your new <{global.smartcard}> from the designated station after 5 days. You can also monitor the progress of your application in your account section.',

    'account.sticket.utn': 'UTN',
    'account.sticket.date': 'Date',
    'account.sticket.event': 'Scan event',
    'account.sticket.location': 'Location',
    'account.sticket.order': 'Order',
    'sticket.table.viewOrder': 'View order',
    'account.sticket.scanType.Blacklist': 'Blacklist',
    'account.sticket.scanType.Entry': 'Entry',
    'account.sticket.scanType.Exit': 'Exit',
    'account.sticket.scanType.Surface': 'Surface',
    'account.sticket.scanType.Unknown': 'Unknown',
    'account.sticket.empty': `<p><b>You don’t have any active sTickets.</b></p><p>sTicket is the digital version of a Season ticket provided as a barcode, that is instantly available to download in the Southeastern app. No need to wait for an email or queue at the station.</p>`,

    'account.orders.reorder': 'Re-order',
    'account.orders.reorder.title': 'Re-order tickets',
    'account.orders.reorder.date': 'Choose a new travel date',
    'account.orders.reorder.fallback.header': 'Re-order in progress. Please continue.',
    'account.orders.reorder.fallback.button': 'Continue',
    'account.orders.reorder.noMatching.header': 'We could not find a matching ticket',
    'account.orders.reorder.noMatching.description':
        'Sorry, We could not find a ticket that matches your previous order. If you would like to perform a search with the same details please click continue and you will be taken to the search page.',

    'account.orders.refunded': 'This order has been refunded',
    'account.orders.disclaimer':
        'This page only shows tickets purchased through this website. If you purchased your Smart ticket at the station please click {here} for a refund.',
    'account.orders.partialRefund': 'This order is partially refunded',
    'account.orders.none': 'No orders found',
    'account.orders.reference': 'Order reference',
    'account.orders.reference.details': 'Details for order reference',
    'account.orders.back': 'Back to purchase history',
    'account.orders.viewOrder': 'View order details',
    'account.orders.viewOrderAndDownloads': 'View order details and download tickets',
    'account.orders.collectionReady':
        'Available for collection, with reference {collectionReference}',
    'account.orders.paymentDetails': 'Payment details',
    'account.orders.refundDetails': 'Refund details',
    'account.orders.eVoucherPayment': '{count, plural, =1 {eVoucher} other {eVouchers}}',
    'account.orders.refundOptionsLink': 'View refund options',
    'account.orders.refundLink': 'Click here to apply for a refund on this ticket',
    'account.orders.viewEtickets': 'View order eTickets',
    'account.orders.viewEtickets.short': 'View eTickets',
    'account.orders.delayRepayLink': 'Claim Delay Repay',

    'account.orders.amend.isAmended': 'Amended Order',
    'account.orders.amend.badge': 'Amended',
    'account.orders.amend.journey': 'Amended Journey',
    'account.orders.amend.unamended': 'Unamended Journey',
    'account.orders.amend.previousJourney': 'Previous Journey',
    'account.orders.amend.amendedTicket': 'Original ticket',
    'account.orders.amend.back': 'Back to amend details',
    'account.orders.amend.bookingLink': 'Amend selected',
    'account.orders.amend.bullet1':
        'You can amend an Advance purchase ticket for another Advance purchase ticket',
    'account.orders.amend.bullet2':
        'Tickets can be amended right up to the day before travel',
    'account.orders.amend.bullet3': 'You cannot amend the origin and destination',
    'account.orders.amend.bullet4': 'You can amend the date, time and class of travel',
    'account.orders.amend.bullet5':
        'You will not receive a refund if the ticket you are amending to is cheaper than your original booking',
    'account.orders.amend.confirmation.details.email':
        'Details of your amendment have been emailed to {email}',
    'account.orders.amend.confirmation.success': 'Order successfully amended',
    'account.orders.amend.selectTicket': 'Select ticket for amendment',
    'account.orders.amend.selectFare': 'Select fare',
    'account.orders.amend.doAmend': 'Amend tickets',
    'account.orders.amend.header': 'Amend for Order #{id}',
    'account.orders.amend.newTicket': 'New ticket',
    'account.orders.amend.optionsLink': 'View amend options',
    'account.orders.amend.reason.default': 'This order cannot be amended',
    'account.orders.amend.search.noServices':
        'There are no services available to amend a ticket to. Please go back and try a different date and time.',
    'account.orders.amend.outwardDate': 'Outward date',
    'account.orders.amend.returnDate': 'Return date',
    'account.orders.amend.step1Title': 'Choose tickets',
    'account.orders.amend.step2Title': 'Choose date',
    'account.orders.amend.step3Title': 'Choose outward service',
    'account.orders.amend.step4Title': 'Choose outward fare',
    'account.orders.amend.step5Title': 'Choose inward service',
    'account.orders.amend.step6Title': 'Choose inward fare',
    'account.orders.amend.step7Title': 'Confirm',
    'account.orders.amend.step8Title': 'Summary',
    'account.orders.amend.ticket':
        '{adults, plural, =0 {} =1 {1 Adult} other {# Adults}}{children, plural, =0 {} =1 {1 Child} other {# Children}}',
    'account.orders.amend.summary.ticketCount': 'You are amending {count} ticket(s)',
    'account.orders.amend.summary.newTravelDate': 'Updated travel date: ',
    'account.orders.amend.summary.existingBooking': 'Existing booking:',
    'account.orders.amend.summary.fee': 'Administration fee:',
    'account.orders.amend.summary.leftToPay': 'Left to pay:',
    'account.orders.amend.summary.newTripCost': 'New ticket(s) cost:',
    'account.orders.amend.summary.moreDetails': 'More details',
    'account.orders.amend.summary.lessDetails': 'Less details',
    'account.orders.amend.warning.isAmending':
        'This order is already being amended. Please finish the current amendment before amending again.',
    'account.orders.amend.warning.notAmendable': 'This order cannot be amended',
    'account.orders.amend.warning.requiresPlusBus':
        'This order contains a Plusbus fare and both the ticket fare and the Plusbus fare must be amended',
    'account.orders.refund.header': 'Refund details for order {id}',
    'account.orders.refund.dayticket.valueRefunded': 'Value remaining',
    'account.orders.refund.initiatedBy': 'Refund initiated by',
    'account.orders.refund.ticketValue': 'Ticket value',
    'account.orders.refund.ticketsValue': 'Ticket(s) value',
    'account.orders.refund.customer': 'Customer',
    'account.orders.refund.customerService': 'Customer Service',
    'account.orders.refund.tickets': 'Tickets Refunded: ',
    'account.orders.refund.smartcardNumber': '<{global.key}> <{global.smartcard}> number: {smartcardNumber}',
    'account.orders.refund.unusedTickets': 'Unused passes',
    'account.orders.refund.itso.initiatedBy': 'Refund initiated by: {who}',
    'account.orders.refund.itso.refundDetails': 'Refund details',
    'account.orders.refund.itso.refundDate': 'Refunded on: {date}',
    'account.orders.refund.itso.cardAmount': 'Amount refunded to card ',
    'account.orders.refund.itso.voucherAmount': 'Amount refunded in vouchers',
    'account.orders.refund.itso.paypalAmount': 'Amount refunded to paypal account ',
    'account.orders.refund.itso.datePeriodPair': '{startDate} to {endDate}',
    'account.orders.refund.itso.description':
        'If you are applying for anything other than a standard refund or there are any circumstances you may need us to consider you should apply using the form  {here}.',
    'account.orders.refund.itso.flexi-description': '- You can apply for an online refund of unused Flexi passes. Expired passes can not be refunded.',
    'account.orders.refund.itso.flexi-description-secondary': '- No administration fee.',
    'account.orders.refund.itso.keySmartcardTicket': '<{global.key}> <{global.smartcard}> ticket',
    'account.orders.refund.itso.sticket': 'sTicket',
    'account.orders.refund.itso.refundCalculation': 'Refund calculation',
    'account.orders.refund.itso.modal.refundForm': 'refund form',
    'account.orders.refund.itso.modal.refundFailureMessage':
        'Please re-attempt to initiate a refund request through your online account after sometime or fill in the {form} to request refund through Customer Service.',
    'account.orders.refund.itso.refundForm.text':
        'If you are applying for anything other than a standard refund or there are any circumstances you may need us to consider you should apply using the form {here}.',
    'account.orders.refund.itso.checks.returnGoldCard':
        'I acknowledge that I must return my Gold Record Card to a <{app.owner.short}> station. By applying for a refund you will be unable to use your ticket from today.',
    'account.orders.refund.itso.checks.invalidateSameDay':
        'By applying for a refund you will be unable to use your ticket as from today.',
    'account.orders.refund.itso.timePeriod':
        '{m} {m, plural, =1 {month} other {months}} and {d} {d, plural, =1 {day} other {days}}',
    'account.orders.refund.itso.timePeriod.noMonth': '{d} {d, plural, =1 {day} other {days}}',
    'account.orders.refund.itso.unavailable.daysUsed': 'Days Used: ',
    'account.orders.refund.itso.unavailable.originalCost': 'Original ticket cost: ',
    'account.orders.refund.itso.unavailable.refundAmount': 'Refund amount: ',
    'account.orders.refund.itso.unavailable.seasonTicketUnused': 'Season ticket unused: ',
    'account.orders.refund.itso.unavailable.seasonTicketUsed': 'Season ticket used: ',
    'account.orders.refund.itso.unavailable.ticketUsed': 'Ticket used: ',
    'account.orders.refund.itso.valueUsed': 'Value used',
    'account.orders.refund.itso.deduction.amount': '- {amount}',
    'account.orders.refund.itso.manualRefundedIn.VOUCHER': 'Amount refunded in vouchers',
    'account.orders.refund.itso.manualRefundedIn.CHEQUE': 'Amount refunded by cheque',
    'account.orders.refund.itso.manualRefundedIn.CARD': 'Amount refunded to alternative card',
    'account.orders.refund.itso.warning.manualRefund':
        'You can apply for a refund by submitting the {form}',
    'account.orders.refund.itso.warning.manualRefundForm': 'Key Smartcard refund form',
    'account.orders.refund.itso.valueAvailable': 'Value available for refund',
    'account.orders.refund.itso.showRefundBreakdown': 'Show refund breakdown',
    'account.orders.refund.itso.hideRefundBreakdown': 'Hide refund breakdown',
    'account.orders.refund.itso.sticket.unavailable': 'Sorry, this ticket cannot be refunded online at the moment.',
    'account.orders.refund.itso.assignedTo': 'Assigned to',
    'account.orders.refund.warning.requiresPlusBus':
        'This order contains a Plusbus fare and the ticket fare cannot be refunded without refunding the Plusbus fare',
    'account.orders.refund.warning.alreadyCollected':
        'This order has already been collected from the ticket machine.',
    'account.orders.refund.warning.unavailable':
        'This order contains no tickets which are refundable online.',
    'account.orders.refund.warning.nonRefundablePayment':
        'This order was bought with a non-refundable payment type',
    'account.orders.refund.warning.refundsLocked': 'We cannot process this refund for your account due to suspicious activity; if you think there has been an error, please contact customer services.',
    'account.orders.refund.warning.nonRefundable': 'This order is now non-refundable',
    'account.orders.refund.warning.nonRefundable.onUserBrand': `{orderBrand, select,
            southern {Please log onto Southern to request a refund}
            greatnorthern {Please log onto Great Northern to request a refund}
            thameslink {Please log onto Thameslink to request a refund}
            gatwick {Please log onto Gatwick Express to request a refund}
            other {This order is now non-refundable}
        }`,
    'account.orders.refund.warning.manualRefund':
        'You can apply for a refund by completing {form} and returning it along with the tickets to the freepost address.',
    'account.orders.refund.refundForm': 'this refund form',
    'account.orders.refund.warning.manualRefund.contactUs': '{url}',
    'account.orders.refund.warning.manualRefund.smart': 'If you have a smart ticket on the Key - please complete ',
    'account.orders.refund.warning.manualRefund.paper': 'If you have a paper ticket - please complete ',
    'account.orders.refund.warning.manualRefund.eTicket': 'If you have an eTicket - please complete ',
    'account.orders.refund.warning.manualRefund.form': 'this refund form ',
    'account.orders.refund.selectTickets':
        'Select the tickets that you would like to have refunded',
    'account.orders.refund.bullet1': 'You can refund unused tickets online',
    'account.orders.refund.bullet2':
        'Tickets are refundable within {days} days of the ticket expiry date',
    'account.orders.refund.bullet3': '<b>Disruption Refund</b>: If there is disruption and you decide not to travel, please apply for your refund {link}',
    'account.orders.refund.bullet4':
        '<b>Industrial Action Refund</b>: If you purchased any of these ticket types - Anytime, Off-Peak, Super Off-Peak, One Day Travelcard, Advance or PlusBus Day Tickets dated specifically for a date affected by Industrial Action, please apply for your refund {refundUrl}. See Industrial Action dates {industrialActionUrl}',
    'account.orders.refund.moreInfo': 'More information about refunds can be found here',
    'account.orders.refund.selectReason': 'Please select reason for refund',
    'account.orders.refund.moreDetails': 'Please tell us more information',
    'account.orders.refund.fee': 'Administration fee: {fee}',
    'account.orders.refund.fee.waived': 'waived',
    'account.orders.refund.fee.alreadyPaid': '({fee} already paid)',
    'account.orders.refund.until': ' - refundable until {expires}',
    'account.orders.refund.ticket.refunded': 'Refunded',
    'account.orders.refund.ticket.notRefundable': 'Not Refundable',
    'account.orders.refund.ticket.used': 'Used',
    'account.orders.refund.ticket.unused': 'Unused',
    'account.orders.refund.ticket.partlyUsed': 'Partly used',
    'account.orders.refund.ticket.invalid-reason':
        'Selected refund reason is not applicable for {ticketName}.',
    'account.orders.refund.notRefundableOnline':
        'Sorry, these tickets are currently not refundable online.',
    'account.orders.refund.reason.advance-ticket': 'Advance tickets are not refundable online.',
    'account.orders.refund.reason.season-ticket':
        'Season tickets are not currently refundable online.',
    'account.orders.refund.notRefundableIn24hours':
        'eTickets purchased for the same date of travel are refundable after 24 hours.',
    'account.orders.refund.reason.exceeded-validity':
        'The {days} day period for refunds has expired for {tickets, plural, =1 {this ticket} other {these tickets}} and {tickets, plural, =1 {it is} other {they are}} no longer refundable.',
    'account.orders.refund.reason.default':
        '{tickets, plural, =1 {This ticket is} other {These tickets are}} not refundable online.',
    'account.orders.refund.warning.GS3':
        'These tickets contain a GroupSave discount. GroupSave discounts only apply to parties of 3 or more. The ticket(s) you are trying to refund would take you below that threshold. Please amend your refund request to ensure that there are at least 3 tickets remaining, alternatively you can choose to refund all of the tickets purchased.',
    'account.orders.refund.warning.2TR':
        'These tickets contain a Two Together discount. Two Together discount is valid only for journeys where the 2 named railcard holders travel together. You will need to apply for a refund on both of these tickets',
    'account.orders.refund.warning.DIC':
        'These tickets contain a disabled child discount. The disabled child discount is valid only for 1 adult travelling with a disabled child. You can only refund the adult ticket or both the adult and the child ticket.',
    'account.orders.refund.warning.DIS':
        'The Disabled Person discount is valid for up to 2 adults. Please check your selected tickets and ensure you meet these requirements',
    'account.orders.refund.warning.FAM':
        'Family & Friends discount discount requires at least one adult travelling with a child. Please amend your ticket selection so that any remaining tickets satisfy this requirement',
    'account.orders.refund.warning.HMF':
        'Your HM Forces Railcard discount would not be valid for the remaining tickets if we proceed with this refund. Please check your ticket selection and revise it if necessary to meet the railcard terms and conditions. If necessary you can refund all the tickets and re-plan your journey.',
    'account.orders.refund.warning.NEW':
        'Your Network Railcard discount would not be valid for the remaining tickets if we proceed with this refund. Please check your ticket selection and revise it if necessary to meet the railcard terms and conditions. If necessary you can refund all the tickets and re-plan your journey.',
    'account.orders.refund.warning.NGC':
        'Your Annual Gold Card discount would not be valid for the remaining tickets if we proceed with this refund. Please check your ticket selection and revise it if necessary to meet the railcard terms and conditions. If necessary you can refund all the tickets and re-plan your journey.',
    'account.orders.refund.warning.SRN':
        'Your Senior Railcard discount would not be valid for the remaining tickets if we proceed with this refund. Please check your ticket selection and revise it if necessary to meet the railcard terms and conditions. If necessary you can refund all the tickets and re-plan your journey.',
    'account.orders.refund.warning.YNG':
        'Your 16-25 railcard discount would not be valid for the remaining tickets if we proceed with this refund. Please check your ticket selection and revise it if necessary to meet the railcard terms and conditions. If necessary you can refund all the tickets and re-plan your journey.',
    'account.orders.refund.warning.fallback':
        'The selection of tickets you have chosen cannot be refunded. There might be railcard or ticket restrictions preventing this. Please try a different combination of tickets.',
    'account.orders.refund.warning.childFlatFare':
        'To refund an adult ticket you must also select the associated child ticket(s).',
    'account.orders.refund.warning.notAllChildrenAdded':
        'To refund a ticket you must also select the associated ticket(s).',
    'account.orders.refund.ticket':
        '{adults, plural, =0 {} =1 {1 Adult} other {# Adults}}{children, plural, =0 {} =1 {1 Child} other {# Children}}',
    'account.orders.refund.plusbus': 'PlusBus',
    'account.orders.refund.summary.subTotal': 'Sub-total',
    'account.orders.refund.summary.fee': 'Administration fee',
    'account.orders.refund.durationUsed': 'Passes used',
    'account.orders.refund.summary.bookingFee': 'Booking fee',
    'account.orders.refund.summary.total': 'Total to be refunded',
    'account.orders.refund.summary.totalRefunded': 'Total refunded',
    'account.orders.refund.summary.cardNotice':
        'Refunds can only be made to the original method(s) of payment.',
    'account.orders.refund.summary.cardMaximum': 'Maximum of {amount}',
    'account.orders.refund.summary.cardMaxed': 'Card is already refunded to its full amount',
    'account.orders.refund.summary.cardExpired': 'Card has expired',
    'account.orders.refund.summary.button': 'Apply for refund',
    'account.orders.refund.confirmation.title': 'Confirm refund request',
    'account.orders.refund.confirmation':
        'You have selected {tickets} {count, plural, =1{ticket} other {tickets}}. Please check the amount before confirming the refund request.',
    'account.orders.refund.incomplete':
        'Sorry, we are unable to process your refund on this occasion.',
    'account.orders.refund.incomplete.reason': 'Please select a reason for your refund.',
    'account.orders.refund.incomplete.note':
        'You have chosen "Other" as the reason for your refund. Please give us some more information.',
    'account.orders.refund.incomplete.tickets': 'Please select one or more tickets to refund.',
    'account.orders.refund.incomplete.total':
        '{tickets, plural, =1 {This ticket is} other {These tickets are}} not refundable as the refund amount is less than the administration fee.',
    'account.orders.refund.voucherAmount': 'Amount to refund in eVouchers',
    'account.orders.refund.cardAmount': 'Amount to refund to card',
    'account.orders.refund.paypalAmount': 'Amount to refund to PayPal',
    'account.orders.refund.successNotification':
        'Your {count, plural, =1 {ticket has} other {tickets have}} been refunded',
    'account.orders.refund.success.title': 'Refund successful',
    'account.orders.refund.success.summary':
        'Your refund of {total} for {count, plural, =1{your ticket has} other{# tickets have}} been approved.',
    'account.orders.refund.success.cardRefund':
        'It might take up to five working days for this to be processed back to your original payment method.',
    'account.orders.refund.success.voucherRefund':
        '{voucherAmount} has been refunded in the form of eVouchers. These are available to use immediately from your account.',
    'account.orders.refund.error.title': 'Refund unsuccessful',
    'account.orders.refund.error.invalid': 'Your request was invalid',
    'account.orders.refund.error.message':
        'Please re-attempt to initiate a refund request through your online account after sometime or fill in the refund form to request refund through Customer Service.',
    'account.orders.refund.error.message.title':
        'Unfortunately, your refund could not be processed.',
    'account.orders.refund.ticket.unusedConfirmation': 'I confirm that the ticket I am submitting for a refund is unused. <{app.owner.short}> reserve the right to check the validity of all refund requests and I understand that if this claim is suspected of being fraudulent, appropriate actions may be taken.',

    'account.orders.favourite.add': 'Add to favourites',
    'account.orders.favourite.added': 'Ticket added to favourites',
    'account.orders.favourite.remove': 'Remove favourite',

    'account.orders.railcards': 'Please make sure you bring your {railcardName} with you',

    'account.etickets.login': 'Log in to access eTickets',
    'account.etickets.oldETickets': 'Find your eTickets from our old app and website',
    'account.etickets.oldETickets.link': 'here',

    'account.historicETickets.title': 'Old eTickets',
    'account.historicETickets.error': 'Error loading tickets',
    'account.historicETickets.back': 'Back to eTickets',

    'account.linkorder.register.heading': 'Link your order to an account',
    'account.linkorder.login.heading': 'Link your order to an account',
    'account.linkorder.loading.message': 'Adding the order to your account',
    'account.linkorder.success.message':
        'Your order has been linked to your account. You can find your order in the Manage my tickets / refunds section',
    'account.linkorder.error.message':
        'We were unable to link your order to this account. To try again, please re-click the link in your booking confirmation email',
    'account.linkorder.error.message.alreadylinked':
        'This order is already linked to an account. Check the Manage my tickets / refunds section.',
    'account.linkorder.error.message.customerservices':
        'If you are still having problems, please contact Customer Services',

    'account.disruptionAlerts.headingText': 'account.nav.disruptionAlerts',
    'account.disruptionAlerts.serviceAlertsHeadingText': 'Timetable Service Alerts',
    'account.disruptionAlerts.descriptionText': `Our new service will email you an alert if there is a delay or disruption to your journey.
                                                 You can personalise the alerts by choosing which services you want to be alerted on and the frequency of these alerts.`,
    'account.serviceTimetableChangeAlerts.descriptionText': `We will alert you if there is a timetable change to your service.`,
    'account.disruptionAlerts.alertsHeadingText': 'Your active alerts',
    'account.disruptionAlerts.timetableAlertsHeadingText': 'Your active service timetable change alerts',
    'account.disruptionAlerts.noAlerts': 'No alerts saved.',
    'account.disruptionAlerts.noServiceChangeAlerts': 'No service timetable change alerts saved.',
    'account.disruptionAlerts.create.supportingText': 'You can get disruption alerts on up to {max} {max, plural, =1 {journey} other {journeys}}.',
    'account.disruptionAlerts.create.maximumReached': 'You have reached the maximum number of {max} {max, plural, =1 {journey} other {journeys}} allowed.',
    'account.disruptionAlerts.limitReached': 'You have saved the maximum amount of disruption alerts',
    'account.disruptionAlerts.genericError': 'An error occured whilst trying to create your disruption alert',
    'account.disruptionAlerts.unableToCreate': 'We could not create your disruption alert',
    'account.disruptionAlerts.alertHeading.aria': '{origin} to {destination}',
    'account.disruptionAlerts.servicesText': 'Alerted on {count} {count, plural, =1{train} other{trains}}',
    'account.disruptionAlerts.stationAlerts': 'You will also be notified of station issues at {origin} and {destination}',
    'account.disruptionAlerts.alertedOnText': 'Only alerted on: ',
    'account.disruptionAlerts.alertDurationText': 'Alert duration: ',
    'account.disruptionAlerts.alertDuration.forever': 'Ongoing',
    'account.disruptionAlerts.alertDuration.oneOff': ' only',
    'account.disruptionAlerts.alertDuration.fromTo': ' until ',
    'account.disruptionAlerts.create': 'Create alert',
    'account.disruptionAlerts.edit': 'Edit alert',
    'account.disruptionAlerts.mute': 'Pause alert',
    'account.disruptionAlerts.mutedUntil': 'Paused {date, select, null {indefinitely} other {until {date}}} – ',
    'account.disruptionAlerts.unmute': 'Reactivate',
    'account.disruptionAlerts.delete': 'Delete alert',
    'account.disruptionAlerts.delete.confirm.text': 'Are you sure you want to delete this alert?',
    'account.disruptionAlerts.delete.confirm.button': 'account.disruptionAlerts.delete',
    'account.disruptionAlerts.delete.cancel.button': 'Cancel',

    'account.loggedOut': 'Logged out',
    'account.loggedOut.youWereloggedOut': 'Automatic log out',
    'account.loggedOut.possibleReasons':
        'As you were inactive for over {autoLogoutMinutes} minutes, we logged you out for your security',

    'account.logoutWarning': 'Log out warning',
    'account.logoutWarning.almostTime': 'Automatic log out',
    'account.logoutWarning.possibleReasons':
        'As you have been inactive for nearly {autoLogoutMinutes} minutes, you will be logged out soon for your security',

    'account.confirm-smartcard.title': 'Please confirm your Key Smartcard details',
    'account.confirm-smartcard.fetch-smartcard-confirmation.failed': 'An error occurred whilst attempting to retrieve your smartcard confirmation data. Please try again later. If the issue persists, please contact customer services.',
    'account.confirm-smartcard.request-smartcard-confirmation.failed': 'An error occurred whilst attempting to replace your smartcard. Please try again later. If the issue persists, please contact customer services.',
    'account.confirm-smartcard.address': 'Please confirm your Smartcard details are correct',
    'account.confirm-smartcard.confirm-question': 'Please confirm you would like a replacement adult smartcard',
    'account.confirm-smartcard.confirm-notification': 'If you do not confirm to have your Key renewed before your current one expires, we will assume that you no longer require a Key Smartcard, and your account will be updated accordingly. You can always order a new Key later.',
    'account.confirm-smartcard.confirm-modal.title': 'Your New Adult Key Smartcard',
    'account.confirm-smartcard.confirm-modal.body': 'Thank you for confirming you would like to receive a new Adult Key Smartcard. Once your current Child Key Smartcard expires, you will be sent your new Smartcard within 7 working days.',
    'account.passenger-assist.accessibility-profile.title': 'My accessibility profile',
    'account.passenger-assist.accessibility-profile.description': 'Create your accessibility profile to make it even quicker to request assistance.',
    'account.passenger-assist.accessibility-profile.form.title': 'Title',
    'account.passenger-assist.accessibility-profile.form.firstName': 'First name',
    'account.passenger-assist.accessibility-profile.form.surname': 'Surname',
    'account.passenger-assist.accessibility-profile.form.email': 'Email',
    'account.passenger-assist.accessibility-profile.form.confirmEmail': 'Confirm new email',
    'account.passenger-assist.accessibility-profile.form.phone': 'Contact phone number',
    'account.passenger-assist.accessibility-profile.form.contactMethod': 'Preferred contact method',
    'account.passenger-assist.accessibility-profile.form.contactMethod.email': 'Email',
    'account.passenger-assist.accessibility-profile.form.contactMethod.phone': 'Phone',
    'account.passenger-assist.accessibility-profile.form.assistance-location.title': 'Points of the journey requiring assistance:',
    'account.passenger-assist.accessibility-profile.form.otherAssistanceLocation': 'Other',
    'account.passenger-assist.accessibility-profile.form.assistance-reason.title': 'Reasons for assistance:',
    'account.passenger-assist.accessibility-profile.form.otherAssistanceType': 'Other',
    'account.passenger-assist.accessibility-profile.form.contact-preferences.title': 'Contact preferences',
    'account.passenger-assist.accessibility-profile.form.contact-preferences.question.1': 'Would you like to be reminded of your assistance?',
    'account.passenger-assist.accessibility-profile.form.contact-preferences.question.2': 'Do you agree to be contacted via email regarding your experience?',
    'account.passenger-assist.accessibility-profile.form.contact-preferences.question.3': 'Can we contact you after your journey to discuss how it went?',
    'account.passenger-assist.accessibility-profile.form.contact-preferences.answer.yes': 'Yes',
    'account.passenger-assist.accessibility-profile.form.contact-preferences.answer.no': 'No',
    'account.passenger-assist.accessibility-profile.delete-btn': 'Delete profile',
    'account.passenger-assist.accessibility-profile.delete-confirm': 'Are you sure you want to delete this accessibility profile?',
    'account.passenger-assist.accessibility-profile.delete-confirm.yes': 'Yes, delete profile',
    'account.passenger-assist.accessibility-profile.delete-confirm.no': 'No, go back',
    'account.passenger-assist.accessibility-profile.back-to-results-screen.button': 'Return to booking',
    'account.passenger-assist.accessibility-profile.edit-btn.aria-label': 'Edit {sectionTitle}',
    'account.passenger-assist.accessibility-profile.section-personal-details.title': 'Personal Details',
    'account.passenger-assist.accessibility-profile.section-assistance-points.title': 'Assistance points',
    'account.passenger-assist.accessibility-profile.section-assistance-reason.title': 'Reason for assistance',
    'account.passenger-assist.accessibility-profile.section-contact-preferences.title': 'Contact Preferences',

    'account.passenger-assist.book-passenger-assist.title': 'Book Passenger Assist',
    'account.passenger-assist.book-passenger-assist.personal-details.title': 'Do you want to use your accessibility profile to book passenger assist?',
    'account.passenger-assist.book-passenger-assist.personal-details.use-ap': 'Use my accessibility profile',
    'account.passenger-assist.book-passenger-assist.personal-details.dont-use-ap': 'Don’t use my accessibility profile',
    'account.passenger-assist.book-passenger-assist.enter-personal-details.title': 'Please Enter your details',
    'account.passenger-assist.book-passenger-assist.when.title': 'When do you need assistance?',
    'account.passenger-assist.book-passenger-assist.train.title': 'Which journey do you need assistance with?',
    'account.passenger-assist.book-passenger-assist.train.subtitle': 'These are some journeys that you have booked an assistance for in the past.',
    'account.passenger-assist.book-passenger-assist.train-outward-search.title': 'Provide your outward journey details',
    'account.passenger-assist.book-passenger-assist.train-return-search.title': 'Provide your return journey details',
    'account.passenger-assist.book-passenger-assist.train-search.recommendation': 'We recommend you book at least 2 hours in advance of travelling.',
    'account.passenger-assist.book-passenger-assist.train-search.alert': 'Please be aware that passenger assistance can only be provided on National Rail parts of your journey.',
    'account.passenger-assist.book-passenger-assist.train-previous-pa-search.alert': 'We couldn\'t find any services that matched your selected previous journey.',
    'account.passenger-assist.book-passenger-assist.train-search.note': 'If you journey involves another train company we may need to confirm part of journey with them, this might depend on their contact centre opening hours.',
    'account.passenger-assist.book-passenger-assist.train-search.departing-station': 'Departing station',
    'account.passenger-assist.book-passenger-assist.train-search.arrival-station': 'Arrival station',
    'account.passenger-assist.book-passenger-assist.train-search.departure-time': 'Departure time',
    'account.passenger-assist.book-passenger-assist.train-search.journey-is-not-listed.button': 'My journey isn\'t listed',
    'account.passenger-assist.book-passenger-assist.train-search.more-details.button': 'more details',
    'account.passenger-assist.book-passenger-assist.train-search.hide-details.button': 'hide details',
    'account.passenger-assist.book-passenger-assist.return.title': 'Do you want to book assistance for the return journey?',
    'account.passenger-assist.book-passenger-assist.return.no-label': 'No, I do not want to book assistance for the return journey',
    'account.passenger-assist.book-passenger-assist.return.yes-label': 'Yes, I want to book assistance for the return journey"',
    'account.passenger-assist.book-passenger-assist.return.back-to-outward': 'Back to outward journey search',
    'account.passenger-assist.book-passenger-assist.contact-preferences.title': 'Contact Preferences',
    'account.passenger-assist.book-passenger-assist.save.accessibility-profile.question': 'Would you like to save your details and create an accessibility profile for future use?',
    'account.passenger-assist.book-passenger-assist.save.accessibility-profile.yes.aria-label': 'Yes – Save details to an accessibility profile',
    'account.passenger-assist.book-passenger-assist.save.accessibility-profile.no.aria-label': 'No – Do not save details',
    'account.passenger-assist.book-passenger-assist.summary.title': 'Check all your details are correct',
    'account.passenger-assist.book-passenger-assist.summary.subtitle-outward-journey': 'Outward journey',
    'account.passenger-assist.book-passenger-assist.summary.edit-outward-journey': 'Edit outward journey',
    'account.passenger-assist.book-passenger-assist.summary.subtitle-return-journey': 'Return journey',
    'account.passenger-assist.book-passenger-assist.summary.edit-return-journey': 'Edit return journey',
    'account.passenger-assist.book-passenger-assist.summary.cancel-confirm': 'Are you sure you want to cancel the passenger assistance booking?',
    'account.passenger-assist.book-passenger-assist.summary.cancel-confirm.yes': 'Yes, cancel the booking',
    'account.passenger-assist.book-passenger-assist.summary.cancel-confirm.no': 'No, keep the booking',
    'account.passenger-assist.edit.outward-journey': 'Edit outward journey details',
    'account.passenger-assist.edit.return-journey': 'Edit return journey details',
    'account.passenger-assist.book-passenger-assist.train.legs-badge.direct': 'Direct',
    'account.passenger-assist.book-passenger-assist.train.legs-badge.multi-leg': 'Multi Leg',
    'account.passenger-assist.book-passenger-assist.train.legs-change': 'Change',
    'account.passenger-assist.create-profile.failed': 'An error occurred whilst attempting to create your accessibility profile. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.update-profile.failed': 'An error occurred whilst attempting to update your accessibility profile. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.delete-profile.failed': 'An error occurred whilst attempting to delete your accessibility profile. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.cancel-request.failed': 'An error occurred whilst attempting to cancel your passenger assist request. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.amend-request.failed': 'An error occurred whilst attempting to amend your passenger assist request. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.get-profile.failed': 'An error occurred whilst attempting to retrieve your accessibility profile. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.get-previous-journeys.failed': 'An error occurred whilst attempting to retrieve your previous journeys. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.book-passenger-assist.failed': 'An error occurred whilst attempting to book your passenger assist. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.get-assisted-bookings.failed': 'An error occurred whilst attempting to get your assisted bookings. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.get-assisted-booking.failed': 'An error occurred whilst attempting to get your assisted booking. Please try again later. If the issue persists, please contact customer services.',
    'account.passenger-assist.edit.outward-journey.error': 'The arrival time of the outward journey cannot be after the departure time of the return journey.',
    'account.passenger-assist.edit.return-journey.error': 'The return journey cannot depart before the outward journey arrives.',

    'account.passenger-assist.view-booked-assistance.title': 'View booked assistance',
    'account.passenger-assist.amend-booked-assistance.title': 'Amend your Passenger Assist request',
    'account.passenger-assist.accessibility-profile.section-booking-details.title': 'Booking details',
    'account.passenger-assist.accessibility-profile.booking-reference.label': 'Booking reference:',
    'account.passenger-assist.accessibility-profile.status.label': 'Status:',
    'account.passenger-assist.accessibility-profile.outward-journey.label': 'Outward journey',
    'account.passenger-assist.accessibility-profile.return-journey.label': 'Return journey',
    'account.passenger-assist.view-booked-assistance.your-pa-requests.title': 'Your Passenger Assist requests',
    'account.passenger-assist.view-booked-assistance.cancel-request.button': 'Cancel request',
    'account.passenger-assist.view-booked-assistance.cancel-request-confirm': 'Are you sure you want to cancel this Passenger Assist request?',
    'account.passenger-assist.view-booked-assistance.cancel-request-confirm.yes': 'Yes, cancel request',
    'account.passenger-assist.view-booked-assistance.cancel-request-confirm.no': 'No, go back',
    'account.passenger-assist.view-booked-assistance.section.amend-request.button': 'Amend request',
    'account.passenger-assist.view-booked-assistance.journey.status-label': 'Status: ',
    'account.passenger-assist.view-booked-assistance.journey.outward-label': 'Outward:',
    'account.passenger-assist.view-booked-assistance.journey.return-label': 'Return:',

    'account.passenger-assist.success-message.create-accessibility-profile.title': 'Your Passenger Assist profile has been created',
    'account.passenger-assist.success-message.create-accessibility-profile.message': 'You can contact us to request Passenger Assist at any time of the day. If your journey involves another train company we may need to confirm part of your journey with them. This might depend on their contact centre opening hours. If this is the case, we will discuss options with you.',
    'account.passenger-assist.success-message.create-accessibility-profile.button': 'Book Passenger Assist',
    'account.passenger-assist.success-message.book-pa.title': 'Passenger Assist request submitted',
    'account.passenger-assist.success-message.book-pa.subtitle1': 'What happens next?',
    'account.passenger-assist.success-message.book-pa.subtitle2': 'We will send you an email when your booking has been confirmed',
    'account.passenger-assist.success-message.book-pa.message': 'View booked assistance to check the status of your booking or make changes',
    'account.passenger-assist.success-message.book-pa.button': 'View booking assistance',
    'account.passenger-assist.need-help': 'Need help?',

    'account.passenger-assist.view-profile': 'View my accessibility profile',
    'account.passenger-assist.book-passenger-assist.direct': 'Direct',
    'account.passenger-assist.book-passenger-assist.multi-leg': 'Multi Leg',

    'account.reservation.no-upcoming': 'You have no upcoming reservations.',
    'account.reservation.no-previous': 'You have no past reservations.',
    'account.reservation.button': 'Make a seat / bike reservation',
    'account.reservation.service-modal.title.inward': 'Select inward service',
    'account.reservation.service-modal.title.outward': 'Select outward service',
    'account.services-modal.ticket-type-list.title.outward': "Please choose type of ticket:",
    'account.services-modal.ticket-type-list.title.single': 'Your Single ticket:',
    'account.services-modal.ticket-type-list.title.return': 'Your Return ticket:',
    'account.reservation.add-reservation-modal.title': 'Add reservation',
    'account.reservation.bike-reservation-modal.title': 'Select bike reservations',
    'account.reservation.add-reservation-modal.seat.title': 'I would like to reserve a seat',
    'account.reservation.add-reservation-modal.bike.title': 'I would like to reserve a bike space',
    'account.reservation.add-reservation-modal.bike.select.title': 'Number of reservations',
    'account.reservation.reservation-summary-modal.title': 'Reservation Summary',
    'account.reservation.reservation-summary-modal.success': 'Your reservations have been successful!',
    'account.reservation.reservation-cancellation-modal.title': 'Cancel Reservation',
    'account.reservation.reservation-cancellation-modal.failed': 'We were unable to process your cancellation. Please try again later or contact customer support.',
    'account.reservation.reservation-cancellation-modal.cancelled': 'Your reservation has been successfully cancelled.',
    'account.reservation.reservation-summary-modal.badge.direct': 'Direct',
    'account.reservation.reservation-summary-modal.badge.multileg': 'Multi-leg',
    'account.reservation.reservation-summary-modal.email.title': `A copy of your reservation has been sent to <span class="highlighted-text">{email}</span>. To collect your reservations, use the reservation code provided at a ticket machine at the station.`,
    'account.reservation.item.title': 'Seat and Bike reservation',
    'account.reservation.item.message': 'This is a reservation only. A valid train ticket is required to travel.',
    'account.reservation.item.message.unavailable': 'Unavailable for this leg.',
    'account.reservation.outbound': 'Outbound',
    'account.reservation.return': 'Return',
    'account.reservation.seat': 'Seat reservation',
    'account.reservation.bike': 'Bike reservation',
    'account.reservation.reference': 'Collection reference',
    'account.reservation.date': 'Booking date :',
    'account.reservation.none': 'You have no upcoming or past reservations',

    "account.reservation.seating.any": "{count, plural, one {You can sit in any vacant seat for your class of travel} other {You can sit in any vacant seats for your class of travel}}",

    'account.reservation.bike.count': '{count, plural, one {# bike space reserved.} other {# bike spaces reserved.}}',
    'account.reservation.add-reservation-modal.bike.no-availability': 'There are not enough bike spaces available for this service.',
    'account.reservation.add-reservation-modal.seat.disabledMessage': 'Seat reservations are not available for this service.',
    'account.reservation.add-reservation-modal.bike.disabledMessage': 'Bike reservations are not available for this service.',

    'booking.fares.seats_reserved_header': 'Seat reservations',
    'booking.fares.bike_seats_reserved_header': 'Bike reservations',

    'booking.fares.seats_reserved': '{count, plural, one {# seat reserved} other {# seats reserved}}',
    'booking.fares.bike_seats_reserved': '{count, plural, one {# bike space reserved} other {# bikes spaces reserved}}',

    'passenger-assist.modal.text': 'If your accessibility details have recently changed, please amend your accessibility profile.',
    'passenger-assist.modal.no-trips.warning': 'You’re trying to proceed to checkout without choosing the journey(s) you require assistance for. \nPlease choose the journeys you require above.',
    'passenger-assist.modal.no-contact-answer.warning': 'Please choose "Can we contact you after your journey to discuss how it went?" above.',

    'alertmessage.required': 'This field is required',
    'alertmessage.required.short': 'Is required',
    'alertmessage.empty': 'This field is not allowed to be empty',
    'alertmessage.choose': 'Please choose from the list',
    'alertmessage.fieldsNoMatch': '{field} fields do not match',
    'alertmessage.badCardHolder': 'Please enter a valid cardholder name',
    'alertmessage.badPhotocardFirstName': 'Please enter a valid photocard first name',
    'alertmessage.badPhotocardLastName': 'Please enter a valid photocard last name',
    'alertmessage.badCardNumber': 'Please enter a valid card number',
    'alertmessage.photocardNumber': 'Please enter a valid photocard number',
    'alertmessage.badExpiryMonth': 'Please enter an expiry month',
    'alertmessage.badExpiryYear': 'Please enter an expiry year',
    'alertmessage.cardExpired': 'Provided card is expired',
    'alertmessage.badCvv': 'Please enter a valid CVV',
    'alertmessage.badSortCode': 'Please enter a valid sort code',
    'alertmessage.badAccountNumber': 'Please enter a valid account number',
    'alertmessage.badBacs':
        'Please enter a valid sort code and account number, and then confirm to validate',
    'alertmessage.bacsValidation.failure':
        'The information you have provided appears to be invalid. Please check your details and try again',
    'alertmessage.bacsValidation.success': 'Your sort code and account number fields match.',
    'alertmessage.badAddressLine': 'Please enter a valid address line',
    'alertmessage.badTownCity': 'Please enter a valid town/city',
    'alertmessage.badPostCode': 'Please enter a valid post code',
    'alertmessage.badCountry': 'Please enter a country',
    'alertmessage.badContactNumber': 'Please enter a valid contact number',
    'alertmessage.badMobileNumber': 'Please enter a valid mobile phone number',
    'alertmessage.badEmail': 'Please enter a valid email address',
    'alertmessage.nonMatchingconfirmEmail': 'Emails must match',
    'alertmessage.badPassword': 'Please enter a strong password (min 8 chars)',
    'alertmessage.badPassword.tooShort': 'At least 8 characters are required',
    'alertmessage.badName': 'Please only enter letters A-Z',
    'alertmessage.incorrectPassword': 'Authentication failed. Please check and try again.',
    'alertmessage.termsConditions': 'Please read and accept the Terms & Conditions',
    'alertmessage.registration.failed':
        'Sorry but we could not register your account at this time. Please try again later',
    'alertmessage.registration.failed.emailInUse':
        'You already have an account with us (Southern, Thameslink, Great Northern, Gatwick Express). All you need to do is log in or register with a different email address. <a href="{learnMoreUrl}" target="_blank">Learn more <i class="external-link external-link-no-underline"><i class="otrl otrl-externallink"></i></i></a>.',
    'alertmessage.paymentRegistration.failed.emailInUse':
        'You already have an account with us (Southern, Thameslink, Great Northern, Gatwick Express). All you need to do is log in or register with a different email address. <a href="{learnMoreUrl} target="_blank">Learn more <i class="external-link external-link-no-underline"><i class="otrl otrl-externallink"></i></i></a>.',
    'alertmessage.registration.failed.postcodeInvalid':
        'Your account has been successfully created. We could not complete your <{global.key}> <{global.smartcard}> registration as the postcode given appears to be invalid',
    'alertmessage.login.failed': 'Login failed. Please check your details and try again',
    'alertmessage.date.future': 'The date entered is in the future',
    'alertmessage.date.invalid': 'An invalid date has been entered',
    'alertmessage.time.invalid': 'An invalid time has been entered',
    'alertmessage.date.tooYoung':
        'Children under 5 travel for free on the rail network. Please apply for a <{global.smartcard}> when the child reaches 5 years of age.',
    'alertmessage.date.tooYoungPhotocard': 'Children under 5 travel for free on the rail network.',
    'alertmessage.date.under16Photocard': 'If you are over 16, please switch the toggle.',
    'alertmessage.date.over16': 'You must be over 16 for the self registred <{global.smartcard}>',
    'alertmessage.login.badPassword': 'Please enter your password (min 8 chars)',
    'alertmessage.login.badEmail': 'Please enter your email address',
    'alertmessage.regex': 'Please enter a valid {fieldName}',
    'alertmessage.regex.fieldName': 'value',
    'alertmessage.disruptedServicesPart1':
        'One or more of the services shown are disrupted, click ',
    'alertmessage.disruptedServicesPart2': ' to see if there are alternative routes available',
    'alertmessage.realTimeSearchPart1': 'You have chosen to search for realtime services, click ',
    'alertmessage.realTimeSearchPart2': ' to search for scheduled services',
    'alertmessage.smartcard': 'A valid {fieldName} is 18 digits long and starts with 633',
    'alertmessage.railcard-number.character-length-16-17': 'Must be at least 15 characters long',

    'alertmessage.string.min': 'Must be {num} or more characters long',
    'alertmessage.string.max':
        'Must be {num, plural, =1 { a single character} other {{num} characters or fewer}}',

    'alertmessage.form.moreRailcardsThanPassengers':
        'You cannot have more Railcards than passengers',
    'alertmessage.price': 'Please enter a valid price',

    'alertmessage.badDiscountCode': 'Please enter a valid discount',
    'alertmessage.discountCodeNotApplied': 'Please apply this discount and proceed',

    'payment.aboutToPay': 'You are about to pay',
    'payment.chooseAmount': 'Amount',
    'payment.deliveryDetails': 'Delivery address',
    'payment.viewDetails': 'View details',
    'payment.email.verification': 'Verify email address',
    'payment.billingAddress': 'Billing address',
    'payment.billingAddress.useShippingAddress': 'Use delivery address',
    'payment.sameAsDeliveryAddress': 'Same as delivery address',
    'payment.sameAsBillingAddress': 'Same as billing address',
    'payment.sameAsBillingAddressDisabled': 'Same as billing address (must be a UK address)',
    'payment.contactDetails': 'Contact details',
    'payment.payNow': 'Pay {basketTotal}',
    'payment.notReady.amazonPayInvalid': 'Please log in to your Amazon Pay account first',
    'payment.notReady.cardFormErrors': 'Please fix errors in your form',
    'payment.notReady.missingPaymentMethod': 'Please select your payment method',
    'payment.notReady.visaCheckoutNotValid': 'Your Visa Checkout card details are not valid. Please try again.',
    'payment.notReady.visaCheckoutAddressNotValid': 'Your Visa Checkout delivery address is outside United Kingdom. Please select UK delivery address to continue.',
    'payment.processing': 'We are processing your payment now...',
    'payment.processing.title': 'Please wait',
    'payment.processing.slowly.1': 'Please wait while your payment is processed.',
    'payment.processing.slowly.2':
        'Thank you for waiting. Your payment is currently being processed.',
    'payment.processing.slowly.3':
        'Thank you for waiting. We apologise for the delay while your payment is being processed.',
    'payment.processing.slowly.4': `We are very sorry that it is taking a long time to complete your purchase.
                                    We are waiting for confirmation that your payment has been processed.
                                    In very rare circumstances this can take several minutes.
                                    We thank you for your patience.`,
    'payment.processing.slowly.5': `We apologise that you are being made to wait a long time for confirmation that your payment has completed.
                                    Our payment provider is experiencing some difficulties.
                                    Your payment has been submitted, and we are waiting for the response.
                                    We ask you to please wait a little longer. Please do not close this page.`,
    'payment.processing.slowly.6': `We are still waiting for confirmation that your payment has succeeded.
                                    In very rare circumstances this can take several minutes.
                                    Please do not try to purchase your tickets again until we know the state of this order,
                                    as this might result in you paying for tickets twice.
                                    If you are concerned about this situation, our customer service team will be happy to help on {customerServicesPhoneNumber}`,
    'payment.amountPayable': 'Amount payable is {payableAmount}',
    'payment.chooseMethod': 'Payment method',
    'payment.chooseMethod.amazonPay': 'Pay with your Amazon account',
    'payment.chooseMethod.card': 'Pay by card',
    'payment.chooseMethod.paypal': 'Pay with PayPal',
    'payment.chooseMethod.visacheckout': 'Pay with Visa Checkout',
    'payment.goBack': 'Go back',
    'payment.goBackAndChooseDifferent': 'Go back and choose different payment method',
    'payment.details': '{paymentMethod} {amount}',
    'payment.details.paypal': '{ paypalAccount }',
    'payment.details.amazonPay': '{ amazonPayAccount }',
    'payment.details.applePay': '**** **** **** { applePayAccount }',
    'payment.details.googlePay': '**** **** **** { googlePayAccount }',
    'payment.evouchers': 'eVouchers',
    'payment.card.enterDetails': 'Please enter your card details',
    'payment.cardType.text':
        'We accept Visa, Visa Debit, MasterCard, MasterCard Debit, Maestro and American Express.',
    'payment.cardType.visa': 'Visa',
    'payment.cardType.mastercard': 'MasterCard',
    'payment.cardType.maestro': 'Maestro',
    'payment.cardType.amex': 'American Express',
    'payment.security.safekey': 'Safe Key',

    'payment.method.paypal': 'PayPal',
    'payment.paypal.chooseCorrectAddress':
        "Please choose a UK delivery address in PayPal widget to continue. You chose an address with '{countryCode}' country code.",

    'payment.warning.paypal-etickets':
        'Your e-Ticket will be sent to your registered PayPal email address. Please ensure you have access to this email account before selecting paypal',
    'payment.visacheckout.noAddress':
        'Please select your Visa Checkout delivery address by clicking the "Edit" link next to the card',
    'payment.visacheckout.payment.card.expired': `Your Visa Checkout saved card has expired.
                                                  You will need to click on the Edit link and select another card.`,
    'payment.visacheckout.review.message': 'Return to {brand} to complete your purchase.',
    'payment.visacheckout.continue': 'Continue',
    'payment.visacheckout.edit': 'Edit',
    'payment.visacheckout': 'Visa Checkout',
    'payment.visacheckout.saveCardDetails': 'Save payment details',
    'payment.visacheckout.cardInfo': '{brand} ** ** {lastFourDigits}',
    'payment.visacheckout.cardExpiryDate': 'Expiry {date}',
    'payment.visacheckout.tellMeMore': 'Tell me more',

    'payment.amazonPay.chooseCorrectAddress':
        "Please choose a UK delivery address in Amazon Pay address book to continue. You chose an address with '{countryCode}' country code.",
    'payment.amazonPay.buttonAltText': 'Click here to pay with Amazon Pay',
    'payment.amazonPay.errors.otrlGeneralError':
        'There was a problem with your Amazon Pay session. You have NOT been charged. Please click the Amazon Pay button to try again.',
    'payment.amazonPay.errors.sessionExpired':
        'Your Amazon Pay session expired. Please click the Amazon Pay button to try again.',
    'payment.amazonPay.header': 'Please select your Amazon Pay method',
    'payment.amazonPay.invalidPaymentMethod':
        'Your payment could not be processed. Please place the order again using another payment method.',

    'payment.registration.termsAndConditions': 'By creating an account you confirm that you have read, and that you accept, our {termsAndConditions} and {privacyPolicy}.',

    // These error messages are based on codes returned to us by the API
    // they are subject to change but we can show a friendly message for each
    'payment.error.API_ERROR': 'There was an issue with the API. Please try again',
    'payment.error.BAD_PRICE': 'Amount does not match total',
    'payment.error.BAD_TRANSACTION_REFERENCE': 'Bad transaction reference',
    'payment.error.BRAND_NOT_FOUND':
        'Sorry, your transaction has been declined by your bank, please contact your bank for more information',
    'payment.error.CANCELLED': 'Purchase cancelled by customer',
    'payment.error.CARD_NOT_FOUND': 'Saved card not found',
    'payment.error.DECLINED_BY_UPSTREAM_PROCESSOR':
        'Sorry, your transaction has been declined by your bank, please contact your bank for more information',
    'payment.error.EXPIRED': 'Purchase expired during authorisation',
    'payment.error.NO_RESULT': 'No result returned',
    'payment.error.ORDER_NOT_FOUND': 'Order not found',
    'payment.error.ORDER_PAID': 'Order already paid',
    'payment.error.REJECTED_BY_FRAUD':
        'Your card cannot be processed at this time, please contact customer services to continue',
    'payment.error.UNKNOWN_ERROR':
        'There was an issue while processing your payment. Please try again',
    'payment.error.UNSUPPORTED_TYPE': 'We’re sorry, but we do not accept this card type',
    'payment.error.USER_NOT_VOUCHER_OWNER': 'Invalid voucher used',
    'payment.error.VOIDED': 'Connection failure. Please try again',
    'payment.error.VOUCHER_NOT_FOUND_OR_EXPIRED': 'Voucher not found or expired',
    'payment.error.VOUCHER_INSUFFICIENT_AMOUNT': 'Voucher does not cover amount',
    'payment.error.AMAZON_PAY_MFA_FAILURE': 'The multi-factor authorisation of your Amazon Pay payment has failed. Please try again.',
    "payment.error.AMAZON_PAY_INVALID_PAYMENT_METHOD_ERROR": 'InvalidPaymentMehotd',
    'payment.error.PAY360_TIMEOUT': 'Your payment session timed out. Please try again, or choose another payment method.',
    'payment.error.PAGE_REFRESHED': 'The page has been refreshed and cannot be recovered. Please try again.',
    'payment.braintree.payWithGooglePay': 'Pay with Google Pay',
    'payment.braintree.payWithApplePay': 'Pay with Apple Pay',

    required: ' (required)',
    'required.fields': 'Required fields',

    'order.summary.heading': 'Order summary',
    'order.summary.discount': 'Promotion',
    'order.summary.total': 'Total:',
    'order.summary.total_all': "Total",
    'order.summary.adminFee': "Admin Fee",
    'order.summary.total_paid': "Total paid",
    'order.summary.total_used': "Total used",
    'order.summary.total_journey': "Total - journey {number}",
    'order.summary.total_refund': "Amount refunded",
    'order.summary.voucherAmount': 'eVouchers selected',
    'order.summary.paymentAmount': 'Amount due',
    'order.summary.discount_code': 'Discount code',
    'order.summary.booking_fee': 'Booking fee',
    'order.summary.charity_donation': 'Charity Donation ',
    'order.summary.restrictions': 'See restrictions',
    'order.summary.smartcard_title': '<{global.key}> <{global.smartcardTicket}>',
    'order.summary.journey_title': 'Journey {number}',
    'order.summary.to': 'to',
    'order.summary.out': 'Out',
    'order.summary.dep': 'Depart',
    'order.summary.arr': 'Arrive',
    'order.summary.return': 'Return',
    'order.summary.duration': 'Duration',
    'order.summary.journey_date': '{date} depart {station} at {time}',
    'order.summary.journey_details': 'Journey details',
    'order.summary.journeyDetails.title': 'Journey details',
    'order.summary.travelBy': 'Travel by',
    'order.summary.seats': 'Seat reservations',
    'order.summary.bikes': 'Bike reservations',
    'order.summary.refunding': 'Refunding and changing your ticket',
    'order.summary.bring_railcard': 'Please make sure you bring your Railcard(s) with you.',
    'order.summary.passengers.adult': '{number} x Adult',
    'order.summary.passengers.adultRailcard': '{number} x Adult with railcard',
    'order.summary.passengers.adult.carnet': '{number} Adult tickets',
    'order.summary.passengers.adult_railcard': '{number} x Adult with Railcard',
    'order.summary.passengers.adult_groupsave': '{number} x Adult with Groupsave',
    'order.summary.passengers.adult_railcard.carnet': '{number} Adult tickets with Railcard',
    'order.summary.passengers.adultRefunded': '{number} x Adult (Refunded)',
    'order.summary.passengers.adultRefunded.carnet': '{number} Adult tickets (Refunded)',
    'order.summary.passengers.child': '{number} x Child',
    'order.summary.passengers.child.carnet': '{number} Child tickets',
    'order.summary.passengers.child_railcard': '{number} x Child with Railcard',
    'order.summary.passengers.child_groupsave': '{number} x Child with Groupsave',
    'order.summary.passengers.child_railcard.carnet': '{number} Child tickets with Railcard',
    'order.summary.passengers.childRefunded': '{number} x Child (Refunded)',
    'order.summary.passengers.childRefunded.carnet': '{number} Child tickets (Refunded)',
    'order.summary.railcardDiscount.JCP': 'Jobcentre Plus Travel Discount Card discount applied',
    'order.summary.railcardDiscount.TSU': '16-17 Saver discount applied',
    'order.summary.plusbus': 'PlusBus',
    'order.summary.validFrom': 'Valid from',
    'order.summary.validTo': 'Valid to',
    'order.summary.removeJourney': 'remove',
    'order.summary.confirmRemoveJourney': 'Are you sure you want to remove this journey?',
    'order.summary.groupSave': '(GroupSave)',
    'order.summary.groupSave.name': 'GroupSave',
    'order.summary.groupSave.text':
        'This fare contains a GroupSave discount and is only valid if all passengers travel together.',
    'order.summary.groupSave.link': 'See details of exclusions and restrictions.',
    'order.summary.roversRangers.text': 'TODO order.summary.roversRangers.text',
    'order.summary.roversRangers.link': 'TODO order.summary.roversRangers.link',
    'order.summary.seating.coachSeat': 'Coach {coach}, Seat {seat}',
    'order.summary.duration.changes': '{changes, plural, =0 {Direct} =1 {Changes} other {Changes}}',
    'order.summary.change_trains': 'Change trains',
    'order.summary.download.tickets': 'Download tickets',
    'order.summary.download.title': 'Download tickets',
    'order.summary.download': 'Download',
    'order.summary.download.generating': 'Generating',
    'order.summary.download.ticketName': 'ticket {id}.pdf',
    'order.charity.title': 'Donate to the {charityName}',
    'order.charity.description-1': 'Donation is non-refundable.',
    'order.charity.description-2': 'You must be over 16 to donate.',
    'order.charity.checkbox-label': 'Round up',

    'order.invalidStateModal.heading': 'Order already paid',
    'order.invalidStateModal.paymentConfirmation': 'View Payment Confirmation',
    'order.invalidStateModal.planJourney': 'Plan another journey',
    'order.invalidStateModal.message':
        'Sorry, but your current basket has already been paid for. This can happen if you have multiple tabs open, or it could be an error on our part. Your basket has been cleared, you can view your payment confirmation or start an new order.',

    'order.interopErrorModal.heading': 'Fare unavailable',
    'order.interopError.message': 'Sorry, but this fare is not available via this train operator.',
    'order.interopError.messageHelp': 'Please contact us if you require assistance.',

    'basket.empty': 'Your basket is empty.',
    'basket.expired': 'Basket expired',
    'basket.expired.chosenJourneys':
        'Your basket has expired. It might be that it has been a while since you added to it, or perhaps {journeys, plural, =1 {your planned journey} other {one of your planned journeys}} is no longer available.',
    'basket.expired.pleaseTryAgain': 'Please try again',
    'basket.error.tooManyTrips':
        'You can only buy one <{global.key}> <{global.smartcardTicket}> at a time. Please complete your order with this ticket, or clear your basket.',
    'basket.error.tripsLimit':
        "Sorry, we can't add this journey because it will exceed the max number of tickets.",
    'basket.error.smartcardTicketConflict':
        'You cannot buy <{global.key}> <{global.smartcard}> tickets with paper tickets. Please complete your current order first, or clear your basket. ',
    'basket.error.noAvailability':
        "Sorry, we can't add this journey because there are not enough available seats.",

    'confirmation.keygo.success': 'You have successfully signed up to <{global.keyGo}>',
    'confirmation.success': 'Your payment was successful',
    'confirmation.details.email': 'Details of your purchase have been emailed to {email}',
    'confirmation.details.tracking.email':
        'Details of your purchase and a tracking link have been emailed to {email}',
    'confirmation.reference': 'Order reference number',
    'confirmation.normalTod':
        'To collect your tickets at {stationName}, bring any payment card and this collection reference.',
    'confirmation.collectionreference': 'Collection reference',
    'confirmation.collectionReferenceByEmail':
        'You will receive your collection reference by email shortly.',
    'confirmation.postalDelivery':
        'Your order will be sent to you by {deliveryMethod} to the following address:',
    'confirmation.delivery.address': 'customer name and address',
    'confirmation.delivery.printAtHome.emailDetails': 'Your {tickets, plural, =1{ticket} other{tickets}} will be emailed to you in a few minutes.',
    'confirmation.delivery.printAtHome.emailed': 'Your {tickets, plural, =1{ticket was} other{tickets were}} emailed to {email}',
    'confirmation.delivery.printAtHome.download': 'You can download and print your {tickets, plural, =1{ticket} other{tickets}} any time before your journey by clicking the download button beside your journey',
    'confirmation.delivery.printAtHome.downloadShortly': 'Your {tickets, plural, =1{ticket} other{tickets}} will also be available to download shortly from your purchase history.',
    'confirmation.delivery.printAtHome.printInstructions': 'Please ensure you print your ticket on plain white A4 paper.',
    'confirmation.smartcardNormal': 'Place your <{global.key}> <{global.smartcard}> on the gate reader or validator at {location} to load your ticket. Please allow a minimum of two hours after purchase before loading your ticket or use the {brand} On Track mobile app to load your ticket to your <{global.key}> <{global.smartcard}> now.',
    'confirmation.fraud': 'Payment Failed',
    'confirmation.decline': 'Sorry, your payment has been declined for the following reason:',
    'confirmation.paymentgateway': 'Payment gateway error: {message}',
    'confirmation.externalError': 'External Service Error: {message}',
    'confirmation.externalErrorMessage': 'This error has been logged, please try again later',
    'confirmation.printPage': 'Print confirmation page',
    'confirmation.backToPayment': 'Back to payment details',
    'confirmation.viewOrderSummary': 'View order summary',
    'confirmation.planAnotherJourney': 'Plan another journey',
    'confirmation.prompt': 'Please confirm',
    'confirmation.discoverMore': '<p>Discover more with our partnership offers, saving you money on great days out. Click on the link: <a href="{url}" target="_blank">Discover More</a></p>',
    'confirmation.accessibilityInfo': '<p>Please click <a href="{url}" target="_blank">here</a> to request any assisted travel requirements ahead of time.</p>',
    'confirmation.tryAgain': 'Try again',
    'confirmation.trackAndTrace.info': 'Protect your loved ones. Download the <a href="{url}" target="_blank">NHS Track and Trace App</a>, and when out and about simply turn on ‘contact tracing’.',
    'confirmation.nhsTrackAndTraceApp': 'NHS Track and Track App',
    'confirmation.delayRepayInfo.title': 'Money back if your train is delayed',
    'confirmation.delayRepayInfo.сarbonFootprintCalculator': 'Discover the environmental impact of your journey with our new ',
    'confirmation.delayRepayInfo.сarbonFootprintCalculator.link': 'Carbon Footprint Calculator',
    'confirmation.delayRepayInfo.message': 'If you arrive 15 minutes or more late at your destination because of a delay or cancellation to a service, you can claim Delay Repay compensation. Delay Repay is a national scheme train companies use to compensate passengers for delays or cancellations. Claims can be made up to 28 days after your journey.',
    'confirmation.delayRepayInfo.link': 'Claim Delay Repay',

    'order.details.collectionreference': 'Collection reference {ref}',
    'order.details.collectionReferenceByEmail':
        'You will receive your collection reference by email shortly',
    'order.details.todWithAnyCard':
        'In {deliveryTime} minutes your ticket(s) will be ready for collection from <strong>{stationName}</strong>, or any other station where ticket collection facilities are available.<br><br>To collect your tickets from a ticket machine you will need both your collection reference <strong>{ref}</strong> and <strong>any credit or debit card with a magnetic strip</strong>  (your card will not be charged but is still required in order to collect tickets).',
        'order.details.eTicketDelivery':
        `Your tickets will be emailed to {isGuest, select,
            true {{
                isPaypal, select,
                true {to your PayPal registered address}
                false {{
                    isAmazonPay, select,
                    true {to your Amazon Pay registered address}
                    false {{email}}
                }}
            }}
            false {{email}}
        }, if not received please check junk/spam inboxes. You have the option to download your eTickets to your native ticket wallet. Registered customers can download their unexpired eTickets from their My Account. You can use a mobile device to present your ticket for inspection or to pass through a gateline. Alternatively, you can print your ticket using plain white A4 paper.`,
    'order.details.postalDelivery': '{deliveryMethod} to the following address:',
    'order.details.postalDelivery.description': 'Your ticket will arrive as a printed eTicket, designed to be more sustainable and easier to recycle. Just scan the barcode on the gate at the station and show the ticket to staff when asked. {here} for more information about eTickets.',
    'order.details.smartcardWithProductDelivery': 'Your ticket will be preloaded onto your new <{global.key}> <{global.smartcard}>.',
    'order.details.smartcard': 'Use the Southeastern mobile app to load your purchase(s) to the Key Smartcard(s) now. Alternatively, please allow a minimum of two hours after purchase to load your ticket(s) at a gate reader or validator at {location}.',

    'voucher.showDetails': 'Show details',
    'voucher.hideDetails': 'Hide details',
    'voucher.reference': 'eVoucher reference',
    'voucher.expiryDate': 'Expiry date',
    'voucher.amount': 'Amount',
    'voucher.total': 'Total {voucherTotal}',
    'voucher.youHaveAvailable': 'You have <strong>{voucherTotal}</strong> in eVouchers available.',
    'voucher.noneAvailable': 'You do not have any eVouchers to view.',
    'voucher.payUsing': 'Pay using eVouchers',
    'voucher.error.number.base': 'Amount must be a number',
    'voucher.error.number.min': 'Amount must be greater than or equal to £0.00',
    'voucher.error.number.max': 'Amount must be less than or equal to £{maxAmount}',

    'ariaLabel.toggleMap': 'Toggle size of map',
    'ariaLabel.plusBusModal': 'Add PlusBus',
    'ariaLabel.showFares': 'Show fares',
    'ariaLabel.showJourneyDetails': 'Show journey details',
    'ariaLabel.showFromToJourneyDetails': 'Show {stations} journey details',
    'ariaLabel.showFromToJourneyDetails.stations': '{from} to {to}',
    'ariaLabel.openSeatingPrefs': 'Add seating preferences',
    'ariaLabel.openDaySaveTicketSearch': 'Open DaySave tickets search form',
    'ariaLabel.openRoversRangersTicketSearch': 'Open Rover tickets search form',
    'ariaLabel.clearContent': 'Clear content',
    'ariaLabel.openBasket': 'Open basket',
    'ariaLabel.basketButton': `{journeys, plural,
            =0 {Basket is empty, 0 journeys £0.00}
            =1 {1 journey in basket totalling {price}}
            other {# journeys in basket totalling {price}}
        }. <{ariaLabel.openBasket}>.`,
    'ariaLabel.showLoginDropdown': 'Log in / register',
    'ariaLabel.openLogin': 'Log in',
    'ariaLabel.openRegister': 'Register',
    'ariaLabel.openFavourites': 'Open favourite tickets',
    'ariaLabel.editSection': 'Edit section ',
    'ariaLabel.booking.delivery.stationSelect':
        'Select the station that you would like to collect your tickets from.',
    'ariaLabel.passengerInfo': '. Click to edit passengers',
    'ariaLabel.stepperInput': `{label, select,
            Children {Currently {value, plural, =1{# child} other{# children}}, click to {direction} 1 child.}
            Adults {Currently {value, plural, =1{# adult} other{# adults}}, click to {direction} 1 adult.}
            other {Currently {value} {label}, click to {direction} 1 {label}.}
        }
    `,
    'ariaLabel.seasonPassenger': '{passenger} ticket selected. Toggle to change',
    'ariaLabel.seasonDuration': 'Include {duration} season tickets',
    'ariaLabel.setFieldDescription': 'set {field} as ',
    'ariaLabel.fieldDescription': '{field} is set as ',
    'ariaLabel.clickToChange': '. Click to change.',
    'ariaLabel.startDateField': 'start date ',
    'ariaLabel.outboundDateField': 'outbound date',
    'ariaLabel.returnDateField': 'return date',
    'ariaLabel.webchatButton': 'Open a web chat to our support team',
    'ariaLabel.add-reservation-modal.bikesSelect': 'Choose the number of bikes',

    'form.dateSelector.placeholder': 'Select a date',
    'form.dateModal.selectDate': 'form.dateSelector.placeholder',
    'form.termsAndConditions': 'terms and conditions',
    'form.keyGoTermsAndConditions': '<{global.keyGo}> Conditions of Use',
    'form.termsYouAgreeTo':
        `By clicking pay you agree that our {termsAndConditions} and the {nationalRailTerms}
         govern your purchase and the terms of your journey.`,
    'form.termsAndConditions.capitalised': 'Terms & Conditions',
    'form.registration.termsAndConditions': 'By clicking “Create my account” you confirm that you have read, and that you accept, our {termsAndConditions} and {privacyPolicy}',
    'form.nationalRailConditions': 'National Rail Conditions of Travel',
    'form.privacyPolicy': 'privacy policy',
    'form.optional': '(optional)',
    'form.required': '(required)',
    'form.fieldset.optional': 'Optional',
    'autocomplete.resultCount': '{count, plural, =1{# item} other{# items}} available',
    'selectCustom.resultCount': '{count, plural, =1{# item} other{# items}} available',

    'form.discountCode.placeholder': 'Discount code',
    'form.discountCode.error.server': 'Server error',
    'form.discountCode.error.login': 'You need to login to use this discount code.',
    'form.discountCode.error.invalidCode': 'Invalid discount code.',
    'form.discountCode.error.invalidDiscount': 'Invalid discount code.',
    'form.discountCode.button': 'Apply',
    'form.discountCode.remove': 'Remove promotion',

    'footer.legal': 'Legal',
    'footer.accessibilityStatement': 'Accessibility statement',
    'footer.owner': 'Govia',
    'footer.faqs': 'FAQs',
    'footer.contact': 'Contact us',

    'poweredBy.text': 'Powered by OnTrack Retail',

    'address.useSavedAddress': 'Use a saved address',
    'address.saveAddress': 'Save this address',
    'address.noName': 'No Name',
    'address.delete': 'Delete address',
    'address.edit': 'Edit address',
    'address.noAddresses': 'You have no saved addresses.',
    'address.none': 'You don’t currently have any addresses saved. The next time you buy a ticket you can save your address details by ticking the ‘Save this address’ box on the payment screen.',
    'address.confirmDelete': 'Please confirm that you wish delete the following address:',
    'address.confirmDeleteOCDR': `This is your One Click Delay Repay address. If you delete this address, you will be opted out of One Click Delay Repay.
        Please confirm that you wish delete the following address:`,
    'address.max': "Sorry, you've reached the maximum number of saved addresses allowed",
    'address.noAllowedEdit':
        'Until your <{global.key}> <{global.smartcard}> is active, you are not allowed to edit your address.',
    'address.enterManually': 'Enter address manually',
    'address.clearAddress': 'Clear address',

    'photocard.confirmDelete': 'Please confirm that you wish delete the following photocard:',
    'photocard.updateOldRecord':
        'We now require additional information to validate your photocard record, please provide the missing information in order for us to update your record and continue with your season ticket purchase.',
    'photocard.delete': 'Delete photocard',
    'photocard.nophotocards': 'You don’t currently have any photocard details saved. The next time you buy a paper season ticket you can save your photocard by ticking the ‘Save this photocard’ box on the ticket collection option screen.',
    'photocard.useSavedPhotocard': 'Use a saved photocard',
    'photocard.savePhotocard': 'Save this photocard',
    'photocard.nickname': 'Nickname',
    'photocard.addPhotocard': 'Add a photocard',
    'photocard.more-info.trigger': 'More info',
    'photocard.more-info.title': 'Photocard details',
    'photocard.more-info.text':
        '<p>We cannot sell a Season Ticket without a valid photocard. Any period Season Ticket requires a photocard.</p> <p>You can use your existing photocard if you are renewing your season ticket.</p> <p>If you do not have a photocard, you can acquire one free of charge from your local station. You will need to provide a passport sized photo.</p> <p>Your photocard and ticket will need to be presented for inspection when travelling.</p>',
    'photocard.deliveryAddress': 'Address',
    'railcard.norailcards': 'You have no saved railcards',
    'railcard.add.info':
        'To add a Railcard to your online account, please complete the form below. We aim to review this within 5 working days.',
    'railcard.add.first-name': 'First name',
    'railcard.add.last-name': 'Last name',
    'railcard.add.railcard-type': 'Railcard type',
    'railcard.add.railcard-number': 'Railcard number',
    'railcard.add.railcard-number.error.character-length-16-17':
        'alertmessage.railcard-number.character-length-16-17',
    'railcard.add.railcard-number.help': 'Railcard number here',
    'railcard.add.railcard-info.help': 'Where can I find my Railcard number?',
    'railcard.add.disabled-person-example-physical': 'disabled-person-example-physical',
    'railcard.add.disabled-person-example-digital': 'disabled-person-example-digital',
    'railcard.add.expiry-date': 'Expiry date',
    'railcard.add.railcard-image': 'Image of your Railcard:',
    'railcard.add.uploadcondition1': `Required`,
    'railcard.add.uploadcondition2': `Must be below ${humanSize(UploadSize5mb)} in size`,
    'railcard.add.apply': 'Apply',
    'railcard.list.railcard-type': 'Railcard type',
    'railcard.list.railcard-number': 'Railcard',
    'railcard.list.railcard-status': 'Status',
    'railcard.list.keyGoEnabled': 'keyGo enabled',
    'railcard.list.expiry': 'Expiry',
    'railcard.list.added': 'Added',
    'railcard.list.pending': 'Pending approval',
    'railcard.list.remove': 'Remove',
    'railcard.list.addKeyGo': 'Add to keyGo',
    'railcard.delete.modal.confirmDelete':
        'Please confirm that you wish to delete the following railcard:',
    'railcard.delete.modal.railcard-type': 'Railcard type:',
    'railcard.delete.modal.railcard-number': 'Railcard number:',
    'railcard.delete.modal.expiry': 'Expiry:',
    'railcard.delete.modal.added': 'Added:',
    'railcard.link.modal.confirmLinkKeyGo':
        'Do you want to add this railcard to the keyGo account <strong>{email}</strong>?',
    'railcard.link.modal.failure':
        'There was a problem linking your railcard to your keyGo account. Please try again later.',
    'railcard.link.modal.success':
        'Your railcard has been successfully linked to your keyGo account.',
    'railcard.newlyApprovedRailcards.modal.title': 'New Railcard',
    'railcard.newlyApprovedRailcards.modal.message.one':
        'Good news, {railcardName} has now been added to your keyGo account. Discounts will be applied automatically when you travel using keyGo',
    'railcard.newlyApprovedRailcards.modal.message.multiple':
        'Good news, {railcardCount} railcards have now been added to your online account, discounts will be applied automatically when you travel using keyGo',
    'railcard.newlyApprovedRailcards.modal.confirmButtonText': 'View railcards',
    'railcard.syncKeyGoRailcards.modal.title': 'Sync with keyGo',
    'railcard.syncKeyGoRailcards.modal.message':
        'Good news, you are now registered for keyGo. You can now add railcards to your keyGo to enjoy discounted travel',
    'railcard.syncKeyGoRailcards.modal.confirmButtonText': 'View railcards',

    // Disruption alerts
    'disruptionAlerts.create.button': 'Create alert',
    'disruptionAlerts.create.button.generic': 'Regular journey alerts',
    'disruptionAlerts.create.button.matchingAlert': 'Alert created',
    'disruptionAlerts.modals.common.title': 'Journey alerts',
    'disruptionAlerts.modals.common.intro': "We'll keep you updated on delays and disruptions to your journey with real time information straight to your inbox.",
    'disruptionAlerts.modals.common.disclaimer': 'By clicking "Create alert" you agree to receiving emails concerning delays and disruptions on chosen services',
    'disruptionAlerts.modals.journeySelect.items': '{totalJourneys, plural, =1{We will only alert you to delays and disruption to the following journey:} other{Select which journeys you want to be alerted on:}}',
    'disruptionAlerts.modals.builder.services.heading': 'What services would you like to be alerted on?',
    'disruptionAlerts.modals.builder.duration.heading': 'Alert duration',
    'disruptionAlerts.modals.builder.duration.aria': 'Select how long you want the alert to last',
    'disruptionAlerts.modals.builder.duration.bankHolidayRestrictionNotice': 'Options limited due to the start date falling on a bank holiday',
    'disruptionAlerts.modals.builder.duration.sixMonths.label': '6 months',
    'disruptionAlerts.modals.builder.duration.sixMonths.supporting': 'This alert will remain active until {date}',
    'disruptionAlerts.modals.builder.duration.datePicker.chooseDate': 'Choose a date',
    'disruptionAlerts.modals.builder.duration.datePicker.changeDate': 'Change',
    'disruptionAlerts.modals.builder.duration.oneOff.label': 'One-off',
    'disruptionAlerts.modals.builder.duration.oneOff.today': 'Today',
    'disruptionAlerts.modals.builder.duration.oneOff.tomorrow': 'Tomorrow',
    'disruptionAlerts.modals.builder.duration.oneOff.supporting': 'You will only be alerted on {date}',
    'disruptionAlerts.modals.builder.duration.custom.label': 'Custom',
    'disruptionAlerts.modals.builder.duration.custom.startDate': 'Start date: ',
    'disruptionAlerts.modals.builder.duration.custom.supporting': 'End date: ',
    'disruptionAlerts.modals.builder.duration.custom.supporting.endDateChosen': 'This alert will remain active until ',
    'disruptionAlerts.modals.builder.days.heading': 'What days do you want to be alerted on?',
    'disruptionAlerts.modals.builder.days.aria': 'Select when you want to be alerted',
    'disruptionAlerts.modals.builder.days.weekdays.label': 'Weekdays',
    'disruptionAlerts.modals.builder.days.weekdays.aria': 'Select which weekdays you want to be alerted on',
    'disruptionAlerts.modals.builder.days.weekdays.monday': 'Monday',
    'disruptionAlerts.modals.builder.days.weekdays.tuesday': 'Tuesday',
    'disruptionAlerts.modals.builder.days.weekdays.wednesday': 'Wednesday',
    'disruptionAlerts.modals.builder.days.weekdays.thursday': 'Thursday',
    'disruptionAlerts.modals.builder.days.weekdays.friday': 'Friday',
    'disruptionAlerts.modals.builder.days.saturdays.label': 'Saturdays',
    'disruptionAlerts.modals.builder.days.sundays.label': 'Sundays',
    'disruptionAlerts.modals.success.heading': 'Journey alert created',
    'disruptionAlerts.modals.success.message.oneOff':
        'You will only be alerted to the service(s) you plan on travelling on. The alert will be cancelled when your train has reached your destination.',
    'disruptionAlerts.modals.success.message.ongoing':
        `You can change your alert preferences at any point by going into 'My Account' and updating what you'd like to receive.`,
    'disruptionAlerts.modals.success.createReturnAlert': 'Create return journey alert',
    'disruptionAlerts.modals.limitExceeded.title': 'We could not create your alert',
    'disruptionAlerts.modals.limitExceeded.message':
        'You have saved the maximum amount of disruption alerts. Please remove one or more alerts and try again.',
    'disruptionAlerts.modals.error.title': 'We could edit create your alert',
    'disruptionAlerts.modals.mute.title': 'Pause your journey alert',
    'disruptionAlerts.modals.mute.muteFrom': 'Pause alert from',
    'disruptionAlerts.modals.mute.reactivation': 'When do you want to reactivate your alert from {origin} to {destination}?',
    'disruptionAlerts.modals.mute.reactivation.never.label': "Deactivate",
    'disruptionAlerts.modals.mute.reactivation.never.supporting': 'This alert will remain paused until you reactivate it within your account',
    'disruptionAlerts.modals.mute.reactivation.day.label': "1 day",
    'disruptionAlerts.modals.mute.reactivation.week.label': '1 week',
    'disruptionAlerts.modals.mute.reactivation.month.label': '1 month',
    'disruptionAlerts.modals.mute.reactivation.custom.label': 'Select a date',
    'disruptionAlerts.modals.mute.reactivation.custom.endDate': 'Reactivation date: ',
    'disruptionAlerts.modals.mute.disclaimer': 'You will not be alerted on any disruption to this journey whilst the alert is paused',
    'disruptionAlerts.modals.mute.confirm.button': 'Pause alert',
    'disruptionAlerts.serviceSelector.stations.origin.label': 'booking.journey.origin.label',
    'disruptionAlerts.serviceSelector.stations.origin': 'booking.journey.origin',
    'disruptionAlerts.serviceSelector.stations.destination.label': 'booking.journey.destination.label',
    'disruptionAlerts.serviceSelector.stations.destination': 'booking.journey.destination',
    'disruptionAlerts.serviceSelector.departureDatetime.part1': 'Alert me on services starting from ',
    'disruptionAlerts.serviceSelector.departureDatetime.part2': ' departing around ',
    'disruptionAlerts.serviceSelector.departureDatetime.changeDate': '(change)',
    'disruptionAlerts.serviceSelector.noServicesFound': 'No services found',
    'disruptionAlerts.serviceSelector.earlier': 'Earlier',
    'disruptionAlerts.serviceSelector.later': 'Later',
    'disruptionAlerts.serviceSelector.table.caption': 'List of services',
    'disruptionAlerts.serviceSelector.table.departs': 'Departs',
    'disruptionAlerts.serviceSelector.table.arrives': 'Arrives',
    'disruptionAlerts.serviceSelector.table.from': 'From',
    'disruptionAlerts.serviceSelector.table.to': 'To',
    'disruptionAlerts.serviceSelector.table.duration': 'Duration',
    'disruptionAlerts.serviceSelector.table.changes': 'Changes',
    'disruptionAlerts.serviceSelector.table.setUpAlert': 'Set up alert',
    'disruptionAlerts.serviceSelector.serviceCount': `
        {count, plural,
            =0 {Receive alerts for up to 5 services}
            =1 {Alerts set up for # service}
            other {Alerts set up for # services}
        }`,
    'disruptionAlerts.serviceSelector.selectionLimitExceeded': 'Too many services selected (maximum {max})',
    'disruptionAlerts.serviceSelector.clearSelection': 'Clear selection',
    'disruptionAlerts.landing.mute.heading': 'account.nav.disruptionAlerts',
    'disruptionAlerts.landing.mute.confirm.text': 'Are you sure you wish to mute {muteAll, select, true {all alerts} other {this alert}} for the day?',
    'disruptionAlerts.landing.mute.confirm.button': 'Mute {muteAll, select, true {all alerts} other {alert}}',
    'disruptionAlerts.landing.mute.successModal.title': '{muteAll, select, true {Alerts} other {Alert}} paused',
    'disruptionAlerts.landing.mute.successModal.message':
        'You will no longer receive disruption notifications for {muteAll, select, true {any of your existing alerts} other {this alert}} today.',
    'disruptionAlerts.landing.mute.errors.common.title': 'We could not mute your {muteAll, select, true {alerts} other {alert}}',
    'disruptionAlerts.landing.mute.errors.invalidURL.message': 'Please double-check the link from your email and try again.',
    'disruptionAlerts.landing.mute.errors.securityCheckFailure.message': 'Invalid security token.',
    'disruptionAlerts.landing.mute.errors.tokenExpired.message': 'Security token expired.',
    'disruptionAlerts.landing.mute.errors.alreadyMuted.message': 'This alert is already muted.',

    'disruptionAlerts.landing.delete.heading': 'Disruption alerts',
    'disruptionAlerts.landing.delete.errors.general': 'Your alert could not be deleted',
    'disruptionAlerts.landing.delete.errors.invalidURL.message': 'Please double-check the link from your email and try again.',
    'disruptionAlerts.landing.delete.successModal.title': 'Alert deleted',
    'disruptionAlerts.landing.delete.successModal.message':
        'You will no longer receive disruption change notifications for this journey',
    'disruptionAlerts.landing.delete.errors.common.title': 'We could not delete your alert',
    'disruptionAlerts.landing.delete.confirm.text': 'Are you sure you wish to delete this alert?',
    'disruptionAlerts.landing.delete.confirm.button': 'Delete alert',

    'nre.journeyPlanner.linkText': 'You can view more information about your ticket here',
    'nre.transferModal.failure.sorry': 'Sorry, something went wrong.',
    'nre.transferModal.failure.text':
        'We were unable to transfer your basket from National Rail to the {brand} ticketing website.',
    'nre.transferModal.failure.text2': 'Please plan your journey again here.',

    'nre.transferModal.success.text':
        'We have transferred your basket from National Rail to the {brand} ticketing website.',
    'nre.transferModal.success.text2': 'You can now continue with your purchase.',
    'nre.landingPage.transferring': 'Transferring your basket',
    'nre.logoAltText': 'national rail logo',

    // Travelcards
    'tickettype.ADT':
        'Valid at all times. Includes unlimited travel on trains, London Underground, buses, DLR and Tramlink services within the zones specified. Refundable.',
    'tickettype.FDT': 'tickettype.ADT',

    'tickettype.1ST':
        'Valid at off-peak times only. Includes unlimited travel on trains, London Underground, buses, DLR and Tramlink services within the zones specified. Refundable.',
    'tickettype.1DT': 'tickettype.CST',
    'tickettype.AM3': 'tickettype.1ST',
    'tickettype.FTX': 'tickettype.1ST',
    'tickettype.ODT': 'tickettype.1ST',
    'tickettype.OTF': 'tickettype.1ST',
    'tickettype.SNT': 'tickettype.1ST',
    'tickettype.SOT': 'tickettype.1ST',
    'tickettype.STO': 'tickettype.1ST',
    'tickettype.STP': 'tickettype.1ST',
    'tickettype.WDT': 'tickettype.1ST',
    'tickettype.WKC': 'tickettype.1ST',
    'tickettype.WRE': 'tickettype.1ST',
    'tickettype.WTC': 'tickettype.1ST',

    // Off-Peak Single
    'tickettype.SVS': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.BVS': 'tickettype.SVS',
    'tickettype.FSS': 'tickettype.SVS',
    'tickettype.BFS': 'tickettype.SVS',

    // Off-Peak Day Single
    'tickettype.CDS': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.SVH': 'tickettype.CDS',

    // Off-Peak Return
    'tickettype.SVR': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.BVR': 'tickettype.SVR',
    'tickettype.FSR': 'tickettype.SVR',
    'tickettype.BFR': 'tickettype.SVR',
    'tickettype.SFR': 'tickettype.SVR',

    // Off-Peak Day Return
    'tickettype.CDR': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.1DR': 'tickettype.CDR',
    'tickettype.FCD': 'tickettype.CDR',
    'tickettype.FCR': 'tickettype.CDR',
    'tickettype.GE1': 'tickettype.CDR',

    // Super Off-Peak Single
    'tickettype.SSS': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.OPS': 'tickettype.SSS',

    // Super Off-Peak Day Single
    'tickettype.SWS': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.OPD': 'tickettype.SWS',
    'tickettype.GDS': 'tickettype.SWS',
    'tickettype.PDS': 'tickettype.SWS',

    // Super Off-Peak Return
    'tickettype.SSR': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.OPR': 'tickettype.SSR',
    'tickettype.SOP': 'tickettype.SSR',

    // Super Off-Peak Day Return
    'tickettype.SRR': 'Flexible ticket valid for travel on off-peak trains only',
    'tickettype.SCO': 'tickettype.SRR',
    'tickettype.PDR': 'tickettype.SRR',
    'tickettype.C1R': 'tickettype.SRR',
    'tickettype.CBA': 'tickettype.SRR',
    'tickettype.GDR': 'tickettype.SRR',
    'tickettype.FSO': 'tickettype.SRR',

    // Super Off-Peak Day Return
    'tickettype.SOS': 'Flexible ticket with no time restrictions on when you can travel',
    'tickettype.FOS': 'tickettype.SOS',
    'tickettype.GFS': 'tickettype.SOS',

    // Anytime Day Single
    'tickettype.SDS': 'Flexible ticket with no time restrictions on when you can travel',
    'tickettype.FDS': 'tickettype.SDS',

    // Anytime Return
    'tickettype.SOR': 'Flexible ticket with no time restrictions on when you can travel',
    'tickettype.GOR': 'tickettype.SOR',
    'tickettype.FOR': 'tickettype.SOR',
    'tickettype.GFR': 'tickettype.SOR',

    // Anytime Day Return
    'tickettype.SDR': 'Flexible ticket with no time restrictions on when you can travel',
    'tickettype.GPR': 'tickettype.SDR',
    'tickettype.FDR': 'tickettype.SDR',

    // Day Save
    'tickettype.SDP':
        'A pass available for unlimited travel on Southern services at anytime on the date shown on the ticket',
    'tickettype.SD1':
        'An off-peak pass available for unlimited travel on off-peak Southern services on the date shown on the ticket',
    'tickettype.SD4':
        'An off-peak pass available for 3 or 4 people travelling together (which can be a combination of Adults and Children) unlimited travel on off-peak Southern services on the date shown on the ticket',

    // ITSO product descriptions
    'itso.typ22.ptyp1': 'Season ticket',
    'itso.typ22.ptyp5': 'Flexi Season Pass',
    'itso.typ22.ptyp4': 'keyGo',
    'itso.typ22.ptyp19': 'Travelcard',
    'itso.typ22.ptyp28': 'DaySave',
    'itso.typ22.ptyp29': 'PlusBus',
    'itso.typ22.ptyp30': 'Gateline Pass',
    'itso.typ22.ptyp31': 'Staff Pass',
    'itso.typ23.ptyp1': 'Peak Single',
    'itso.typ23.ptyp2': 'Off-Peak Single',
    'itso.typ23.ptyp3': 'Peak Return',
    'itso.typ23.ptyp4': 'Off-Peak Return',

    'maintenance.title': 'Temporarily down for maintenance',
    'maintenance.heading': 'Temporarily down for maintenance',
    'maintenance.description':
        "Sorry for the inconvenience but we're performing some scheduled maintenance at the moment, and should be back online shortly.",

    'newsletter.unsubscribe.heading': 'Unsubscribe from marketing emails',
    'newsletter.unsubscribe.button': 'Unsubscribe',
    'newsletter.unsubscribe.option.sr-only':
        'Check this box if you would like to unsubscribe from emails with {option}',

    'newsletter.unsubscribed.heading': 'You have been successfully unsubscribed',
    'newsletter.unsubscribed.body':
        'Your changes have been saved. Please allow up to 14 days for any changes to take effect. If you want to get our great offers again, you can change your preferences at anytime',

    'smartcardCTA.message.smartcard':
        'The <{global.key}> <{global.smartcard}> is an alternative to paper tickets that makes it quicker and easier to buy and use tickets.',
    'smartcardCTA.message.keyGo':
        '{keygobrand} allows you to use your <{global.key}> much like Pay As You Go Oyster.{br} It works by charging your online account the best value ticket for the journeys you made that day.',
    'smartcardCTA.message.signup.keyGo': '<{global.keyGo}> sign up',
    'smartcardCTA.button.submit.smartcard': 'Get one now',
    'smartcardCTA.button.submit.keyGo': 'Start using it',

    // Delay Repay
    'delayrepay.error.title': 'We are very sorry.',
    'delayrepay.error.message':
        'An error occurred when trying to complete your request, and this has been logged. Please try again and if you still see this message please visit our { faq }.',
    'delayrepay.error.faq': 'troubleshooting guide',
    'delayrepay.error.description.upload': 'There was a problem uploading "{ file }"',
    'delayrepay.error.description.submit': 'There was a problem submitting your claim details',
    'delayrepay.error.description.realex': 'There was a problem authorising your card',
    'delayrepay.error.description.captcha': 'There was a problem validating your captcha response',
    'delayrepay.step.delayrepay-customer': 'delayrepay.section.customerdetails',
    'delayrepay.step.delayrepay-claims': 'delayrepay.section.claims',
    'delayrepay.step.delayrepay-payment': 'delayrepay.section.payment',
    'delayrepay.step.delayrepay-confirm': 'Done',
    'delayrepay.apply.title': 'Delay Repay',
    'delayrepay.info': 'Welcome to Delay Repay, the compensation scheme for train delays. Please log in to continue.',
    'delayrepay.intro.text':
        'Delay Repay is a national compensation scheme that most train operating companies use to compensate customers for delays. You can claim compensation if your journey is delayed by 15 minutes or more (except when delays are caused by planned engineering work).',
    'delayrepay.moreInfo.text':
        'For more information about the delay repay scheme, please visit our {moreInfo}.',
    'delayrepay.moreInfo.link': 'guide to delay repay',
    'delayrepay.details.myAccount': 'My Account',
    'delayrepay.details.warning':
        'Please go to {myAccount} to make changes to your personal details.',
    'delayrepay.action.uploadsInProgress':
        'Please wait: {uploadsInProgress, plural, =1 {# upload} other {# uploads}} in progress',
    'delayrepay.action.submit': 'Submit',
    'delayrepay.action.carddetails': 'Card details',
    'delayrepay.account.login': 'Log in to Delay Repay',
    'delayrepay.button.start': 'Start application',
    'delayrepay.section.customerdetails': 'Your details',
    'delayrepay.section.claims': 'Claim details',
    'delayrepay.section.claim': 'Claim { index }',
    'delayrepay.section.payment': 'Payment method',
    'delayrepay.section.confirm': 'Confirmation',
    'delayrepay.registration.description':
        'Add a password to create an account saving time the next time you claim delay repay or wish to purchase a ticket',
    'delayrepay.label.address': 'Postal address',
    'delayrepay.label.ticketType': 'Type of ticket',
    'delayrepay.label.price': 'Cost of ticket (e.g. 10.95)',
    'delayrepay.label.ticketNumber': 'Ticket number',
    'delayrepay.placeholder.ticketNumber': '5-digit number',
    'delayrepay.placeholder.issueNumber': 'Ticket number',
    'delayrepay.label.photocardNumber': 'Photocard number',
    'delayrepay.label.smartcardNumber': '<{global.smartcardUppercase}> number',
    'delayrepay.placeholder.smartcardNumber': '(e.g. 633597-0000-0000-0000)',
    'delayrepay.label.contactlessNumber': '<{global.smartcardUppercase}> number',
    'delayrepay.label.date': 'Date of journey',
    'delayrepay.label.issueDate': 'Issue date',
    'delayrepay.label.time': 'Timetabled departure time',
    'delayrepay.label.arrivalDate': 'Arrival date',
    'delayrepay.label.arrivalTime': 'Timetabled arrival time',
    'delayrepay.label.ticketOrigin': 'Origin',
    'delayrepay.label.origin': 'Departing',
    'delayrepay.label.originStation': 'From',
    'delayrepay.label.ticketOriginStation': 'Ticket origin',
    'delayrepay.label.via': 'Via',
    'delayrepay.label.viaStation': 'Changing at',
    'delayrepay.label.destination': 'Arriving',
    'delayrepay.label.destinationStation': 'To',
    'delayrepay.label.ticketDestination': 'Destination',
    'delayrepay.label.ticketDestinationStation': 'Ticket destination',
    'delayrepay.label.reason': 'Reason for claim',
    'delayrepay.label.reasonDetails': 'Please supply further information (max 250 characters)',
    'delayrepay.label.delay': 'Length of delay',
    'delayrepay.label.uploadButton': 'Choose image',
    'delayrepay.label.ticketScan': 'Upload a scanned copy/photo of your ticket',
    'delayrepay.label.statementScan': 'Upload a scanned copy/photo of your statement/receipt',
    'delayrepay.label.previousUpload':
        'I have previously provided you with a copy of my monthly or longer season ticket, that is in date and valid for the journey being claimed for.',
    'delayrepay.label.reimbursementMethod': 'Reimbursement method',
    'delayrepay.ticketType.default': 'Please select type of ticket',
    'delayrepay.ticketType.group.non-season': 'Non season ticket holders',
    'delayrepay.ticketType.group.electronic': 'e-ticket',
    'delayrepay.ticketType.group.season': 'Season ticket holders',
    'delayrepay.ticketType.group.contactless': 'Oyster and contactless card holders',
    'delayrepay.ticketType.single': 'Single journey',
    'delayrepay.ticketType.return': 'Return journey',
    'delayrepay.ticketType.selfprint-single': 'e-ticket single',
    'delayrepay.ticketType.selfprint-return': 'e-ticket return',
    'delayrepay.ticketType.season-weekly': '7 Day',
    'delayrepay.ticketType.season-six-monthly': '6 Monthly',
    'delayrepay.ticketType.season-quarterly': 'Quarterly',
    'delayrepay.ticketType.season-monthly': 'Monthly',
    'delayrepay.ticketType.season-annual': 'Annual',
    'delayrepay.ticketType.season-other': 'Other',
    'delayrepay.ticketType.season-oyster': 'Oyster season',
    'delayrepay.ticketType.contactless-key': '<{global.key}> <{global.smartcardUppercase}>',
    'delayrepay.ticketType.contactless-paygo': 'Oyster Pay as You Go or Contactless',
    'delayrepay.ticketType.contactless-other': 'Other <{global.smartcard}>',
    'delayrepay.ticketType.advance': 'Advance',
    'delayrepay.ticketType.anytime-day-return': 'Anytime day return',
    'delayrepay.ticketType.anytime-day-single': 'Anytime day single',
    'delayrepay.ticketType.anytime-return': 'Anytime return',
    'delayrepay.ticketType.anytime-single': 'Anytime single',
    'delayrepay.ticketType.offpeak-day-return': 'Off-peak day return',
    'delayrepay.ticketType.offpeak-day-single': 'Off-peak day single',
    'delayrepay.ticketType.offpeak-return': 'Off-peak return',
    'delayrepay.ticketType.offpeak-single': 'Off-peak single',
    'delayrepay.ticketType.rover-ranger': 'Rail rover/ranger',
    'delayrepay.station.default': 'Please select...',
    'delayrepay.reason.default': 'Please select...',
    'delayrepay.reason.Delayed Departure': 'Delayed departure',
    'delayrepay.reason.Delayed on Route': 'Delayed en route',
    'delayrepay.reason.Train Diverted': 'Train diverted',
    'delayrepay.reason.Train Cancelled': 'Train cancelled',
    'delayrepay.reason.Train short formed': 'Train short formed',
    'delayrepay.reason.Missed Connection': 'Missed connection',
    'delayrepay.reason.Resubmitted Claim': 'Resubmitted claim',
    'delayrepay.reason.Abandoned Journey': 'Journey abandoned',
    'delayrepay.reason.Strike Action': 'Strike action',
    'delayrepay.reason.Other': 'Other – tell us more about your delay',
    'delayrepay.reasonDetails.Train Cancelled':
        'If the train you intended to catch was cancelled, we will check the next available train you could have taken unless you provide further information',
    'delayrepay.reasonDetails.Resubmitted Claim':
        'Please include your previous claim number for our records',
    'delayrepay.time.default': 'Please select...',
    'delayrepay.time.very-short': '15-29 minutes',
    'delayrepay.time.short': '30-59 minutes',
    'delayrepay.time.medium': '1 hour to 1 hour 59 minutes',
    'delayrepay.time.long': '2 hours or more',
    'delayrepay.charities.texts.railway-children':
        'The Railway Children fights for children at risk of abuse and exploitation on the streets of Africa, India and here in the UK. Their work in this country focuses on those finding themselves in danger in and around the transport network, and makes sure they are kept safe. Using their strong links with rail industry partners and the British Transport Police they race to reach vulnerable young people before an abuser can.',
    'delayrepay.charities.texts.samaritans':
        'Every six seconds somebody contacts Samaritans. The charity responds to more than 5.7 million calls for help a year. Its team of 20,000 specially trained volunteers, based in 201 branches across the UK and Ireland, are there to listen to and support anyone who’s struggling to cope.',
    'delayrepay.charities.texts.nhs':
        'As the National membership organisation for NHS Charities, we represent, champion and support the work of more than 140 member charities. These fantastic NHS Charities give over £1 million everyday to the NHS enhancing patient experience and care.',
    'delayrepay.claim.add': 'Add another claim',
    'delayrepay.claim.clone': 'Copy details from claim 1',
    'delayrepay.claim.remove': 'Remove claim',
    'delayrepay.claim.maxLengthModal':
        'Please only claim for the delay you experienced, as we use live train running information to automate verification of customer claims and the process may take longer if the information you provide is inaccurate. \nWe also reserve the right to take further action if knowingly inaccurate claims are submitted',
    'delayrepay.claim.maxLengthModal.title': 'Please check your delay duration is correct',
    'delayrepay.help.ticketScan.title': 'Sending us a copy of your ticket',
    'delayrepay.help.ticketScan': `A photo or scan of your ticket is required so that we can validate your claim.
                                    Please note that you can only upload one image per single claim, so you will need to scan the tickets onto a single page and upload as one image or add another claim by using the button.
                                    File uploads are limited to 5MB.`,
    'delayrepay.help.statementScan.title': 'Sending us a copy of your statement/receipt',
    'delayrepay.help.statementScan':
        'A photo or scan of your statement/receipt is required so that we can validate your claim.',
    'delayrepay.help.statementScan.season-oyster': `A photo or scan of your Oyster statement/receipt is required so that we can validate your claim.
                                    For Oyster Season you must provide a receipt showing the date of purchase, the price you paid and the zones covered by your Oyster card.
                                    File uploads are limited to 5MB.`,
    'delayrepay.help.statementScan.contactless-paygo': `A photo or scan of your Oyster statement/receipt is required so that we can validate your claim.
                                    For Oyster pay as you go or Contactless you must provide a journey statement.
                                    File uploads are limited to 5MB.`,
    'delayrepay.help.statementScan.contactless-other': `A photo or scan of your contactless statement/receipt is required so that we can validate your claim.
                                    File uploads are limited to 5MB.`,
    'delayrepay.help.ticketNumber.title': 'delayrepay.label.ticketNumber',
    'delayrepay.help.ticketNumber': `Please provide your ticket number to allow us to assess your claim more quickly.
                                    The ticket number is composed of two parts as indicated on the sample images below: - a 5 digit number (this is marked in the sample images in turquoise), and a ticket number (this is marked in dark blue).`,
    'delayrepay.help.ticketNumber.imageHelp':
        'Tickets can come in a number of formats. One of these sample tickets should help you locate the ticket number on your ticket. If you cannot identify your ticket number please type 00000 00000 into the ticket number box fields – but this will slow down the processing of your claim',
    'delayrepay.help.ticketNumber.imageLabel': 'Sample ticket {image} of {total}',
    'delayrepay.help.smartcardNumber.title': '<{global.smartcardUppercase}> number',
    'delayrepay.help.smartcardNumber':
        'Your <{global.smartcardUppercase}> number can be found in the top left corner on the reverse of your <{global.key}> <{global.smartcardUppercase}>.',
    'delayrepay.payment.intro':
        'You have a statutory right to receive your compensation in the same way as you paid for your ticket, however, you are free to choose from any of the options below. Additional information on these options is available on our {delayRepayLink}',
    'delayrepay.payment.methods':
        'Please select your preferred payment method carefully. Once submitted, this cannot be changed.',
    'delayrepay.payment.linkText': 'website',
    'delayrepay.payment.showOtherOptions': 'Show other repayment types',
    'delayrepay.paymentMethod.bacs': 'Electronic bank transfer (BACS)',
    'delayrepay.paymentMethod.e-vouchers': 'eVoucher',
    'delayrepay.paymentMethod.paypal': 'PayPal',
    'delayrepay.paymentMethod.credit/debit card': 'Visa Debit/Credit',
    'delayrepay.paymentMethod.cashable voucher': 'Cashable voucher',
    'delayrepay.paymentMethod.cheque': 'Cheque',
    'delayrepay.paymentMethod.rail-charity': 'Charity',
    'delayrepay.paymentMethod.enableOption': 'to enable this option',
    'delayrepay.paymentMethod.message.longer':
        'This option will take longer to process and send than other payment methods',
    'delayrepay.paymentMethodUnavailable':
        'Apologies, this repayment is not available at the moment, please choose another repayment option',
    'delayrepay.paymentMethodUnavailable.credit/debit card':
        'We have temporarily removed this repayment option due to an issue with the banks and we apologise for any inconvenience. This will be reinstated when the issue has been resolved.',
    'delayrepay.paymentMethodUnavailable.e-vouchers':
        'Apologies, but eVoucher repayment is not available at the moment, please choose another repayment option. We are working to resolve this problem as soon as we can',
    'delayrepay.paymentMethod.amazon-incentives': 'Amazon account balance',
    'delayrepay.paymentMethod.amazon.description':
        'Please login to Amazon and grant access to your amazon email address and customer id, so that we can reimburse your delay repay claim into your Amazon account balance. You have the option to associate these details with your <{app.owner.short}> account to speed up this process in future.',
    'delayrepay.paymentMethod.amazon.login': 'Login with Amazon',
    'delayrepay.paymentMethod.amazon.selectedAccount': 'Payment to Amazon account: {account}',
    'delayrepay.paymentMethod.amazon.useAnother': 'Use a different account',
    'delayrepay.paymentMethod.amazon.required':
        'Please login to Amazon to continue, or choose another payment method',
    'delayrepay.paymentMethod.amazon.saveAccount': 'Remember this for future claims',
    'delayrepay.paymentMethod.amazon.savedAccount':
        'This account is saved in your user preferences. If you do not wish to use this account, you can choose to remove this account from your preferences, or use another account for this claim.',
    'delayrepay.paymentMethod.amazon.forgetSavedAccount': 'Forget saved account',
    'delayrepay.paymentMethod.amazon.confirmForgetSavedAccount':
        'Are you sure you want to remove this Amazon account from your preferences?',
    'delayrepay.paymentMethod.charity.selectCharity':
        'Please select a charity you would like to donate your compensation to.',
    'delayrepay.paymentMethod.charity.required':
        'Please select a charity or choose another payment method',
    'delayrepay.label.sortCode': 'Sort code',
    'delayrepay.placeholder.sortCode': 'e.g, 12-34-56, 10 20 30, or 112233',
    'delayrepay.label.accountNumber': 'Account number',
    'delayrepay.label.paypalEmail': 'PayPal email address',
    'delayrepay.label.agreeTerms':
        'Please tick this box to confirm you are making this claim on your own behalf and that you have made or intended to make the journey you are claiming for.',
    'delayrepay.label.bankMonthly': 'Monthly',
    'delayrepay.label.bankQuarterly': 'Quarterly',
    'delayrepay.label.bankAnnually': 'Annually',
    'delayrepay.label.bankNone': 'Do not bank',
    'delayrepay.text.bankPayment':
        "If you'd like to bank your claim, please tell us when you would like to receive your compensation below.",
    'delayrepay.text.success': 'Your application has been submitted successfully.',
    'delayrepay.text.successConfirmation':
        'You will receive an acknowledgement email within the next {timePeriod}.',
    'delayrepay.button.submitAnother': 'Submit another claim',
    'delayrepay.button.buyTickets': 'Buy tickets',
    'delayrepay.signedup': 'Account created, you are now signed in',
    'delayrepay.upload.success': 'Uploaded',
    'delayrepay.upload.cancel': 'Cancel',
    'delayrepay.upload.remove': 'Remove',
    'delayrepay.upload.retry': 'Try again',
    'delayrepay.error.uploadSize.min': 'Chosen file is too small, please choose another',
    'delayrepay.error.uploadSize.max': 'Chosen file is too large, please choose another',
    'delayrepay.error.fileType': 'Please choose a valid JPG or PNG image, or a PDF',
    'delayrepay.error.upload': 'Upload failed, please choose another image',
    'delayrepay.error.journeyTime':
        'This departure time is in the future. Please select a new time or date',
    'delayrepay.error.arrivalTime':
        'This is before your departure time. Please check your train times',
    'delayrepay.error.destination': 'Please choose a different station to your departure station',
    'delayrepay.error.via.sameAsOrigin':
        'This is the same as your departure station. Please choose another station, or remove this if your journey did not require a change',
    'delayrepay.error.via.sameAsDestination':
        'This is the same as your destination station. Please choose another station, or remove this if your journey did not require a change',
    'delayrepay.error.lengthOfDelay': 'Not valid for claims before {date}',
    'delayrepay.address.default': 'Default',
    'delay-repay.maintenance.description':
        'We are sorry but the Delay Repay form is currently unavailable. We are aware and working to fix the problem as quickly as possible. Please be aware that Customer Relations will not be able to accept claims by phone and we suggest that you try again later. We are sorry for the inconvenience.',

    'auto-dr.account.help-title': 'What is Auto Delay Repay?',
    'auto-dr.account.modal-description': 'What is One Click Delay Repay?',
    'auto-dr.account.modal-subscribe': 'Subscribe to One Click Delay Repay',
    'auto-dr.subscribe.modal-title': 'Sign up to One Click Delay Repay',
    'auto-dr.subscribe.add-journey': 'Add journey',
    'auto-dr.subscribe.add-journey-max': 'You can only add a maximum of {maxJourneys} journeys',
    'auto-dr.subscribe.continue': 'Continue',
    'auto-dr.subscribe.modal-address-title': 'In case we need to contact you by post, please tell us your address.',
    'auto-dr.subscribe.modal-address-subtitle': 'Select one of the options below.',
    'auto-dr.subscribe.modal-address-item.selected': 'Selected address item',
    'auto-dr.subscribe.modal-address-item.select': 'Select this address item',
    'auto-dr.subscribe.modal-address.add-new-address': 'Add new address',
    'auto-dr.subscribe.modal-checkbox-email': 'Email',
    'auto-dr.subscribe.modal-checkbox-app': 'Southeastern app notification',
    'auto-dr.subscribe.modal-checkbox-app-and-email': 'Email and app notification',
    'auto-dr.subscribe.modal-add-another-payment-method': 'Add another repayment method',
    'auto-dr.subscribe.modal-payment': 'How would you like to receive your compensation?',
    'auto-dr.subscribe.compliance': 'Your payment details will be stored in accordance with our Privacy Policy, to allow us to pay your One Click Delay Repay compensation',
    'auto-dr.subscribe.change': 'You can change your repayment method at any time through your account',
    'auto-dr.subscribe.mobile-app-description': 'To get app One Click Delay Repay notifications you will need to download and be logged into the Southeastern App',
    'auto-dr.subscribe.modal-close': 'Close',
    'auto-dr.subscribe.back': 'Back',
    'auto-dr.subscribe.bacs-error': 'The information you have provided appears to be invalid. Please check your details and try again',
    'auto-dr.account.info': `In many cases we can determine when you may have been delayed and create a claim for you. These are stored and shown here in the Auto Delay Repay section of your account. Claims will be visible for a period of {claimWindow} days, unless you choose to click the ‘dismiss’ button. Claims which have already been processed will be greyed out.
        Claims are generated based on your entry and exit taps so delay length should always be correct. In the event that you disagree with the stated delay length, click the claim button where you will be able to change this. You will be prompted to provide an explanation of your delay and all such claims will be referred to an adviser for assessment which will result in your claim taking longer to process.`,
    'auto-dr.account.subscription.title': 'Notification settings',
    'auto-dr.account.optout': 'You have been opted out of One Click Delay Repay',
    'auto-dr.account.subscription.info':
        'Whenever we determine you have been delayed, we can send you an email alert for free.',
    'auto-dr.account.subscribed':
        'We will alert you at {emailAddress} when we generate a new Auto Delay Repay claim',
    'auto-dr.account.unsubscribed':
        'You are not currently subscribed to the Auto Delay Repay service',
    'auto-dr.account.subscribe.claims.history.text': 'Check your Delay Repay claim history to see if you have any One Click claims',
    'auto-dr.account.subscribe.claims.history.button': 'Claim history',
    'auto-dr.account.subscribe.claims.history.opt-out-1': 'If you no longer want to receive One Click Delay Repay claims, you can',
    'auto-dr.account.subscribe.claims.history.opt-out-2': 'opt out',
    'auto-dr.account.subscribe.claims.history.opt-out-error': 'There was an error attempting to opt out of One Click Delay Repay',
    'auto-dr.account.add.journey': 'Add regular journey',
    'auto-dr.journey.heading': "Add this journey to One Click Delay Repay",
    'auto-dr.journey.info': "To help determine which service you have been travelling on, please provide us with the Southeastern journey(s) that you frequently make.",
    'auto-dr.unsubscribed.text': 'One Click Delay Repay is a quicker and easier way to claim compensation if your journey has been delayed. It’s available to all our Key Smartcard customers and anyone who buys an eTicket or a paper Advance ticket through our website or app, when travelling on the Southeastern network.',
    'auto-dr.account.booking.unsubscribed.title': 'One Click Delay Repay',
    'auto-dr.account.booking.unsubscribed': 'Sign Up',
    'auto-dr.account.subscribe': 'Sign up',
    'auto-dr.account.unsubscribe': 'Opt out',
    'auto-dr.account.subscribe.button': 'Add journey',
    'auto-dr.account.login': 'Log in to One Click Delay Repay',
    'auto-dr.subscribe.heading': 'Sign up to One Click Delay Repay',
    'ddm.notify.text': 'Your preference will be saved for all journey alerts. This can be changed at any point through your account.',
    'auto-dr.notify.heading': 'How would you like to receive your One Click Delay Repay claims?',
    'ddm.notify.heading': 'How would you like to be notified of disruption?',
    'ddm.account.subscribe.journeys.modal.save': 'Save Alert',
    'auto-dr.account.subscribe.heading': 'Your One Click Delay Repay Journeys',
    'auto-dr.account.subscribe.address.title': 'Address',
    'auto-dr.account.address.edit.button': 'Edit address',
    'auto-dr.account.repayment.modal.title': 'Change reimbursement method',
    'auto-dr.account.notifications.modal.title': 'Change notification preferences',
    'auto-dr.account.repayment.method': 'You have chosen to receive email notifications:',
    'auto-dr.account.repayment.method.button': 'Change repayment method',
    'auto-dr.account.repayment.method.error': 'Error saving chosen reimbursement method',
    'auto-dr.account.repayment.method.name': 'You have chosen to receive One Click Delay Repay compensation by {method}',
    'auto-dr.account.repayment.method.bacs': 'BACS',
    'auto-dr.account.repayment.method.paypal': 'PayPal',
    'auto-dr.account.repayment.method.e-vouchers': 'e-Vouchers',
    'auto-dr.account.repayment.method.sort-code': 'Sort code',
    'auto-dr.account.repayment.method.account-number': 'Account number',
    'auto-dr.account.repayment.method.paypal-email-address': 'PayPal email address',
    'auto-dr.account.repayment.method.charity': 'You have chosen to donate your One Click Delay Repay compensation to the charity: {charityName}',
    'auto-dr.account.arrows': 'arrows',
    'auto-dr.account.journey.title': 'Saved journeys:',
    'auto-dr.account.journey.delete': 'Delete journey',
    'auto-dr.account.journey.edit': 'Edit journey',
    'auto-dr.account.journey.heading': 'Your regular journeys',
    'auto-dr.account.journey.action-summary-text': 'journey from ${journeyOriginName} to ${journeyDestinationName} that you regularly take on ${uniqueFormattedDays} at ${uniqueSortedServiceTimes}',
    'auto-dr.account.journey.delete-aria-text': 'Delete {a11yString}',
    'auto-dr.account.journey.edit-aria-text': 'Edit {a11yString}',
    'auto-dr.account.journey.max-favourite-journeys': 'You have reached the maximum journeys allowed. Please edit or delete your saved regular journeys to be able to add more journeys.',
    'auto-dr.account.journey.help-text': 'Entering regular journeys helps us to determine which trains you travel on.',
    'auto-dr.subscribe.info': 'Whenever we determine that you have been delayed by 15 mins or more when travelling on a Southeastern service, we can send you an alert for free. We can send this alert via email or through the Southeastern app.',
    'auto-dr.account.subscribe.info': 'To help determine which service you have been travelling on, please provide us with the Southeastern journey(s) that you frequently make. ',
    'auto-dr.account.subscribe.info.bold': 'If you do not provide your Southeastern journeys, we may be unable to create One Click Delay Repay claims when you have been delayed on our services.',
    'auto-dr.account.subscribe.journeys.modal.error': 'Please select at least one day of the week you travel on',
    'journey.modal.travel.table.from': 'From',
    'journey.modal.travel.table.departureTime': 'Departing',
    'journey.modal.travel.table.to': 'To',
    'journey.modal.travel.table.duration': 'Duration',
    'journey.modal.travel.table.changes': 'Changes',
    'journey.modal.travel.table.selected': 'Select journey',
    'journey.modal.travel.table.selected.mobile': 'Select',
    'auto-dr.account.subscribe.journeys.modal.title': 'Add your regular journey',
    'auto-dr.account.subscribe.journeys.modal.save': 'Save Journey',
    'auto-dr.account.subscribe.journeys.modal.info': 'Enter your journey information',
    'auto-dr.account.subscribe.journeys.modal.usually': 'What days do you usually make this journey?',
    'auto-dr.account.subscribe.journeys.modal.departing': 'Journeys departing around',
    'auto-dr.account.subscribe.journeys.modal.labels.weekday': 'Weekday',
    'auto-dr.account.subscribe.journeys.modal.labels.time': 'Time departing',
    'auto-dr.account.subscribe.journeys.add': 'Add a',
    'auto-dr.account.subscribe.journeys.addJourney': 'Add a {weekday} journey',
    'auto-dr.account.subscribe.journeys.departingAround': 'Search for journeys departing around',
    'auto-dr.account.subscribe.journeys': 'journey',
    'auto-dr.account.subscribe.journeys.modal.no-compatible-journeys': 'No compatible journeys found',
    'auto-dr.account.subscribe.journeys.modal.loading-journeys': 'Loading journeys',
    'auto-dr.account.subscribe.journeys.modal.travel.from-to-aria-label': '{departing} from {from} to {to}, duration {duration}, changes {changes}',
    'auto-dr.account.subscribe.journeys.modal.travel': 'Select the journeys you travel on',
    'auto-dr.account.subscribe.journeys.modal.travel.selected': '{journeysQuantity}/{maxJourneys} journeys selected',
    'auto-dr.account.subscribe.journeys.modal.travel.return.journey': 'If you wish to set up the return journey, this must be added as an individual journey.',
    'auto-dr.account.subscribe.info.limit.journeys': 'You can set up {maxSavedAddresses} journeys per day. Return journeys must be added as individual journeys. You have reached the maximum journeys allowed. Please edit or delete your saved One Click Delay Repay journeys to be able to add more journeys',
    'auto-dr.account.subscribe.info.regularJourneyAlert': 'Set up regular journey alert',
    'auto-dr.account.subscribe.info.includeStationFacilityAlerts': 'Include station facility alerts',
    '': '',
    'auto-dr.subscribe.confirm':
        'Do you want to subscribe to emails notifying you of new One Click Delay Repay claims?',
    'auto-dr.subscribe.button': 'Subscribe now',
    'auto-dr.subscribe.success.heading': 'You are now subscribed',
    'auto-dr.subscribe.success.message': 'We hope you find this service useful.',
    'auto-dr.subscribe.success.changeOfMind':
        'If you change your mind and wish to unsubscribe from One Click Delay Repay notification emails you can',
    'auto-dr.unsubscribe.heading': 'Unsubscribe from One Click Delay Repay',
    'auto-dr.unsubscribe.confirm':
        'Do you want to unsubscribe from emails notifying you of new One Click Delay Repay claims?',
    'auto-dr.unsubscribe.button': 'Unsubscribe now',
    'auto-dr.unsubscribe.success.heading': 'You are now unsubscribed',
    'auto-dr.unsubscribe.success.message': 'We are sorry to see you go.',
    'auto-dr.unsubscribe.success.changeOfMind':
        'If you change your mind and wish to sign up to One Click Delay Repay notification emails you can',
    'auto-dr.subscription.signupAccountSettings.loggedOut':
        'to change your preferences in the One Click Delay Repay section in My Account',
    'auto-dr.subscription.signupAccountSettings.loggedIn': 'change your preferences in',
    'auto-dr.claims.title': 'Claim notifications',
    'auto-dr.claims.summary': 'You have {totalClaims} with a total value of {value}',
    'auto-dr.claims.zeroTotalValue.summary.1Paragraph': 'As keyGo weekly capping may be applied to your {journeyOrJourneys} this week we cannot provide a compensation value at this time. This is due to the cost of your travel becoming less expensive the more you travel within a specified time. This also means we cannot easily confirm the value of your Delay Repay claim until the monitoring period has elapsed as that amount will also change as it is linked to the cost of your travel.',
    'auto-dr.claims.zeroTotalValue.summary.2Paragraph': 'Your claim will take longer than usual to fully process because we have to wait for the monitoring period to end. You can find more information {here}.',
    'auto-dr.claims.zeroTotalValue.summary.2Paragraph.noHereLink': 'Your claim will take longer than usual to fully process because we have to wait for the monitoring period to end.',
    'auto-dr.claims.count': '{totalClaims, plural, =1{1 claim} other{# claims}}',
    'auto-dr.claims.expiry': 'Your oldest claim will expire {expiry}',
    'auto-dr.claims.no-claims': 'You have no pending Auto Delay Repay claims.',
    'auto-dr.claims.loading-error': 'There was an error loading claim notifications.',
    'auto-dr.claims.retry': 'Retry',
    'auto-dr.claims.pagination':
        'Showing {count} of {totalClaims, plural, =1{1 claim} other{# claims}}',
    'auto-dr.claims.more': 'Load more',
    'auto-dr.claim.journey.title': '{journeyDate} at {journeyTime}',
    'auto-dr.claim.delay.length': '{length} mins',
    'auto-dr.claim.delay.reason': '{reason}',
    'auto-dr.claim.expires': 'Expires {date}',
    'auto-dr.claim.expired': 'Expired {date}',
    'auto-dr.claim.button.process': 'Claim',
    'auto-dr.claim.button.dismiss': 'Dismiss',
    'auto-dr.claim.notification.submitted': 'Claim submitted',
    'auto-dr.claim-form.title': 'Submit claim',
    'auto-dr.claim-form.button.submit': 'Submit claim',
    'auto-dr.claim-form.step.review': 'Review your claim',
    'auto-dr.claim-form.step.contact': 'Contact details',
    'auto-dr.claim-form.step.reimbursement': 'Reimbursement method',
    'auto-dr.claim-form.editWarning': `Your claim is pre-approved for a delay of {length} minutes.
                                    If you want to change the length of the delay, please give us more information about your delay, so we can assess the validity of the claim.`,
    'auto-dr.claim-form.delayLength.edit': 'Change delay length',
    'auto-dr.claim-form.delayLength.choose': 'Please choose...',
    'auto-dr.claim-form.delayLength.cancel': 'Cancel editing',
    'auto-dr.claim-form.address.edit': 'Edit',
    'auto-dr.claim-form.reimbursementMethod.choose': 'Please choose...',
    'auto-dr.claim-form.reimbursementMethodUnavailable.credit/debit card':
        'We have temporarily removed the Debit/Credit card repayment option due to an issue with the banks and we apologise for any inconvenience. This will be reinstated when the issue has been resolved.',
    'auto-dr.claim-form.sortCode': 'Sort code (e.g, 12-34-56, 10 20 30)',
    'auto-dr.claim-form.sortCodeAriaLabel': 'Sort code {sortCode}',
    'auto-dr.claim-form.sortCodeLabel': 'Sort code',
    'auto-dr.claim-form.sortCodePlaceholder': 'e.g. 123456',
    'auto-dr.claim-form.accountNumber': 'Account number',
    'auto-dr.claim-form.accountNumberAriaLabel': 'Account number {accountNumber}',
    'auto-dr.claim-form.accountNumberLabel': 'Account number',
    'auto-dr.claim-form.accountNumberPlaceholder': 'e.g 12345678',
    'auto-dr.claim-form.error': 'An error occurred, please try again',
    'auto-dr.dismiss.title': 'Dismiss claim',
    'auto-dr.dismiss.confirm': 'Are you sure you would like to dismiss this claim?',
    'auto-dr.dismiss.error': 'An error occurred, please try again',
    'auto-dr.claim.notification.dismissed': 'Claim dismissed',

    'one-click-delay-repay.optIn.success.header': 'You are now signed up to One Click Delay Repay',
    'one-click-delay-repay.optIn.success.copy': `If you have a Key Smartcard ticket or travel with eTickets, please tell us your regular journeys to help us determine which trains you travel on. Just click the “Add journey” button below.\nPlease remember to tap in and out where possible with your Key Smartcard or scan your eTicket barcode, as we will need to see at least one tap or scan to see you’ve travelled.\nIf you have an Advance ticket, please travel on the train you’ve booked for. If it’s delayed by 15 minutes or more, we’ll notify you of a One Click claim.`,

    'one-click-delay-repay.paymentMethods.sortCode.length': 'Sort code must be 6 characters.',
    'one-click-delay-repay.paymentMethods.accountNumber.length': 'Account number must be 8 characters',

    'one-click-delay-repay.error-setting-up-regular-journey': 'Unfortunately there was an error when setting up your {alert}',

    'compensation.title': 'Additional Compensation for Thameslink and Great Northern customers',
    'compensation.section.customer': 'Start your claim',
    'compensation.section.address': 'Your address',
    'compensation.section.routes': 'Tell us about your tickets',
    'compensation.section.tickets': 'Tell us about your tickets',
    'compensation.section.amount': 'Check your claim amount',
    'compensation.section.payment': 'Confirm your payment method',
    'compensation.section.done': 'Done',
    'compensation.next': 'Continue',
    'compensation.tickets.next': 'Continue to payment',
    'compensation.help.title': `What you'll need to complete your claim`,
    'compensation.help.season.text':
        'When making your application, you will need to provide as much information as possible about qualifying season tickets you held between 20th May and 28th July 2018. This may include:',
    'compensation.help.season.checklist': `A scan or photograph of your season ticket(s) and photocard
        The ticket receipt (if you no longer have your ticket)
        Great Northern, Southern, Thameslink Key smartcard number (found on the front of your smartcard)
        For a Smartcard issued by another operator; your Smartcard number and an upload of a receipt or booking confirmation showing details of the tickets held on your smartcard
        Oyster season ticket: a receipt showing the date of purchase, the price you paid and the zones covered by your Oyster card, plus your travel statement. This must be scanned onto a single attachment per ticket and uploaded
        Contactless statement showing weekly caps
        Where you normally travel from and to
        How you purchased your ticket and where you purchased it from`,
    'compensation.help.season.footnote':
        '<p>We understand that you might not have all this information available but by providing as much evidence as possible, this will help us to verify your claim as quickly as possible. If you need any assistance, please contact us at 0345 026 4700.',
    'compensation.season.preamble.title': 'Am I eligible?',
    'compensation.season.preamble': 'Please go to our {link} to confirm that you are eligible',
    'compensation.season.preamble.linkText': 'eligibility checker',
    'compensation.nonSeason.login.savedSession':
        'A partial claim is saved on this device. It will expire {timeToExpire}',
    'compensation.nonSeason.requiresLogin':
        'Use your existing online login details which you would normally use to buy a ticket or make a delay repay claim. If you don’t have an account already, you need to register here.',
    'compensation.help.nonSeason.text': `Please supply as much information as you can about your journeys between 20 May and 28 July 2018. This may include uploading attachments to support proof of travel, which must be <u>no more than 5mb each</u>.
        You can also give us further details to support your claim, by selecting ‘Other evidence’ which gives you the option to provide us with further written information.
        You may need to provide proof of travel for each journey so we suggest you have this information to hand before beginning your claim. You will have the option to save a partially completed claim and log back in within 1 week to finish it but this must be on the same device you began the claim on.
        The information you may need to provide includes:`,
    'compensation.help.nonSeason.checklist': `Where you travel from and to
        A scan or photograph of your ticket(s)
        The ticket receipt (if you no longer have your ticket)
        Oystercard number and journey statements (optional)
        Contactless journey statement
        Redacted bank statement showing relevant purchases
        Details about where you bought your ticket from`,
    'compensation.help.nonSeason.footnote':
        '<p>We understand that you might not have all this information available but by providing as much evidence as possible, this will help us to assess your claim as quickly as possible. If you are concerned that you do not have the required evidence to support your travel over the qualifying period please visit our {faqLink}. If you believe you’re eligible but don’t have all the evidence, we encourage you to still make a claim. If you need any assistance, please contact us at {phoneNumber}.</p>',
    'compensation.nonSeason.preamble.title': 'Am I eligible?',
    'compensation.nonSeason.preamble': 'Please go to our {link} to confirm that you are eligible',
    'compensation.nonSeason.preamble.linkText': 'eligibility checker',
    'compensation.no-code.whitelist.text': `If you don't have an email or letter containing a code, but you believe you are entitled to compensation you can apply online`,
    'compensation.no-code.whitelist.linkText': 'when the scheme opens fully',
    'compensation.no-code.season.text': `If you don't have an email or letter containing a code, but you believe you are entitled to compensation`,
    'compensation.no-code.season.linkText': 'you can still apply online',
    'compensation.help.whitelist.checklist': `Email or letter stating compensation
        Your unique code reference
        Your photocard`,
    'compensation.whitelist.preamble':
        'If you have been directed to this page, it is because we have been able to confirm that you are eligible for compensation under this scheme.',
    'compensation.whitelist.no-code': `I don't have a unique code`,
    'compensation.whitelist.text':
        'Please fill in the following details, so that we can find your pre-approved claim.',
    'compensation.whitelist.photocardNumber':
        'Photocard number is required if you received your passcode in a letter',
    'compensation.customer.text':
        'In order to process your claim efficiently, please supply as much information as you possibly can. This will speed up the time taken to process your claim.',
    'compensation.amount.calculation':
        'We have automatically calculated your compensation amount at:',
    'compensation.amount.evidence':
        'This is based upon the eligibility of your season ticket(s) during the scheme period. See {link} for more information',
    'compensation.amount.evidence.linkText': 'our FAQ',
    'compensation.payment.text': 'Please choose your preferred repayment method',
    'compensation.done.title': 'Thank you',
    'compensation.done.whitelist':
        'There’s nothing else you need to do, we’re just making some final checks and then we will be able to confirm your compensation payment which we’ll do by email in the next 24 hours.',
    'compensation.done.season': `What's next...
        We will review your claim within 20 working days. You will receive an additional email once your claim has been assessed to advise you of the outcome.`,
    'compensation.done.nonSeason': 'compensation.done.season',
    'compensation.done.text': `If you require any further assistance, please contact a member of our team on {customerServicesPhoneNumber}.
        For more information about the Additional Industry Compensation Scheme, please visit {compensationLink}`,
    'compensation.done.linkText': 'our dedicated website',
    'compensation.route.instructions':
        'Please enter the origin and destination stations displayed on your ticket. If your ticket is a travelcard, please choose the stations you usually travel between.',
    'compensation.route.validate': 'Validate route',
    'compensation.route.validate.reminder':
        'Please validate that this route is eligible for compensation',
    'compensation.route.delete.confirm': 'Are you sure you wish to delete this route?',
    'compensation.routeCode.default': `Please select the route`,
    'compensation.routeCode.label': `Route code`,
    'compensation.eligiblity.none':
        'Unfortunately this route does not qualify for additional compensation',
    'compensation.eligiblity.QUALIFIES_WEEKLY_CAP':
        'This route is eligible for compensation of 1 week',
    'compensation.eligiblity.QUALIFIES_MONTHLY_CAP':
        'This route is eligible for compensation of up to 4 weeks',
    'compensation.route.add': 'Add another route',
    'compensation.route.addTicket': 'Add a ticket to this route',
    'compensation.placeholder.compensationCode': 'Your unique code',
    'compensation.placeholder.ticketNumber': '5-digit Ticket number',
    'compensation.placeholder.ticketNumber.caption':
        'If available please provide the number of one of your Carnet tickets.',
    'compensation.help.ticketNumber.imageHelp':
        'Tickets can come in a number of formats. One of these sample tickets should help you locate the ticket number on your ticket.',
    'compensation.help.ticketNumber': `Please provide your ticket number to allow us to assess your claim more quickly.`,
    'compensation.placeholder.photocardNumber': 'Photocard number',
    'compensation.placeholder.smartcardNumber': '<{global.theKey}> <{global.smartcard}> number',
    'compensation.placeholder.contactlessNumber': 'Smartcard number',
    'compensation.placeholder.oysterNumber': 'Oyster card number',
    'compensation.label.oysterNumber': 'Oyster card number',
    'compensation.help.ticketNumber.title': 'compensation.placeholder.ticketNumber',
    'compensation.label.purchaseLocation': 'Place of purchase',
    'compensation.label.purchaseLocationStationStation': 'Station',
    'compensation.label.originStation': 'Leaving from',
    'compensation.label.destinationStation': 'Going to',
    'compensation.label.travelcardStation': 'Travelcard zones',
    'compensation.travelcardStation.default': 'Please choose zones...',
    'compensation.label.weeklyCap': 'Weekly cap',
    'compensation.weeklyCap.default': 'Please choose zones...',
    'compensation.placeholder.purchaseLocationOther': 'Please specify',
    'compensation.purchaseLocation.default': 'Place of purchase',
    'compensation.purchaseLocation.ticket-office': 'Station',
    'compensation.purchaseLocation.online': '<{app.owner}> website',
    'compensation.purchaseLocation.other': 'Somewhere else',
    'compensation.placeholder.date.validFrom': 'Start date',
    'compensation.placeholder.date.validUntil': 'Valid until',
    'compensation.label.ticket': 'Ticket #{number}',
    'compensation.label.price': 'Ticket cost (e.g. 10.95)',
    'compensation.ticket.add': 'Add ticket to this route',
    'compensation.label.ticketType': 'Type of ticket',
    'compensation.ticketType.default': 'Please select type of ticket',
    'compensation.ticketType.season-standard-class': 'Standard class paper ticket',
    'compensation.ticketType.season-first-class': 'First class paper ticket',
    'compensation.ticketType.season-oyster': 'Oyster season',
    'compensation.ticketType.season-oyster.callToAction':
        'By providing your Oystercard number you consent to GTR checking TfL data to validate the journeys made and cost of travel.',
    'compensation.ticketType.contactless-key':
        '<{global.key}> <{global.smartcardUppercase}> (Thameslink, Great Northern & Southern only)',
    'compensation.ticketType.contactless-paygo': 'Weekly Capped Contactless',
    'compensation.ticketType.contactless-other': 'Other smartcard',
    'compensation.label.duration': 'Season duration',
    'compensation.duration.default': 'Please select duration',
    'compensation.duration.weekly': 'Weekly',
    'compensation.duration.monthly': 'Monthly',
    'compensation.duration.annual': 'Annual',
    'compensation.duration.custom': 'Other',
    'compensation.label.months': 'Months',
    'compensation.label.days': 'Days',
    'compensation.upload.hint.season-oyster':
        'Oyster season ticket holders – you will need to provide a receipt showing the date of purchase, the price you paid and the zones covered by your Oyster card, plus your travel statement. This must be scanned onto a single attachment per ticket and uploaded',
    'compensation.label.ticketUpload': 'Upload a scanned copy/photo of your ticket',
    'compensation.label.statementUpload': 'Upload a scanned copy/photo of your statement/receipt',
    'compensation.label.skipFileUpload': `I cannot upload a scan because I no longer have this ticket`,
    'compensation.label.agreeTerms':
        'We reserve the right to check the validity of all claims we receive and we treat seriously any applications we believe may be fraudulent. We may take further action if appropriate. This could include, making further checks, refusing a claim or seeking recovery of any sums paid in error.',
    'compensation.alert.customerValidation': `Our records show that you have been pre-approved for additional compensation - you will have been notified by email or letter. If you've not received either of these please call us on 0345 026 4700`,
    'compensation.alert.whitelistValidation':
        'We cannot find your pre-approved claim using the details you have entered, or you may have submitted a claim previously, with the same credentials. Please check to ensure you have entered your passcode correctly and you are using the same email address that your claim details were sent to.',
    'compensation.alert.paymentMethod': 'Please choose a method of reimbursement',
    'compensation.alert.customDuration': 'Please specify duration length (min. 1 month)',
    'compensation.alert.eligibleDuration':
        'This ticket is not eligible for compensation as the ticket was not valid during the compensation period',
    'compensation.alert.needsMoreEvidence':
        'In order for us to process your application we have to have at least {expectedDays} days of ticket history for you, currently you have only told us about {actualDays} days worth. Please add more tickets',
    'compensation.ticket.delete.confirm': 'Are you sure you wish to delete this ticket?',
    'compensation.ticket.uploading': 'Uploading...',
    'compensation.ticket.uploadFailed': 'Upload failed',
    'compensation.alert.isUploading':
        'Image {count, plural, =1{upload} other{uploads}} in progress. Please wait for {count, plural, =1{upload} other{uploads}} to complete before continuing',
    'compensation.alert.illegalTicket':
        'You have entered a photocard number ({photocardNumber}) which has been used in a previous claim. Please correct before proceeding',
    'compensation.alert.hasFailedUploads':
        '{count, plural, =1{# image upload} other{# image uploads}}. Please check and try again',
    'compensation.whitelist.message.schemeClosed': `
            <p>This scheme has now closed for applications.
            If you have a claim in progress, this will be completed within the timescales advised.
            If you require an update, please contact the team on 0345&nbsp;026&nbsp;4700&nbsp;(option&nbsp;1)</p>
            <p>For further details about the scheme, please visit <a href="{schemeUrl}">{schemeUrl}</a></p>`,
    'compensation.season.message.schemeClosed': 'compensation.whitelist.message.schemeClosed',
    'compensation.nonSeason.message.schemeClosed': 'compensation.whitelist.message.schemeClosed',
    'compensation.evidenceType.label': 'Evidence type',
    'compensation.evidenceType.default': 'Choose evidence type',
    'compensation.evidenceType.ticket-mixture':
        'Ticket combination (of Carnet and a different ticket type)',
    'compensation.evidenceType.ticket-image': 'Original ticket',
    'compensation.evidenceType.receipt': 'Ticket receipt',
    'compensation.evidenceType.bank-statement': 'Redacted bank statement',
    'compensation.evidenceType.bank-statement.description':
        'Please remove any sensitive information by striking it out in marker pen. We just need to be able to see your name and the relevant purchases made for tickets to assess your claim.',
    'compensation.evidenceType.journey-history': 'Oyster/CPAY journey statement',
    'compensation.evidenceType.freedom-pass-image': 'Freedom pass picture/Scan',
    'compensation.evidenceType.further-information': 'Other evidence',
    'compensation.evidenceType.further-information.description':
        'Please upload the evidence you have available to support your claim and provide a short explanation in the freetext box of what you are providing and why.',
    'compensation.evidenceType.no-evidence': 'No evidence',
    'compensation.evidenceType.no-evidence.description':
        'If you are unable to provide sufficient evidence of your travel during the qualifying period, please provide us with as much information as you can including evidence of more recent regular journeys that correspond to the journeys you are claiming for.',
    'compensation.nonSeason.travelWeek.heading': 'Week #{index}',
    'compensation.nonSeason.travelWeek.choose': 'Travel week',
    'compensation.nonSeason.travelWeek.range': '{from} - {to}',
    'compensation.nonSeason.travelWeek.chooseButton': 'Choose',
    'compensation.nonSeason.travelWeek.incomplete':
        'This week is incomplete. Please click to expand this week and make the necessary amendments',
    'compensation.nonSeason.travelWeek.instructions':
        'You must evidence a minimum of 3 return journeys in any one week (Monday-Sunday) during the qualifying period. If you use a Carnet ticket please select the date of purchases/first day of use.',
    'compensation.nonSeason.travelWeek.add': 'Add another week',
    'compensation.nonSeason.travelWeek.delete': 'Delete this week and all tickets',
    'compensation.nonSeason.travelWeek.confirmDelete': `
            Are you sure you wish to delete this week?
            {numDays, plural,
                =0 {}
                =1 {It has tickets assigned to 1 day.}
                other {It has tickets assigned to # days.}
            }`,
    'compensation.nonSeason.travelWeek.full.header': 'This week is full',
    'compensation.nonSeason.travelWeek.journeys.required':
        'You need {days, plural, =1 {1 more day} other {# more days}} worth of journey details.',
    'compensation.nonSeason.travelWeek.full': 'You can not add any more tickets this week.',
    'compensation.nonSeason.dayPicker.choose': 'Choose a date',
    'compensation.nonSeason.dayPicker.manage': 'Add / edit ticket',
    'compensation.nonSeason.dayPicker.label':
        '{hasTickets, select, true {View/edit tickets on} other {Add tickets to}} {date}',
    'compensation.nonSeason.ticketModal.header': 'Ticket details',
    'compensation.nonSeason.ticketModal.confirmDelete':
        'Are you sure you want to remove these tickets?',
    'compensation.nonSeason.ticketType.single': 'Paper ticket (single)',
    'compensation.nonSeason.ticketType.return': 'Paper ticket (return)',
    'compensation.nonSeason.ticketType.carnet': 'Carnet - Book of 5',
    'compensation.nonSeason.ticketType.freedom-pass': 'Freedom Pass',
    'compensation.nonSeason.ticketType.contactless-key':
        '<{global.key}> <{global.smartcardUppercase}>/<{global.keyGo}> (Thameslink, Great Northern & Southern only)',
    'compensation.nonSeason.ticketType.contactless-paygo': 'TfL Oyster pay as you go',
    'compensation.nonSeason.ticketType.contactless-other': 'Contactless payment card',
    'compensation.nonSeason.ticketType.single.short': 'Single',
    'compensation.nonSeason.ticketType.return.short': 'Return',
    'compensation.nonSeason.ticketType.contactless-key.short':
        '<{global.key}> <{global.smartcardUppercase}>/<{global.keyGo}>',
    'compensation.nonSeason.ticketType.contactless-paygo.short': 'Oyster',
    'compensation.nonSeason.ticketType.contactless-other.short': 'Contactless',
    'compensation.nonSeason.ticketType.contactless-paygo.callToAction':
        'compensation.ticketType.season-oyster.callToAction',
    'compensation.nonSeason.outward.title': 'Ticket details for {date}',
    'compensation.nonSeason.return.title': 'Return journey',
    'compensation.nonSeason.return.optional':
        'Please provide details about your return journey if it was not using {ticketType}.',
    'compensation.nonSeason.return.add': 'Add return ticket',
    'compensation.nonSeason.ticket.delete': 'Delete tickets',
    'compensation.routeSelector.label': 'Route',
    'compensation.routeSelector.default': 'Please select a route',
    'compensation.routeSelector.other': 'Other',
    'compensation.routeSelector.route': '{origin} to {destination}',
    'compensation.routeSelector.swapStations': 'Swap stations',
    'compensation.nonSeason.ticketEvidence.uploadButton': 'Upload evidence',
    'compensation.nonSeason.ticketEvidence.notRequired':
        'No evidence is required for this ticket type',
    'compensation.nonSeason.ticketEvidence.skipUpload':
        'I have already uploaded this for another day this week',
    'compensation.nonSeason.ticketEvidence.comments':
        'Please supply further information (max 500 characters)',
    'compensation.nonSeason.ticketEvidence.comments.placeholder':
        'Briefly explain what information you are supplying to help us assess your claim: Max 500 characters',
    'compensation.ticket.warning.route.nonCircular': `The return portion single ticket should be the reverse of your outward journey. You will need to provide us with an explanation of your journey pattern using the 'Other evidence' option to help us assess your claim`,
    'compensation.ticket.error.exceedLevel2':
        'Level 2 stations are only eligible for 1 week of compensation. As you have claimed for a level 2 station in a previous week, this is the maximum allowed',
    'compensation.ticket.error.returnMismatch.route':
        'The return carnet ticket should be the reverse of your outward journey',
    'compensation.ticket.error.returnMismatch.eligibility':
        'This journey has a different compensation level to your outward journey. To qualify, both journeys must be the same compensation level',
    'compensation.ticket.error.ticketType.carnet':
        'A carnet must be the only ticket in a week. Please remove other tickets before adding this ticket',
    'compensation.ticket.error.ticketType':
        'This ticket type must be the only ticket in a week. Please remove other tickets before adding this ticket',
    'compensation.ticket.error.maxBookSize':
        'You have already added the maximum number of carnet tickets',
    'compensation.resumeSession.title': 'Resume session?',
    'compensation.resumeSession.description':
        'We have an incomplete claim which was started by another user on this device. If you would like to continue that application, please log out and log back in with the correct user account. Or you can start a fresh application - this will clear the saved application',
    'compensation.resumeSession.abort': 'Start fresh application',
    'compensation.resumeSession.resume': 'Log out',
    'compensation.resumeSession.modal.title': 'Claim saved for later',
    'compensation.resumeSession.modal.heading': 'You have logged out',
    'compensation.resumeSession.modal.message':
        'Your claim will be saved for 1 week. You must log back in on the same device to complete your application',

    'ontrackapp.widget.title': 'Meet the On Track App',
    'ontrackapp.widget.desc': 'Download the fastest, easiest and smartest train assistant.',
    'ontrackapp.widget.appstoreButtonAltText': 'App Store logo',
    'ontrackapp.widget.googlePlayButtonAltText': 'Google Play Store logo',
    'ontrackapp.widget.smartphoneImgAltText': '{brand} mobile app screenshot',

    'tutorial.dismiss-checkbox-label': "Don't show me again",

    'jp-list-tutorial.trigger': 'Open List view tutorial',
    'jp-list-tutorial.intro.title': 'Quick Tutorial',
    'jp-list-tutorial.intro.text':
        "Welcome to our website tutorial, it aims to help you find the best deal using our new and improved features. Please click '<{button.text.next-tip}>' to continue, or '<{button.text.close}>' to skip this tutorial.",
    'jp-list-tutorial.tabs.title': 'Two New Views',
    'jp-list-tutorial.tabs.text':
        'We have two new views that you can use to buy tickets on, a List View and a Grid View. Check them out like this. We will remember the last view you used and show you that view again next time!',
    'jp-list-tutorial.list-view.title': 'The List View',
    'jp-list-tutorial.list-view.text':
        'This is the List View, where you have each step of the service and ticket selection process presented to you, a screen at a time.',
    'jp-list-tutorial.toc-filter.title': 'Cheapest Fares',
    'jp-list-tutorial.toc-filter.text':
        'Clicking this button enhances your journey search, to display the best priced fares on London Midland trains only. De-select the button to view all operator fares available for that journey.',
    'jp-list-tutorial.select-service.title': 'Selecting a Service',
    'jp-list-tutorial.select-service.text':
        'You start by selecting the service you want to travel on. You can use the “Show earlier” and “Show later” links to see more services.',
    'jp-list-tutorial.service-info.title': 'Service Details',
    'jp-list-tutorial.service-info.text':
        'Here, you can see detailed information about each service, like calling points, interchanges, facilities available on the train, and a mapped out route of the journey.',
    'jp-list-tutorial.select-fare.title': 'Selecting a Ticket',
    'jp-list-tutorial.select-fare.text':
        'You can then select that ticket that you want. You can filter them by 1ST and Standard Class. We always show the cheapest ticket first.',
    'jp-list-tutorial.ticket-details.title': 'Ticket Details',
    'jp-list-tutorial.ticket-details.text':
        'You can click on “Show ticket details" to see more information about your ticket, such as the ticket type, when you can use your ticket, which routes you can use it on and the restrictions of your ticket.',
    'jp-list-tutorial.return-service.title': 'Return Services',
    'jp-list-tutorial.return-service.text':
        'On this screen you can see the return services that your chosen ticket is valid on. Again, you can use the “Show earlier” and “Show later” to see more services. You can also add Plus Bus and choose reservations, if required.',
    'jp-list-tutorial.preferences.title': 'Reservations and PlusBus',
    'jp-list-tutorial.preferences.text':
        'You can choose your reservation preferences here, or buy PlusBus, if they are applicable to your journey.',

    'jp-grid-tutorial.trigger': 'Open Grid view tutorial',
    'jp-grid-tutorial.intro.title': 'jp-list-tutorial.intro.title',
    'jp-grid-tutorial.intro.text': 'jp-list-tutorial.intro.text',
    'jp-grid-tutorial.tabs.title': 'jp-list-tutorial.tabs.title',
    'jp-grid-tutorial.tabs.text': 'jp-list-tutorial.tabs.text',
    'jp-grid-tutorial.grid-view.title': 'The Grid View',
    'jp-grid-tutorial.grid-view.text':
        'This is the Grid View, where you can see outward and return journeys side-by-side.',
    'jp-grid-tutorial.toc-filter.title': 'jp-list-tutorial.toc-filter.title',
    'jp-grid-tutorial.toc-filter.text': 'jp-list-tutorial.toc-filter.text',
    'jp-grid-tutorial.service-carousel.title': 'The Services Carousel',
    'jp-grid-tutorial.service-carousel.text':
        'This is the services carousel, where you can search for earlier or later trains and see the running time, duration and changes for the services shown. You can also click anywhere on the carousel to see more detailed service information.',
    'jp-grid-tutorial.service-info.title': 'jp-list-tutorial.service-info.title',
    'jp-grid-tutorial.service-info.text': 'jp-list-tutorial.service-info.text',
    'jp-grid-tutorial.ticket-categories.title': 'Ticket Categories',
    'jp-grid-tutorial.ticket-categories.text':
        'This is where you choose your tickets, we organise them into the following categories, Singles, Off-Peak and Anytime. We highlight and pre-select the cheapest fares for you. If you can’t see the ticket that you are looking for, press “View All Available Fares” to see a full list of tickets.',
    'jp-grid-tutorial.ticket-details.title': 'jp-list-tutorial.ticket-details.title',
    'jp-grid-tutorial.ticket-details.text': 'jp-list-tutorial.ticket-details.text',
    'jp-grid-tutorial.preferences.title': 'jp-list-tutorial.preferences.title',
    'jp-grid-tutorial.preferences.text': 'jp-list-tutorial.preferences.text',

    'seasons-tutorial.trigger': 'Open Season ticket view tutorial',
    'seasons-tutorial.intro.title': 'jp-list-tutorial.intro.title',
    'seasons-tutorial.intro.text': 'jp-list-tutorial.intro.text',
    'seasons-tutorial.filters.title': 'Filters',
    'seasons-tutorial.filters.text':
        'You can filter the season ticket results by route, duration and class.',
    'seasons-tutorial.comparison.title': 'Comparison Pricing',
    'seasons-tutorial.comparison.text':
        'Where possible we show you the saving against purchasing a season ticket of a shorter duration.',

    'sync-1617-keygo.tutorial.intro.title': 'Sync 1617 title',
    'sync-1617-keygo.tutorial.intro.text':
        'TODO: Display a custom modal message asking the customer to go to Photocards/Railcards if they wish to add their 16-17 Saver railcard to their keyGo account',

    'confirmation-modal.delete-item': 'Please confirm you wish to delete this item!',

    'stationSelector.results.aria': 'List of station results.',

    'station-finder-tutorial.trigger': 'Open Station finder tutorial',
    'station-finder.intro.title': 'Find a station',
    'station-finder.intro.text':
        'Not sure where you are going to? Use the station finder to search for the nearest stations to your destination. If you have location sharing enabled on your device, you can search for the stations that are closest to you!',
    'station-finder.origin-destination.title': 'Adding station to the journey planner',
    'station-finder.origin-destination.text':
        'When you click on a station icon, you can choose to use it as an origin or a destination. You can also see information about the facilities available at that station',

    'stationFinder.expand.buttonLabel': 'Find stations',
    'stationFinder.shrink.buttonLabel': 'Close station finder',
    'stationFinder.search.placeholder': 'Find stations near street/town/postcode',
    'stationFinder.search.buttonText': 'Go',
    'stationFinder.search.useCurrentPosition': 'Use my current location',
    'stationFinder.geolocation.error.permissionDenied': `Please enable location sharing on your device. Alternatively, type in a street/city/postcode and we will show you nearby stations.`,
    'stationFinder.geolocation.error':
        'Unable to determine your location. Please try later, or type a street/town/postcode into the search box',
    'stationFinder.label.error': 'Sorry, we could not find ',
    'stationFinder.noLabel.error': 'Please enter a street/town/postcode into the search box',
    'stationFinder.search.error': 'No stations found. Please try a different location',
    'stationFinder.modal.mainText': 'Would you like to add this station to your journey planner?',
    'stationFinder.modal.origin': 'Make this my<br /><strong>Origin</strong> station',
    'stationFinder.modal.noOrigin': '<strong>Origin</strong> is not available for this search type',
    'stationFinder.modal.destination': 'Make this my<br /><strong>Destination</strong> station',
    'stationFinder.modal.noDestination':
        '<strong>Destination</strong> is not available for this search type',
    'stationFinder.modal.link': 'Find a station',
    'stationFinder.modal.title': 'Search for a station',
    'stationFinder.modal.update': 'station updated to',
    'stationFinder.modal.facilities': 'Facilities',
    'stationFinder.modal.openingHours': 'Ticket office hours',
    'stationFinder.modal.openingHoursChecking': 'Checking for ticket office opening hours...',
    'stationFinder.modal.openingHoursNotAvailable':
        'Sorry, opening hours are not available for this station',
    'stationFinder.nextDay.modal.heading': 'No available journeys for the selected date',
    'stationFinder.nextDay.modal.message': 'There are no outbound journeys available on the day you selected. We have returned the nearest available journey.',
    'stationFinder.modal.openingHours.unknown': 'n/a',
    'stationFinder.station': 'Station',
    'stationFinder.status.open': 'The ticket office at this station is: Open',
    'stationFinder.status.closed': 'The ticket office at this station is: Closed',
    'stationFinder.status.opening-soon': 'The ticket office at this station is: Opening Soon',
    'stationFinder.status.no-office': 'This station does not have a ticket office',
    'stationFinder.status.not-available':
        'Opening status is not available for the ticket office at this station',

    'etickets.results.none-available': 'You have no upcoming trips with eTickets',
    'etickets.results.none-available.filtered': "You don't have any eTickets with this Order ID",
    'etickets.table.start-date': 'Start Date',
    'etickets.table.route': 'Route',
    'eticket.table.help.sr-only': 'What is an eTicket? Click to open modal.',
    'eticket.table.pdf': 'PDF',
    'eticket.table.order': 'Order',
    'eticket.table.download-eticket.sr-only': 'Click to download your eTicket.',
    'eticket.table.download-pdf.sr-only': 'Click to download your PDF ticket.',
    'eticket.table.view-order': 'View order',
    'etickets.filter.placeholder': 'Filter by order ref',
    'etickets.filter.error': 'Order IDs should be numbers',
    'etickets.filter.warning':
        'This is a filtered set of your eTickets. You may have other valid eTickets available for download.',
    'etickets.filter.see-all': 'Click here to see all of your eTickets',
    'etickets.modal.apple.sr-only': 'eTickets are compatible with Apple mobile devices',
    'etickets.modal.android.sr-only': 'eTickets are compatible with Android mobile devices',
    'etickets.modal.content':
        'This is an eTicket which provides you with an alternative way of downloading the eTicket directly to your devices. By simply clicking on the icon, the eTicket will be automatically added to your native iOS or Android wallet.',

    'widgets.journeyPlanner.tabs.search': 'nav.pointTickets',
    'widgets.journeyPlanner.tabs.season': 'nav.seasonTickets',
    'widgets.journeyPlanner.button.search': 'Find trains',
    'widgets.journeyPlanner.button.season': 'Show tickets',
    'widgets.poweredBy':
        'Search results powered by <a href="{href}" target="_blank"><{app.owner.short}></a>',

    'openGolf.header.help': 'About these train tickets',
    'openGolf.header.title': 'Choose your tickets for The Open',
    'openGolf.help': 'TODO openGolf.help',
    'openGolf.search.backToSearch': 'Try a different search',
    'openGolf.search.button': 'Search tickets',
    'openGolf.search.noResults.heading':
        'Sorry, there were no Open tickets found that match your search.',
    'openGolf.search.noResults.text': 'Please try a different date or origin station.',
    'openGolf.selector.subtitle': 'Choose day of validity',
    'openGolf.ticket.add': 'Add to basket',
    'openGolf.ticket.remove': 'Remove',
    'openGolf.tickets.day1': '1 Day ticket',
    'openGolf.tickets.day2': '2 Day ticket',
    'openGolf.tickets.day3': '3 Day ticket',
    'openGolf.tickets.day4': '4 Day ticket',
    'openGolf.tickets.day8': '8 Day ticket',

    'findTicketsSearch.title': 'Buy your tickets to<br/>Sandwich for The Open',
    'findTicketsSearch.subtitle': 'Where will you be travelling from?',
    'findTicketsSearch.submit': 'Find tickets',

    'timetableWidget.checkTimes': 'Check times',
    'timetableWidget.date.label': 'What day are you travelling on?',
    'timetableWidget.results.view.earlier': 'View earlier trains',
    'timetableWidget.results.view.later': 'View later trains',
    'timetableWidget.results.departure': 'Departure',
    'timetableWidget.results.arrival': 'Arrival',
    'timetableWidget.results.duration': 'Duration',
    'timetableWidget.results.destination': 'Destination',
    'timetableWidget.search.errors.default':
        'There was an error while performing a search. Please try again later.',
    'timetableWidget.search.errors.datetimeInPast':
        'A date/time entered is in the past. Please change.',
    'timetableWidget.time.label': 'Leaving at...',

    'timetableWidget.app-links-description': 'To get app notifications you will need to download and be logged into the Southeastern App',

    'timetableWidget.info': 'We will let you know if there is a change to your booked journey because of a timetable change. Timetable changes can be caused by engineering work and major disruption, and could result in your train being cancelled or retimed.\nDisruption alerts are sent up to the day before travel.',

    'preference-centre.title': 'Preference Centre',
    'preference-centre.intro':
        'From time to time we share information about changes to your services, ticket expiries and renewals, and special offers. You can update your subscription preferences by ticking the following options and clicking submit. Please read our {privacyPolicy} for more information.',
    'preference-centre.service-updates': 'Travel and service updates',
    'preference-centre.promotions': 'Days out inspiration and exclusive promotions',
    'preference-centre.products': 'New products and services',
    'preference-centre.all': 'Select all',
    'preference-centre.service-updates.help':
        'Get contacted about service announcements and important travel information, such as planned engineering work reminders, Key smartcard updates and season ticket renewals.',
    'preference-centre.promotions.help':
        'Get inspiring days out ideas and receive the latest fare promotions, competitions and other exclusive deals from us and our partners. We will not share your details with any of our partners.',
    'preference-centre.products.help':
        'Find out about the latest products and services we are developing to make your journeys easier and more comfortable.',
    'preference-centre.edit-notice.short': 'Once registered, you can change your preferences at any point by going into \'My Account\' and updating what you would like to receive.',
    'preference-centre.edit-notice':
        "Once registered, you can change your preferences at any point by going into 'My Account' and updating what you would like to receive. Please read our {privacyPolicy} for more information.",
    'preference-centre.terms-and-conditions.warning':
        'By ticking this box I confirm that I have read and accepted the {termsAndConditions}',
    'preference-centre.privacy-notice':
        '<p >Please note, you are registering with Govia Thameslink Railway through <{app.owner.short}>. <{app.owner.short}> is a trading name of Govia Thameslink Railway Limited which includes the following brands: Gatwick Express, Great Northern, Southern and Thameslink.</p>',
    'preference-centre.privacy-notice.more':
        '<p>We will not pass on your personal information to any organisation outside of Govia Thameslink Railway Limited. Should we no longer operate the <{app.owner.short}> franchise, please be aware that we will pass your details to the Successor franchise or Secretary of State for Transport to allow you to continue using your customer account and receive marketing emails if you have consented to them.</p>',
    'preference-centre.privacy-notice.extra':
        '<p>We will not pass on your personal information to any organisation outside of <{app.owner.short}>. Should we no longer operate the <{app.owner.short}> franchise, please be aware that we will pass your details to the Successor franchise or Secretary of State for Transport to allow you to continue using your customer account and receive marketing emails if you have consented to them.</p>',
    'preference-centre.age-restriction': 'I am 16 or over',
    'preference-centre.age-restriction.warning':
        'If you are under 16 we can only send you Travel updates.',
    'preference-centre.age-restriction.modal-text':
        'To comply with data protection laws and to additionally protect minors we by default choose not to send under 16’s any marketing or promotional material.',
    'preference-centre.age-restriction.update':
        'If you are over 16 please update your date of birth in the form above.',
    'preference-centre.option.sr-only':
        'Please check this box if you wish to receive {option} via {method}',
    'preference-centre.update-success.notification':
        'Your changes have been saved. Please allow up to 14 days for any changes to take effect.',
    'preference-centre.age-verification': 'Age Verification:',
    'preference-centre.select-communication-preferences':
        'Choose what commmunications you would like to receive.',
    'preference-centre.tutorial.title': 'account.nav.preferences',
    'preference-centre.tutorial.text':
        'Welcome to our preferences centre tutorial, it aims to inform you of why you need to update your preferences and how to do so.',
    'preference-centre.form.action': 'Confirm',
    'preference-centre.preference.title': 'One Click Delay Repay preferences:',
    'preference-centre.ddm-preference.title': 'Regular journey alert preferences:',
    'preference-centre.ddm-and-timetable-alerts-preference.title': 'Regular journey alert and timetable change preferences',
    'preference-centre-unsubscribe.button': 'Sign up ',
    'preference-centre.subscribe.preference.text': 'You have chosen to receive {notifyByPreferenceText} notifications',
    'preference-centre.subscribe.preference.to': ' to {userEmail}',
    'preference-centre.subscribe.preference.noPrefSet': 'You do not have any notification preference set.',
    'preference-centre.subscribe.preference.updating': 'Updating notification preferences',
    'preference-centre.subscribe.preference.edit.button': 'Edit One Click Delay Repay preferences',
    'notification-settings.edit.button': 'Edit journey alert and timetable change preferences',
    'notification-settings.title': 'Notification Settings',
    'preference-centre.unsubscribe.preference.subtitle': 'You are not currently signed up to One Click Delay Repay',
    'preference-centre.unsubscribe.preference.text': `If you’re delayed by 15 minutes or more on the Southeastern network, you can claim Delay Repay in just One Click.\nWe will automatically create a Delay Repay claim for you. We can send you the claim by email or through the Southeastern app, or you can log into your account to check. To receive your compensation, all you have to do is confirm the journey details and we’ll take care of the rest.\nOne Click is available to all our Key smartcard customers and anyone who buys an sTicket, eTicket or paper Advance ticket through our website or app.`,
    'preference-centre.notification.title': 'preference-centre.prompt-modal.title',
    'preference-centre.notification.description': 'Please update your preferences',
    'preference-centre.prompt-modal.title': 'Your marketing preferences',
    'preference-centre.prompt-modal.text':
        '<p>Your privacy is important to us. We have updated our preference centre so that you can tell us what emails you would like to receive. Please check and update your preferences.</p>',
    'preference-centre.prompt-modal.continue-text': 'View Preferences',
    'preference-centre.linkName': 'My Preferences',
    'preference-centre.auto-delay-repay.link':
        'You now have the option to subscribe to One Click Delay Repay notifications. Find out more and update your preferences in your {preferenceCentreLink} page.',
    'preference-centre.unsubscribe.title': 'Please choose what you would like to unsubscribe from',
    'preference-centre.unsubscribe.empty.title':
        'You have been unsubscribed from <{app.owner.short}> emails',
    'preference-centre.unsubscribe.empty.disclaimer':
        'If you have JavaScript disabled or are running Ad-Blocking software please allow up to 30 days for this change to take effect.',
    'preference-centre.toggle.info.emailProducts': 'You are currently only opted in to receive new products and services updates.',
    'preference-centre.toggle.info.emailPromotions': 'You are currently only opted in to receive promotions and reward updates.',
    'preference-centre.consents.title': 'At TransPennine Express we want you to have access to special offers, exclusive competitions and the best travel tips. Sound good?',
    'preference-centre.consents.title_2': 'Yes, keep me in the loop by:',
    'preference-centre.consents.phoneNumber': 'Phone Number',
    'preference-centre.consents.phoneNumber.sr': 'Enter your Phone Number to receive SMS',
    'preference-centre.consents.confirmAge': 'Please confirm your age:',
    'preference-centre.consents.privacyPolicy': "Marketing communications will only come from TransPennine Trains Limited trading as TransPennine Express or any successor operator. Don't worry, if you change your mind, you can opt out at any time. To find out more, read our {link}",
    'preference-centre.consents.privacyPolicyLink': 'Privacy Policy',
    'preference-centre.consents.getError': 'There was an issue retrieving your consent, please check your internet and try again',
    'preference-centre.get-consents.error': 'An error occurred whilst attempting to retrieve your consents. Please try again later. If the issue persists, please contact customer services.',
    'preference-centre.update-consents.error': 'An error occurred whilst attempting to update your consents. Please try again later. If the issue persists, please contact customer services.',

    'postcode-lookup.error.invalid':
        'This postcode appears to be invalid. Please check and try again',
    'postcode-lookup.error.not-found':
        "Sorry, we can't find any addresses for this postcode. Please enter manually",
    'postcode-lookup.input.placeholder': 'Enter your postcode',
    'postcode-lookup.select.placeholder': 'Select your address',
    'postcode-lookup.button.label': 'Search address',

    'inApp.updateRequired': 'Update Required',
    'inApp.iOSUpdateMessage':
        'To continue buying online tickets you need to update the app to the latest version.<a class="app-update-cta" href="#">Update now</a>',
    'inApp.AndroidUpdateMessage':
        'To continue buying online tickets you need to update the app to the latest version.<a class="app-update-cta" href="#">Update now</a>',

    'smartSelfRegistration.cta.title':
        'The <{global.key}> <{global.smartcard}> is an alternative to paper tickets that makes it quicker and easier to buy and use tickets.',
    'smartSelfRegistration.warning.tryAnotherEmail': 'Try with another email ',
    'smartSelfRegistration.warning.requestReplacement': 'Request a replacement',
    'smartSelfRegistration.warning.accountFound': 'Account found. Please login.',
    'smartSelfRegistration.warning.noAccount': 'No account found. Please register.',
    'smartSelfRegistration.warning.smartcardFound': 'Existing <{global.smartcard}> found.',
    'smartSelfRegistration.warning.pendingSmartcard':
        'Unfortunately, you are not able to use this service at this time as you have already requested a new <{global.smartcard}> which is on its way to you.',
    'smartSelfRegistration.warning.replacementSmartcard':
        'You already have an Active <{global.smartcard}> on your account. If your <{global.smartcard}> is lost or stolen, please click below to request a replacement.',
    'smartSelfRegistration.warning.linkSmartcard':
        'There is already a <{global.smartcard}> linked to your account, however, it doesn’t currently have any active products on it. If you would like to replace it, please enter your new <{global.smartcard}>’s number below.',
    'smartSelfRegistration.warning.noSmartcard':
        '<{global.smartcard}> not found. Please register your new <{global.smartcard}>.',
    'smartSelfRegistration.warning.childSmartcardReplacement':
        'Unfortunately, child <{global.smartcard}> holders are not eligible to register for an adult <{global.smartcard}>. If your <{global.smartcard}> is lost or stolen, please click above to request a replacement.',
    'smartSelfRegistration.warning.childSmartcardActivation':
        'Unfortunately, child <{global.smartcard}> holders are not eligible to register for an adult <{global.smartcard}>.',

    'smartcardKeyGo.register': 'Get a <{global.key}> <{global.smartcard}> & <{global.keyGo}>',
    'smartcardKeyGo.register-mobile': 'Sign up for <{global.keyGo}>',
    'smartcardKeyGo.register-onlyKeyGo': 'Sign up for <{global.keyGo}>',
    'smartcardKeyGo.title': 'Apply for the <{global.key}> <{global.smartcard}> & <{global.keyGo}>',
    'smartcardKeyGo.title-onlyKeyGo': 'Apply for <{global.keyGo}>',
    'smartcardKeyGo.heading': 'KeyGo Carnet tickets can only be used with a Key Smartcard with keyGo. Please register for keyGo below before purchasing keyGo Carnet ticket.',
    'smartcardKeyGo.errors.smartcard': 'There was a problem applying for a <{global.key}> <{global.smartcard}>. Please try again later',

    'refundWidget.title': 'Refund calculator',
    'refundWidget.field.price': 'Ticket price',
    'refundWidget.field.startDate': 'Ticket start date',
    'refundWidget.field.endDate': 'Ticket end date',
    'refundWidget.field.surrenderDate': 'Date ticket no longer needed from',
    'refundWidget.field.duration': 'Duration',
    'refundWidget.field.day': 'Select a date',
    'refundWidget.field.duration.option.annual': 'Annual',
    'refundWidget.field.duration.option.custom': 'Custom',
    'refundWidget.field.validation.oneMonthAndOneDayBetweenStartSurrender':
        'Unfortunately, we can only calculate refunds if you have used your ticket for more than a month.',
    'refundWidget.field.validation.endDateBeforeSurrender':
        'The "Ticket end date" should be equal/after the "Date of last use".',
    'refundWidget.field.validation.lessThenOneYearBetweenStartSurrender':
        'Unfortunately, we can only calculate refunds for periods shorter then one year.',
    'refundWidget.confirm': 'Calculate refund',
    'refundWidget.totalRefund': 'Estimated refund amount:',
    'refundWidget.adminFee': 'Admin fee:',
    'refundWidget.total': 'Total:',
    'refundWidget.note':
        'The exact amount may vary depending on the final validation of the refund application.',
    'refundWidget.poweredBy-text': 'Powered by OnTrack Retail',
    'refundWidget.poweredBy-alt': 'OnTrack Retail logo',
    'refundWidget.poweredBy-ownersite': 'https://www.ontrackretail.co.uk/',

    'transitioning-child-smartcard.modal.title': 'Your Smartcard is expiring',
    'transitioning-child-smartcard.modal.paragraph.1': 'As your 16th birthday is approaching, please be aware that your child smartcard is due to expire.',
    'transitioning-child-smartcard.modal.paragraph.2': 'While season tickets on your child smartcard remain valid for up to four months after your 16th birthday, you will no longer be able to purchase day tickets with it.',
    'transitioning-child-smartcard.modal.paragraph.3': 'Once you have turned 16 and have no active season ticket, we will upgrade your account and issue you an adult smartcard, which you should receive within seven days.If you have an active child season ticket during this transition and need to purchase single or return tickets, please buy adult eTickets while you await your new adult smartcard.',
    'transitioning-child-smartcard.modal.paragraph.4': 'Your new Smartcard will be sent to the following address:',
    'transitioning-child-smartcard.modal.paragraph.5.1': 'If your address has changed, please go to the ',
    'transitioning-child-smartcard.modal.paragraph.5.address.link': 'Address',
    'transitioning-child-smartcard.modal.paragraph.5.2': ' section of your account, and amend it before you current Smartcard expires.',

    'smartcard.pending.cards.list.summary': '{total, plural, =1{# <{global.key}> <{global.smartcard}> is} other{# <{global.smartcard}>s are}} pending approval',
    'smartcard.associated.cards.list.title': 'These <{global.key}> <{global.smartcard}>s are linked to your account',
    'smartcard.associated.cards.list.description': 'You can buy ticket(s) for the following <{global.key}> <{global.smartcard}>s:',
    'smartcard.associated.cards.list.empty': 'You have no <{global.key}> <{global.smartcard}> accounts linked to your account. After requesting to link an account, you will have to wait until the linking request is approved.',
    'smartcard.associated.cards.list.remove': 'Remove linked <{global.key}> <{global.smartcard}>',
    'smartcard.associated.accounts.list.title': 'The following account(s) can buy tickets for your <{global.key}> <{global.smartcard}>:',
    'smartcard.pending.accounts.list.summary': 'You have been sent {total} \'Linking <{global.key}> <{global.smartcard}> request(s)\'! Please check your email and click approve to allow them to buy tickets for your <{global.key}> <{global.smartcard}>.',
    'smartcard.associated.accounts.list.description': 'Manage who can buy ticket(s) for your <{global.key}> <{global.smartcard}> here.  Once you have approved their request to link your <{global.key}> (sent by email), their name will appear below.',
    'smartcard.associated.accounts.list.empty': 'Manage who can buy tickets for your <{global.key}> <{global.smartcard}> here. Once you have approved their request to link your Key (sent by email), their name will appear below.',
    'smartcard.associated.accounts.list.remove': 'Unlink your <{global.key}> <{global.smartcard}>',
    'smartcard.associated.accounts.list.awaiting': 'This link awaits your confirmation',
    'smartcard.association.details.number': '<{global.key}> <{global.smartcard}> Number:',
    'smartcard.association.details.status': '<{global.key}> <{global.smartcard}> Status:',
    'smartcard.association.details.email': 'Account email address:',
    'smartcard.associate.title': 'Link another <{global.key}> <{global.smartcard}> to your account',
    'smartcard.associate.description': 'You can buy and manage ticket(s) for friends and family, including children’s school travel, to download to their <{global.key}> card. Find out more <a href="https://www.southeasternrailway.co.uk/Tickets/Tickets-explained/the-key-smart-Tickets" target="_blank">here</a>.',
    'smartcard.associate.instruction': 'Type the <{global.key}> <{global.smartcard}> number or the email address belonging to the <{global.key}> card account you wish to link.',
    'smartcard.associate.process-description': 'An email will be sent to the recipient to approve your request.',
    'smartcard.associate.button.label': 'Request to link account',
    'smartcard.associate.field.label': '<{global.smartcard}> to associate',
    'smartcard.associate.confirm.heading': 'Request pending',
    'smartcard.associate.confirm.message': 'An email has been sent to the recipient to approve your request.',
    'smartcard.associate.awaiting.title': 'Awaiting confirmation',
    'smartcard.remove-card-association.modal.title': 'Remove linked <{global.key}> <{global.smartcard}>',
    'smartcard.remove-card-association.modal.description': 'Are you sure you want to unlink {name}’s card? You will no longer be able to buy tickets for them.',
    'smartcard.remove-account-association.modal.title': 'Unlink your <{global.key}> <{global.smartcard}>',
    'smartcard.remove-account-association.modal.description': 'Are you sure you want to unlink your <{global.key}> card from {name}’s account? They will no longer be able to buy smart tickets for you.',
    'smartcard.associate.field.required': 'Valid email or smartcard number required',
    'smartcard.dissociate.error.heading': 'Unlink account error',
    'smartcard.associate.error.heading': 'Link account error',
    'smartcard.associate.error.unknown.title': 'No <{global.smartcard}>',
    'smartcard.associate.error.unknown.message': 'Sorry we couldn’t find a <{global.smartcard}> with that account. Please check your details and try again.',
    'smartcard.associate.error.smartcardNotFound.title': '<{global.smartcard}> not found',
    'smartcard.associate.error.notFound.title': 'Account not found',
    'smartcard.associate.error.notFound.email.message': 'Sorry, we couldn’t find an account with those details. Please try again.',
    'smartcard.associate.error.notFound.isrn.message': 'Sorry, we couldn’t find an account with those details. Please try again.',
    'smartcard.associate.error.invalid.title': 'Invalid <{global.smartcard}>',
    'smartcard.associate.error.own.message': 'You can\'t link your own account. Try with your friends or family’s <{global.key}> <{global.smartcard}>s.',
    'smartcard.associate.error.alreadyRequested.message': '<{global.smartcard}> is already linked to this account or same request has been submitted.',
    'smartcard.associate.error.inactive.message': '<{global.smartcard}> is not active, it can\'t be linked.',
    'smartcard.associate.error.remove-association.title': 'Cannot unlink account',
    'smartcard.associate.confirmation.title': 'Request to link your <{global.key}> <{global.smartcard}>',
    'smartcard.associate.confirmation.approve': 'Your Key Smartcard is now linked to {name}’s account',
    'smartcard.associate.confirmation.reject': 'You have rejected {name}’s request to link your <{global.key}> <{global.smartcard}> to their account',

    'smartCarnet.passes': 'passes',
    'smartCarnet.filters.routes': 'Routes',
    'smartCarnet.filters.anyPermitted': 'Any Permitted',
    'smartCarnet.filters.brandOnly': 'ThamesLink Only',
    'smartCarnet.filters.services': 'Services',
    'smartCarnet.filters.peak': 'Peak',
    'smartCarnet.filters.offPeak': 'Off Peak',
    'smartCarnet.ticketFare.numberOfPasses': 'Number of passes:',
    'smartCarnet.return.warning': 'A carnet ticket only allows for one way travel. Return journey from {price}',
    'smartCarnet.return.noResults': 'Sorry, there were no Smart Carnet tickets found for return journey. Please try a different date or stations.',
    'smartCarnet.fare.passes': '<strong>Passes:</strong> {numOfTickets}',
    'smartCarnet.fare.validFor': '(valid for {months} months)',
    'smartCarnet.fare.validUntil': 'Valid until',
    'smartCarnet.error.maxPassesReached': 'You cannot add this ticket to the basket as it would exceed the <strong>{limit} passes limit</strong> for a journey from <strong>{origin}</strong> to <strong>{destination}</strong> on your account.<br/><br/>Please try lowering the number of passes to purchase.',
    'smartCarnet.refund.numberOfPasses': 'Passes:',
    'smartCarnet.refund.notRefundable': 'not refundable',
    'smartCarnet.account.anyRoute': 'Travel is allowed via any permitted route',
    'smartCarnet.account.filters.active': 'Remaining tickets',
    'smartCarnet.account.filters.expired': 'Expired tickets',
    'smartCarnet.account.filters.all': 'Show all',
    'smartCarnet.account.description': 'If you have multiple <{global.keyGo}> Carnet tickets, passes that expire at an earlier date will be used first.',
    'smartCarnet.account.noResults': 'No <strong>{ticketStatus}</strong> <{global.keyGo}> Carnet tickets found.',
    'smartCarnet.account.orderReference': 'Order reference',
    'smartCarnet.account.remainingPasses': '{passes} passes remaining',
    'smartCarnet.account.ticket.remainingPasses': '<strong>Remaining passes:</strong> {passes}',
    'smartCarnet.account.ticket.usedPasses': '<strong>Used passes:</strong> {passes}',
    'smartCarnet.account.ticket.validity': '<strong>Valid:</strong> {startDate} - {endDate}',
    'smartCarnet.account.title': '<{global.keyGo}> Carnet tickets',
    'smartCarnet.account.refundsLink': 'Click here to manage your tickets including refunds',
    'smartCarnet.ticketStatus.1': 'active',
    'smartCarnet.ticketStatus.2': 'expired',
    'smartCarnet.ticketStatus.3': 'active or expired',
    'smartCarnet.account.journey.offPeak': 'keyGo Carnet Off-Peak (Adult)',
    'smartCarnet.account.journey.peak': 'keyGo Carnet Peak (Adult)',
    'flexi.name': 'Flexi Season',
    'flexi.ticketName': 'Flexi Season ticket',
    'flexi.tickets': 'Passes',
    'flexi.perPass': 'per pass',
    'flexi.passesLower': 'passes',
    'flexi.fare.tickets': '{numOfTickets} passes',
    'flexi.fare.validFor': 'valid for {days} days from start date',
    'flexi.fare.validityInfo': 'Each pass allows you to travel all-day with unlimited travel that day between the two stations you\'ve selected',
    'flexi.fare.validUntil': 'Valid until',
    'flexi.account.remainingTickets': 'Remaining passes',
    'flexi.account.ticketsRemaining': 'passes remaining',
    'flexi.account.usedTickets': 'Used passes',
    'flexi.account.ticketsActivatedOn': 'Passes activated on',
    'flexi.account.validFrom': 'Passes valid from',
    'flexi.account.validTo': 'Passes expire on',
    'flexi.account.empty': 'No results found',
    'flexi.account.refundLink': 'Refund {origin}, to {destination}. {route} Flexi Season ticket',
    'flexi.account.addToFavourites': 'Add {origin}, to {destination}. {route} Flexi Season ticket to your favourite tickets',
    'flexi.account.ticketSummary': 'From {origin} to {destination}. {route}. {remainingTickets} passes remaining. Passes activated on {activationDates}',
    'flexi.account.validityInfo': 'Passes must all be used before the expiry date. Any unused passes that are remaining beyond this validity date will not be able to be refunded',
    'flexi.londonFaresWarning': 'Flexi Season tickets are not available for journeys between two stations within the London Travelcard Zones 1-9',
    'flexi.londonFaresWarningLinkText': 'find out more',
    'flexi.londonFaresWarningLinkText.aria': 'Find out more with flexi Season FAQs',
    'fareHandOff.title': 'Add fare',
    'fareHandOff.title.ERROR': 'Error',
    'fareHandOff.state.INITIAL': 'Initialising',
    'fareHandOff.state.ERROR': 'There is a problem',
    'fareHandOff.state.CHECKING_RAILCARD': 'Checking 16/17 Saver',
    'fareHandOff.state.RAILCARD_ENTRY': 'Checking 16/17 Saver',
    'fareHandOff.state.RAILCARD_INCOMPLETE': 'Checking 16/17 Saver',
    'fareHandOff.state.SEARCHING': 'Searching for fares',
    'fareHandOff.state.SEARCH_COMPLETE': 'Searching for fares',
    'fareHandOff.state.FARE_NOT_FOUND': 'Fare not found',
    'fareHandOff.state.FARE_SELECTED': 'Adding fare to basket',
    'fareHandOff.state.FARE_ADDED': 'Adding fare to basket',
    'fareHandOff.state.ADDING_PLUSBUS': 'Adding PlusBus',
    'fareHandOff.state.CHECKING_DELIVERY': 'Checking delivery',
    'fareHandOff.state.REDIRECT_DELIVERY': ' ',
    'fareHandOff.state.REDIRECT_PAYMENT': ' ',
    'fareHandOff.state.REDIRECT_SEARCH_RESULTS': ' ',
    'fareHandOff.message.FARE_NOT_FOUND': 'We could not find the fare you are looking for. Would you like to see a list of available fares?',
    'fareHandOff.message.RAILCARD_INCOMPLETE': 'The fare that you chose includes a 16/17 saver discount, but we could not validate your railcard for the period of your ticket. Would you like to try again, or view prices without the 16/17 railcard?',
    'fareHandOff.button.viewFares': 'View available fares',
    'fareHandOff.button.retryRailcard': 'Retry 16/17 Railcard',
    'charity.name.railway-children': 'Railway Children',
    'charity.description.railway-children': '<p>Every year in the UK, almost 10,000 children are found in danger on the rail network. They might be running away from crisis in the home, unknowingly running towards danger at the hands of criminals, or in the worst cases, trying to end their lives.</p><p>The charity Railway Children is working in partnership with British Transport Police to create a safety net to provide immediate protection, followed by individualised support plans, for these children to help change the direction of their lives, setting them onto the path to a brighter, happier future.</p><p style="margin-bottom: 0;">You can learn more about Railway Children’s work in the UK, as well as across India and Tanzania by visiting <a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.name.dame-kelly': 'Dame Kelly Holmes Trust',
    'charity.description.dame-kelly': '<p>Dame Kelly Holmes Trust puts world class athletes shoulder to shoulder with young people. Equipping them with a winning mindset and shaping their futures - it’s a teammate like no other. Whether that’s building relationship skills, improving self-esteem or learning to stay focused, our sporting champions help the next generation move forward with confidence. Our goal is to improve the health and wellbeing of 10,000 young people across the UK facing challenges in their everyday lives.</p><p style="margin-bottom: 0;"><a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.name.samaritans': 'Samaritans',
    'charity.description.samaritans': '<p>Every 10 seconds, Samaritans responds to a call for help.</p><p>We’re here, day or night, for anyone who’s struggling to cope, who needs someone to listen without judgement or pressure. Samaritans is not only for the moment of crisis, we’re taking action to prevent the crisis.</p><p>We give people ways to cope and the skills to be there for others. And we encourage, promote and celebrate those moments of connection between people that can save lives.</p><p style="margin-bottom: 0;"><a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.name.cruk': 'Cancer Research UK',
    'charity.description.cruk': '<p>Thanks to supporters like you, our pioneering work into how to prevent, diagnose and treat cancer has benefitted millions of lives. Giving as little as £2 a month could fund something as big as a new research centre or as small as glass slides to analyse cancer cells.</p><p style="margin-bottom: 0;"><a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.name.great-ormond-street': 'Great Ormond Street Hospital Children\'s Charity',
    'charity.description.great-ormond-street': '<p>Every day brings new challenges at Great Ormond Street Hospital (GOSH). Every day, around 600 children and young people from across the UK arrive. Every day, doctors and nurses battle the most complex illnesses, and the brightest minds come together to achieve pioneering medical breakthroughs. This extraordinary hospital has always depended on charitable support to give seriously ill children the best chance to fulfil their potential, and every day is a chance for you to make a difference. By donating today, you will positively impact so many seriously ill children treated at GOSH. 100% of your donation is given by South Eastern Railway to Great Ormond Street Hospital Children’s Charity (Registered charity no. 1160024).</p><p style="margin-bottom: 0;"><a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.name.nhs': 'NHS',
    'charity.description.nhs': '<p style="margin-bottom: 0;"><a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.name.dec': 'Disasters Emergency Committee',
    'charity.description.dec': '<p style="margin-bottom: 0;"><a href="{charityUrl}" target="_blank">{urlText}</a></p>',
    'charity.loading.error': 'There are no charities available at this moment, please try again later.',
    'seatfinder.title': 'Click here to check how busy your train may be with <u>SeatFinder</u>',
    'timetableAlertsWidget.notificationPreference': 'Notification preference:',
    'rewardPoints.information.pointsAmount': 'Congratulations you have earned {totalRewardPoints} reward points.',
    'rewardPoints.information.acknowledgment': 'Thank you for booking directly with us.',
    'rewardPoints.instruction.list.heading': 'Follow these steps to exchange your points for rewards:',
    'rewardPoints.instruction.list.firstStep': 'Go to the{rewardPointsUrl}',
    'rewardPoints.instruction.list.firstStep.url': 'Next Level Rewards website',
    'rewardPoints.instruction.list.secondStep': 'Create an account',
    'rewardPoints.instruction.list.thirdStep': 'Enter your reward ID, {rewardPointUid} (Please note reward points will be available to redeem from the day after purchase)',
    'rewardPoints.instruction.list.fourthStep': 'Choose your rewards',
    'rewardPoints.instruction.reminder': 'Remember, the more points you have the greater the reward you can claim!',
    'rewardPoints.instruction.tsAndCs': 'Subject to Terms and Conditions. Please visit{rewardPointsTermsUrl}for more details.',
    'rewardPoints.instruction.tsAndCs.url': 'Next Level Rewards',
    'rewardPoints.earned.pointsAmount': 'You’ve earned {rewardPoints} rewards points',
    'rewardPoints.earned.findOutMore': 'Find out more about our Reward program',
    'rewardPoints.toGet.pointsAmount': 'You’ll earn {rewardPoints} rewards points',
    'rewardPoints.toGet.findOutMore': 'Find out more about our Reward program',
    'rewardPoints.modal.title': 'Southern Rail Reward Program',
    'rewardPoints.modal.heading': 'Commute more, collect more, with Southern Rewards',
    'rewardPoints.modal.howToGet': 'You can earn 3 points for every £1 you spend on Anytime, Flexi Season and Season tickets bought directly from our website and fulfilled using an eTicket or Key Smartcard, until 31st March 2023 (T’s and C’s apply).',
    'rewardPoints.modal.usage': 'Use your points to claim amazing rewards, such as days out, meals out, free cinema tickets and coffees, or even online training courses. Upon signing up to the rewards programme you will also be automatically entered into a prize draw where you can win even more!',
    'rewardPoints.modal.findOutMore': 'Find out more',
    'rewardPoints.modal.schemeEnding': 'As of 1st April, you will no longer receive reward points for your Anytime, Flexi Season and Season tickets bought directly from the Southern Railway website and fulfilled using an eTicket or Key Smartcard.',
    'rewardPoints.modal.existingRewardCodes': 'Existing reward codes can still be logged via the Southern Next Level Rewards website',
    'rewardPoints.modal.existingRewardCodesUntil': 'until 31st May.',

    'carbonFootprint.button.info': 'How we work out your carbon footprint',
    'carbonFootprint.modal.title': 'Carbon Footprint',
    'carbonFootprint.modal.paragraph1': "Travelling by train is one of the most environmentally friendly ways to travel, with lower carbon emissions than most cars.",
    'carbonFootprint.modal.paragraph2': 'The environmental impact of your journey for both train and car travel is shown below.',
    'carbonFootprint.modal.paragraph3': 'To help you draw comparisons from the CO2e savings we have provided CO2e equivalents. Data from these is provided by the ',
    'carbonFootprint.modal.paragraph3.link': 'EPA Carbon Calculator',
    'carbonFootprint.modal.paragraph4': ' and ',
    'carbonFootprint.modal.paragraph4.link': 'Carbon Footprint',
    'carbonFootprint.modal.paragraph5': '.',
    'carbonFootprint.saved': "Good news! You will save {savedCO2e}kg CO2e because you're taking the train instead of the car.",
    'carbonFootprint.modal.info.title': 'CO2e emissions for this {distance} mile journey between {origin} and {destination}:',
    'carbonFootprint.modal.info.card-train.title': 'By train',
    'carbonFootprint.modal.info.card-car.title': 'By car',
    'carbonFootprint.modal.info.card.subtitle': 'Emissions for this journey:',
    'carbonFootprint.modal.info.card.emission': '{emission} kg CO2e',
    'carbonFootprint.modal.info.paragraph1': 'Distance is calculated as the direct distance between the origin and destination stations.',
    'carbonFootprint.modal.info.paragraph2': "These calculations are based on:<br><br>Car = Average petrol car 0.22469 kg CO2e/passenger mile<br>Train = National Rail 0.07150 kg CO2e/passenger mile",
    'carbonFootprint.modal.info.paragraph3': 'Conversion factors are taken from ',
    'carbonFootprint.modal.alternative.info.title': 'The {savedCO2e}kg CO2e that you will save by taking the train instead of the car is similar to:',
    'carbonFootprint.modal.alternative.card.battery.title': 'Fully Charging',
    'carbonFootprint.modal.alternative.card.battery.info': '{value} smartphones',
    'carbonFootprint.modal.alternative.card.washingMachine.title': 'Using a washing machine',
    'carbonFootprint.modal.alternative.card.washingMachine.info': '{value} times',
    'carbonFootprint.modal.alternative.card.tv.title': 'Watching',
    'carbonFootprint.modal.alternative.card.tv.info': '{value} hours of TV',
    'carbonFootprint.modal.alternative.card.kettle.title': 'Boiling',
    'carbonFootprint.modal.alternative.card.kettle.info': '{value} kettles',
    'carbonFootprint.modal.alternative.card.bulb.title': 'Leaving a light on for',
    'carbonFootprint.modal.alternative.card.bulb.info': '{value} hours',
    'carbonFootprint.modal.alternative.card.microwave.title': 'Using a microwave',
    'carbonFootprint.modal.alternative.card.microwave.info': '{value} times',

    'confirmation.sTicket.app.description': 'Go to the ‘My Tickets’ section of the app to download and view your sTicket, you will need to be logged in.<br/>If you don’t have the { brand } app, you can download it on the App Store or Google Play.',
    'booking.delivery.sticket.modal.body': 'Go to the ‘My Tickets’ section of the app to download and view your sTicket, you will need to be logged in.',

    'ontrackapp.widget.brandAltText': '{brand} logo',

    'modals.assistance-helplines.title': 'Assistance helplines',
    'modals.assistance-helpline.preamble': 'To make a passenger assist request by phone or for any travel queries please call:',
    'modals.assistance-helplines.heading-1': 'Passenger Assist:',
    'modals.assistance-helplines.item-1': '0800 783 4524',
    'modals.assistance-helplines.heading-2': 'Text Relay number:',
    'modals.assistance-helplines.item-2': '18001 0800 783 4524',
    'modals.assistance-helplines.footer': 'These numbers are available 24 hours a day, 7 days a week.',

    'delivery.e-ticket.bikeReservation': 'eTicket is not available for orders with bike reservations.',
});

export default {
    gatwick: defaultMessages.merge(gatwick),
    'gatwick-android': defaultMessages.merge(gatwick),
    'gatwick-ios': defaultMessages.merge(gatwick),
    'gatwick-ba': defaultMessages.merge(gatwickBa),
    'gatwick-ryanair': defaultMessages.merge(gatwickRyanair),
    'gatwick-easyjet': defaultMessages.merge(gatwick),
    southeastern: defaultMessages.merge(southeastern),
    southern: defaultMessages.merge(southern),
    thameslink: defaultMessages.merge(thameslink),
    greatnorthern: defaultMessages.merge(greatnorthern),
    ontrackretail: defaultMessages.merge(onTrackRetail),
    ticketyboo: defaultMessages.merge(ticketyboo),
    transpennine: defaultMessages.merge(transpennine),
};



// WEBPACK FOOTER //
// ./src/messages/dictionary.js